// Why use a single, global Action Types file?
//
// - Prevent action name collisions
// - Have a convenient "birds-eye" view of all possible actions in our app
// - A single place to, if necessary, document the meaning of specific action types

// Why use constants?
//
// - Reduce risk of simple typo errors (ESLint or even reference errors are quite telling)
// - Empower developers using IDE-like features (Nuclide, VSCode, Flow, etc)

// Action naming suggestions:
//
// - Use declarative action names, (e.g. ITEM_ADDED)
// - Don't use imperative action names, (e.g. ADD_ITEM)
// - Use the same `const` name as its corresponding string value (e.g. `const FOO = 'FOO'`)
// - When possible, keep your actions "namespaced" based on feature, i.e. `SELECTOR_WIDGET_*`
// - Keep exports sorted alphabetically (namespace prefixing should automatically group things by feature)
//   - Meta TODO: Create eslint plugin: /* eslint wf/sort-exports: "error" */

import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';

// TODO: We should create a general type for this purpose:
export type __DEPRECATED__BaseAction<T, P = any> = {
  state: Record<any, any>;
  type: T;
} & P;

export type SymbolOpenedSource =
  | 'symbols panel'
  | 'props panel'
  | 'canvas'
  | 'navigator'
  | 'banner'
  | 'finder'
  | 'show instances';

export type SymbolOpenedTrigger =
  | 'click'
  | 'right-click'
  | 'double-click'
  | 'shortcut';

export type SymbolOpenedAnalytics = {
  source: SymbolOpenedSource;
  trigger: SymbolOpenedTrigger;
  symbolId: string | undefined;
};

export type ParentSymbolOfNodeInInstanceFocusedPayload = {
  nativeIdPath: NativeId[];
  keepCurrentSelection?: boolean;
  ix3InteractionId?: string;
  ix3InteractionSource?: 'left panel' | 'right panel';
  clearIx3Selection?: boolean;
  analytics?: SymbolOpenedAnalytics;
};

export type LinkSettingsOverlayTargetCreateRequestedPayload = {
  kind: 'popover' | 'modal';
  triggerId: string;
  anchorNativeIdPath: NativeId[];
  componentKey: string;
};

export const ELEMENT_PRESET_ASSET_IMPORT_SUCCEEDED =
  'ELEMENT_PRESET_ASSET_IMPORT_SUCCEEDED' as const;
export const ELEMENT_ADDED = 'ELEMENT_ADDED' as const;
export const IMAGE_PREVIEW_UPDATED = 'IMAGE_PREVIEW_UPDATED' as const;
export const ELEMENT_MOVED = 'ELEMENT_MOVED' as const;
export const AISB_GENERATION_COMPLETED_REMOTELY =
  'AISB_GENERATION_COMPLETED_REMOTELY' as const;
export const AISB_GENERATION_ERROR_ENCOUNTERED_REMOTELY =
  'AISB_GENERATION_ERROR_ENCOUNTERED_REMOTELY' as const;
export const AISB_GENERATION_COMPLETED = 'AISB_GENERATION_COMPLETED' as const;
export const AISB_GENERATION_STEP_UPDATED_REMOTELY =
  'AISB_GENERATION_STEP_UPDATED_REMOTELY' as const;
export const AISG_ASSETS_ADDED_REMOTELY = 'AISG_ASSETS_ADDED_REMOTELY' as const;
export const AISG_HOVERED_SECTION_CONTROLS_CLICKED =
  'AISG_HOVERED_SECTION_CONTROLS_CLICKED' as const;
export const AIDA_ELEMENT_ADDED_TO_CANVAS =
  'AIDA_ELEMENT_ADDED_TO_CANVAS' as const;
export const AISG_FTU_PREVIEW_START_BUILDING_CLICKED =
  'AISG_FTU_PREVIEW_START_BUILDING_CLICKED' as const;
export const __HACK__CLEARFIX_STATE_CHANGED =
  '__HACK__CLEARFIX_STATE_CHANGED' as const;
export const ACCOUNT_FORM_FIELD_INVALID = 'ACCOUNT_FORM_FIELD_INVALID' as const;
export const ACCOUNT_IMAGE_DELETED = 'ACCOUNT_IMAGE_DELETED' as const;
export const ACCOUNT_IMAGE_PREVIEWED = 'ACCOUNT_IMAGE_PREVIEWED' as const;
export const ACCOUNT_IMAGE_UPLOADED = 'ACCOUNT_IMAGE_UPLOADED' as const;
export const ACCOUNT_SETTINGS_SAVED = 'ACCOUNT_SETTINGS_SAVED' as const;
export const ACCOUNT_SETTINGS_SAVED_BEGIN =
  'ACCOUNT_SETTINGS_SAVED_BEGIN' as const;
export const ACCOUNT_SETTINGS_SAVED_ERROR =
  'ACCOUNT_SETTINGS_SAVED_ERROR' as const;
export const AGENTS_FETCH_REQUESTED = 'AGENTS_FETCH_REQUESTED' as const;
export const AGENTS_FETCH_SUCCEEDED = 'AGENTS_FETCH_SUCCEEDED' as const;
export const AGENTS_FETCH_FAILED = 'AGENTS_FETCH_FAILED' as const;
export const AGENT_CREATE_REQUESTED = 'AGENT_CREATE_REQUESTED' as const;
export const AGENT_CREATE_SUCCEEDED = 'AGENT_CREATE_SUCCEEDED' as const;
export const AGENT_CREATE_FAILED = 'AGENT_CREATE_FAILED' as const;
export const AGENT_COMPOSER_CREATE_REQUESTED =
  'AGENT_COMPOSER_CREATE_REQUESTED' as const;
export const AGENT_COMPOSER_SEED_CONSUMED =
  'AGENT_COMPOSER_SEED_CONSUMED' as const;
export const AGENT_DELETE_REQUESTED = 'AGENT_DELETE_REQUESTED' as const;
export const AGENT_DELETE_SUCCEEDED = 'AGENT_DELETE_SUCCEEDED' as const;
export const AGENT_DELETE_FAILED = 'AGENT_DELETE_FAILED' as const;
export const AGENT_EXECUTE_REQUESTED = 'AGENT_EXECUTE_REQUESTED' as const;
export const AGENT_EXECUTE_SUCCEEDED = 'AGENT_EXECUTE_SUCCEEDED' as const;
export const AGENT_EXECUTE_FAILED = 'AGENT_EXECUTE_FAILED' as const;
export const AGENT_DETAILS_REQUESTED = 'AGENT_DETAILS_REQUESTED' as const;
export const AGENT_DETAILS_SUCCEEDED = 'AGENT_DETAILS_SUCCEEDED' as const;
export const AGENT_DETAILS_FAILED = 'AGENT_DETAILS_FAILED' as const;
export const WORKFLOW_DRAFT_FETCH_REQUESTED =
  'WORKFLOW_DRAFT_FETCH_REQUESTED' as const;
export const WORKFLOW_DRAFT_FETCH_SUCCEEDED =
  'WORKFLOW_DRAFT_FETCH_SUCCEEDED' as const;
export const WORKFLOW_DRAFT_FETCH_FAILED =
  'WORKFLOW_DRAFT_FETCH_FAILED' as const;
export const WORKFLOW_DRAFT_DISCARD_REQUESTED =
  'WORKFLOW_DRAFT_DISCARD_REQUESTED' as const;
export const WORKFLOW_DRAFT_DISCARD_SUCCEEDED =
  'WORKFLOW_DRAFT_DISCARD_SUCCEEDED' as const;
export const WORKFLOW_DRAFT_DISCARD_FAILED =
  'WORKFLOW_DRAFT_DISCARD_FAILED' as const;
export const WORKFLOW_DRAFT_SAVE_REQUESTED =
  'WORKFLOW_DRAFT_SAVE_REQUESTED' as const;
export const WORKFLOW_DRAFT_SAVE_SUCCEEDED =
  'WORKFLOW_DRAFT_SAVE_SUCCEEDED' as const;
export const WORKFLOW_DRAFT_SAVE_FAILED = 'WORKFLOW_DRAFT_SAVE_FAILED' as const;
export const WORKFLOW_DRAFT_RECEIVED = 'WORKFLOW_DRAFT_RECEIVED' as const;
export const WORKFLOW_DRAFT_SELECTION_CLEARED =
  'WORKFLOW_DRAFT_SELECTION_CLEARED' as const;
export const WORKFLOW_DRAFT_UNAVAILABLE_ACKNOWLEDGED =
  'WORKFLOW_DRAFT_UNAVAILABLE_ACKNOWLEDGED' as const;
export const WORKFLOW_VERSIONS_FETCH_REQUESTED =
  'WORKFLOW_VERSIONS_FETCH_REQUESTED' as const;
export const WORKFLOW_VERSIONS_FETCH_SUCCEEDED =
  'WORKFLOW_VERSIONS_FETCH_SUCCEEDED' as const;
export const WORKFLOW_VERSIONS_FETCH_FAILED =
  'WORKFLOW_VERSIONS_FETCH_FAILED' as const;
export const WORKFLOW_VERSION_FETCH_REQUESTED =
  'WORKFLOW_VERSION_FETCH_REQUESTED' as const;
export const WORKFLOW_VERSION_FETCH_SUCCEEDED =
  'WORKFLOW_VERSION_FETCH_SUCCEEDED' as const;
export const WORKFLOW_VERSION_FETCH_FAILED =
  'WORKFLOW_VERSION_FETCH_FAILED' as const;
export const WORKFLOW_VERSION_SWITCH_REQUESTED =
  'WORKFLOW_VERSION_SWITCH_REQUESTED' as const;
export const WORKFLOW_VERSION_SWITCH_SUCCEEDED =
  'WORKFLOW_VERSION_SWITCH_SUCCEEDED' as const;
export const WORKFLOW_VERSION_SWITCH_FAILED =
  'WORKFLOW_VERSION_SWITCH_FAILED' as const;
export const AGENT_SELECTED = 'AGENT_SELECTED' as const;
export const AGENT_TOGGLE_ACTIVE_REQUESTED =
  'AGENT_TOGGLE_ACTIVE_REQUESTED' as const;
export const AGENT_TOGGLE_ACTIVE_SUCCEEDED =
  'AGENT_TOGGLE_ACTIVE_SUCCEEDED' as const;
export const AGENT_TOGGLE_ACTIVE_FAILED = 'AGENT_TOGGLE_ACTIVE_FAILED' as const;
export const WORKFLOW_EXECUTION_POLL_REQUESTED =
  'WORKFLOW_EXECUTION_POLL_REQUESTED' as const;
export const WORKFLOW_EXECUTION_POLL_SUCCEEDED =
  'WORKFLOW_EXECUTION_POLL_SUCCEEDED' as const;
export const WORKFLOW_EXECUTION_POLL_FAILED =
  'WORKFLOW_EXECUTION_POLL_FAILED' as const;
export const AGENTS_SET_VIEW_MODE = 'AGENTS_SET_VIEW_MODE' as const;
export const AGENTS_SET_ENABLED = 'AGENTS_SET_ENABLED' as const;
export const AGENTS_SET_SELECTED_NODE = 'AGENTS_SET_SELECTED_NODE' as const;
export const AGENTS_TOGGLE_OVERVIEW = 'AGENTS_TOGGLE_OVERVIEW' as const;
export const AGENT_MANUAL_RUN_QUEUED = 'AGENT_MANUAL_RUN_QUEUED' as const;
export const AGENTS_CLOSE_SET_INPUTS = 'AGENTS_CLOSE_SET_INPUTS' as const;
export const AGENTS_CLOSE_CHAT_DOCK = 'AGENTS_CLOSE_CHAT_DOCK' as const;
export const AGENTS_OPEN_CHAT_DOCK = 'AGENTS_OPEN_CHAT_DOCK' as const;
export const WORKFLOW_RUNS_FETCH_REQUESTED =
  'WORKFLOW_RUNS_FETCH_REQUESTED' as const;
export const WORKFLOW_RUNS_FETCH_SUCCEEDED =
  'WORKFLOW_RUNS_FETCH_SUCCEEDED' as const;
export const WORKFLOW_RUNS_FETCH_FAILED = 'WORKFLOW_RUNS_FETCH_FAILED' as const;
export const WORKFLOW_RUNS_LIST_UPDATE_RECEIVED =
  'WORKFLOW_RUNS_LIST_UPDATE_RECEIVED' as const;
export const WORKFLOW_RUN_DETAIL_FETCH_REQUESTED =
  'WORKFLOW_RUN_DETAIL_FETCH_REQUESTED' as const;
export const WORKFLOW_RUN_DETAIL_FETCH_SUCCEEDED =
  'WORKFLOW_RUN_DETAIL_FETCH_SUCCEEDED' as const;
export const WORKFLOW_RUN_DETAIL_FETCH_FAILED =
  'WORKFLOW_RUN_DETAIL_FETCH_FAILED' as const;
export const WORKFLOW_RUN_SELECTED = 'WORKFLOW_RUN_SELECTED' as const;
export const ACTIVITY_LOG_OPENED = 'ACTIVITY_LOG_OPENED' as const;
export const ACTIVITY_LOG_FETCH_FAILED = 'ACTIVITY_LOG_FETCH_FAILED' as const;
export const ACTIVITY_LOG_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_LATEST_EVENTS_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_LATEST_EVENTS_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_NUMBER_OF_PUBLISHES_FETCH_FAILED =
  'ACTIVITY_LOG_NUMBER_OF_PUBLISHES_FETCH_FAILED' as const;
export const ACTIVITY_LOG_NUMBER_OF_PUBLISHES_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_NUMBER_OF_PUBLISHES_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_NUMBER_OF_PUBLISHES_REQUESTED =
  'ACTIVITY_LOG_NUMBER_OF_PUBLISHES_REQUESTED' as const;
export const MULTIPLAYER_CONNECTION_STATUS_VISIBILITY_TOGGLED =
  'MULTIPLAYER_CONNECTION_STATUS_VISIBILITY_TOGGLED' as const;
export const SHOW_ORPHANED_LIBRARY_COMPONENTS =
  'SHOW_ORPHANED_LIBRARY_COMPONENTS' as const;
export const ADD_TAB_SECTION_HEADER_CLICKED =
  'ADD_TAB_SECTION_HEADER_CLICKED' as const;
export const ADD_NEW_PAGE_MENU_CLICKED = 'ADD_NEW_PAGE_MENU_CLICKED' as const;
export const ADD_NEW_PAGE_MENU_OPTION_CLICKED =
  'ADD_NEW_PAGE_MENU_OPTION_CLICKED' as const;
export const ADD_TAB_VISIBILITY_MERGED = 'ADD_TAB_VISIBILITY_MERGED' as const;
export const ADD_TAB_LIBRARY_CARD_CLICKED =
  'ADD_TAB_LIBRARY_CARD_CLICKED' as const;
export const AJAX_FAILED = 'AJAX_FAILED' as const;
export const ANALYTICS_EVENTS_TRACKED = 'ANALYTICS_EVENTS_TRACKED' as const;
export const ANIMATION_PATH_CHANGED = 'ANIMATION_PATH_CHANGED' as const;
export const RIVE_PATH_CHANGED = 'RIVE_PATH_CHANGED' as const;
export const APPLICATION_LOADED = 'APPLICATION_LOADED' as const;
export const ASSET_DROP_ZONE_TOGGLED = 'ASSET_DROP_ZONE_TOGGLED' as const;
export const ASSET_MANAGER_FOLDER_SHOW_CHILDREN_CLOSED =
  'ASSET_MANAGER_FOLDER_SHOW_CHILDREN_CLOSED' as const;
export const ASSET_MANAGER_FOLDER_SHOW_CHILDREN_TOGGLED =
  'ASSET_MANAGER_FOLDER_SHOW_CHILDREN_TOGGLED' as const;
export const ASSET_MANAGER_FOLDER_EXPAND_ALL_FOLDERS =
  'ASSET_MANAGER_FOLDER_EXPAND_ALL_FOLDERS' as const;
export const ASSET_MANAGER_FOLDER_COLLAPSE_ALL_FOLDERS =
  'ASSET_MANAGER_FOLDER_COLLAPSE_ALL_FOLDERS' as const;
export const ASSET_MANAGER_ADD_FOLDER_WORKFLOW_INITIATED =
  'ASSET_MANAGER_ADD_FOLDER_WORKFLOW_INITIATED' as const;
export const ASSET_MANAGER_ADD_FOLDER_REQUESTED =
  'ASSET_MANAGER_ADD_FOLDER_REQUESTED' as const;
export const ASSET_MANAGER_EDIT_SETTINGS =
  'ASSET_MANAGER_EDIT_SETTINGS' as const;
export const ASSET_MANAGER_VIEW_SETTINGS =
  'ASSET_MANAGER_VIEW_SETTINGS' as const;
export const ASSET_MANAGER_OPEN_SOURCE_SITE =
  'ASSET_MANAGER_OPEN_SOURCE_SITE' as const;
export const ASSET_MANAGER_COPY_CDN_LINK =
  'ASSET_MANAGER_COPY_CDN_LINK' as const;
export const ASSET_MANAGER_OPEN_LINK = 'ASSET_MANAGER_OPEN_LINK' as const;
export const ASSET_MANAGER_SAVE_ASSET_SETTINGS =
  'ASSET_MANAGER_SAVE_ASSET_SETTINGS' as const;
export const ASSET_MANAGER_ADD_FOLDER_SUCCEEDED =
  'ASSET_MANAGER_ADD_FOLDER_SUCCEEDED' as const;
export const ASSET_MANAGER_ADD_FOLDER_FAILED =
  'ASSET_MANAGER_ADD_FOLDER_FAILED' as const;
export const ASSET_MANAGER_BULK_DELETE_FOLDERS_REQUESTED =
  'ASSET_MANAGER_BULK_DELETE_FOLDERS_REQUESTED' as const;
export const ASSET_MANAGER_BULK_DELETE_FOLDERS_SUCCEEDED =
  'ASSET_MANAGER_BULK_DELETE_FOLDERS_SUCCEEDED' as const;
export const ASSET_MANAGER_BULK_DELETE_FOLDERS_FAILED =
  'ASSET_MANAGER_BULK_DELETE_FOLDERS_FAILED' as const;
export const ASSET_MANAGER_DELETE_FOLDER_SUCCEEDED =
  'ASSET_MANAGER_DELETE_FOLDER_SUCCEEDED' as const;
export const ASSET_MANAGER_DELETE_FOLDER_FAILED =
  'ASSET_MANAGER_DELETE_FOLDER_FAILED' as const;
export const ASSET_MANAGER_UPDATE_FOLDER_REQUESTED =
  'ASSET_MANAGER_UPDATE_FOLDER_REQUESTED' as const;
export const ASSET_MANAGER_UPDATE_FOLDER_FAILED =
  'ASSET_MANAGER_UPDATE_FOLDER_FAILED' as const;
export const ASSET_MANAGER_UPDATE_FOLDER_SUCCEEDED =
  'ASSET_MANAGER_UPDATE_FOLDER_SUCCEEDED' as const;
export const ASSET_MANAGER_SEARCH_INPUT_QUERY_CHANGED =
  'ASSET_MANAGER_SEARCH_INPUT_QUERY_CHANGED' as const;
export const ASSET_MANAGER_DISPLAY_MODE_SELECTED =
  'ASSET_MANAGER_DISPLAY_MODE_SELECTED' as const;
export const ASSET_MANAGER_HOT_SELECT_CANCELED =
  'ASSET_MANAGER_HOT_SELECT_CANCELED' as const;
export const ASSET_SELECTION_STARTED = 'ASSET_SELECTION_STARTED' as const;
export const ASSET_MANAGER_ASSET_HOVERED =
  'ASSET_MANAGER_ASSET_HOVERED' as const;
export const ASSET_MANAGER_ASSET_UNHOVERED =
  'ASSET_MANAGER_ASSET_UNHOVERED' as const;
export const ASSET_MANAGER_HOT_SELECT_STARTED =
  'ASSET_MANAGER_HOT_SELECT_STARTED' as const;
export const ASSET_MANAGER_FILTER_SELECTED =
  'ASSET_MANAGER_FILTER_SELECTED' as const;
export const ASSET_MANAGER_SORT_SELECTED =
  'ASSET_MANAGER_SORT_SELECTED' as const;
export const ASSET_METADATA_UPDATED = 'ASSET_METADATA_UPDATED' as const;
export const ASSET_PROCESSING_TOGGLED = 'ASSET_PROCESSING_TOGGLED' as const;
export const ASSET_TRANSCODE_PROGRESSED = 'ASSET_TRANSCODE_PROGRESSED' as const;
export const ASSET_UNREGISTERED = 'ASSET_UNREGISTERED' as const;
export const ASSET_S3_PINGED = 'ASSET_S3_PINGED' as const;
export const ASSET_S3_UPLOAD_NOT_NEEDED = 'ASSET_S3_UPLOAD_NOT_NEEDED' as const;
export const ASSET_S3_UPLOAD_NEEDED = 'ASSET_S3_UPLOAD_NEEDED' as const;
export const ASSET_SIGNING_FINISHED = 'ASSET_SIGNING_FINISHED' as const;
export const ASSET_SIGNING_FAILED = 'ASSET_SIGNING_FAILED' as const;
export const ASSET_UPLOAD_FAILED = 'ASSET_UPLOAD_FAILED' as const;
export const ASSET_UPLOAD_FINISHED = 'ASSET_UPLOAD_FINISHED' as const;
export const ASSET_REPLACE_UPLOAD_FINISHED =
  'ASSET_REPLACE_UPLOAD_FINISHED' as const;
export const ASSET_REPLACE_UPLOAD_FAILED =
  'ASSET_REPLACE_UPLOAD_FAILED' as const;
export const ASSET_REPLACE_PERSIST_SUCCEEDED =
  'ASSET_REPLACE_PERSIST_SUCCEEDED' as const;
export const ASSET_REPLACE_PERSIST_FAILED =
  'ASSET_REPLACE_PERSIST_FAILED' as const;
export const ASSETS_LOADED = 'ASSETS_LOADED' as const;
export const PAGE_ASSETS_LOADED = 'PAGE_ASSETS_LOADED' as const;
export const ASSET_UPLOAD_FINISHED_REMOTELY =
  'ASSET_UPLOAD_FINISHED_REMOTELY' as const;
export const ASSETS_LIST_FIRST_FETCH_REQUESTED =
  'ASSETS_LIST_FIRST_FETCH_REQUESTED' as const;
export const ASSETS_LIST_FIRST_FETCH_SUCCEEDED =
  'ASSETS_LIST_FIRST_FETCH_SUCCEEDED' as const;
export const ASSETS_LIST_FIRST_FETCH_FAILED =
  'ASSETS_LIST_FIRST_FETCH_FAILED' as const;
export const ASSETS_LIST_NEXT_FETCH_REQUESTED =
  'ASSETS_LIST_NEXT_FETCH_REQUESTED' as const;
export const ASSETS_LIST_NEXT_FETCH_SUCCEEDED =
  'ASSETS_LIST_NEXT_FETCH_SUCCEEDED' as const;
export const ASSETS_LIST_NEXT_FETCH_FAILED =
  'ASSETS_LIST_NEXT_FETCH_FAILED' as const;
export const ASSETS_ITEM_FETCH_REQUESTED =
  'ASSETS_ITEM_FETCH_REQUESTED' as const;
export const ASSETS_ITEM_FETCH_SUCCEEDED =
  'ASSETS_ITEM_FETCH_SUCCEEDED' as const;
export const ASSETS_ITEM_FETCH_FAILED = 'ASSETS_ITEM_FETCH_FAILED' as const;
export const DATA_TYPE_CHANGED = 'DATA_TYPE_CHANGED' as const;
export const ELEMENT_EXPRESSION_LOCALIZED =
  'ELEMENT_EXPRESSION_LOCALIZED' as const;
export const ELEMENT_EXPRESSIONS_LOCALIZED =
  'ELEMENT_EXPRESSIONS_LOCALIZED' as const;
export const DYNAMO_BINDING_REMOVED = 'DYNAMO_BINDING_REMOVED' as const;
export const CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_STARTED =
  'CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_STARTED' as const;
export const CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_ENQUEUE_STARTED =
  'CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_ENQUEUE_STARTED' as const;
export const CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_ENQUEUE_SUCCEEDED =
  'CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_ENQUEUE_SUCCEEDED' as const;
export const CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_PROGRESSED =
  'CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_PROGRESSED' as const;
export const CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_FAILED =
  'CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_FAILED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_STARTED =
  'LOCALIZATION_CORRUPTION_RECOVERY_STARTED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_STARTED =
  'LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_STARTED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_SUCCEEDED =
  'LOCALIZATION_CORRUPTION_RECOVERY_ENQUEUE_SUCCEEDED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED =
  'LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_FAILED =
  'LOCALIZATION_CORRUPTION_RECOVERY_FAILED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_FINISHED =
  'LOCALIZATION_CORRUPTION_RECOVERY_FINISHED' as const;
export const LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED =
  'LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED' as const;
export const LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED =
  'LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED' as const;
export const LOCALIZATION_CORRUPTION_REPORT_NODES_RESOLVED =
  'LOCALIZATION_CORRUPTION_REPORT_NODES_RESOLVED' as const;
export const LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY =
  'LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY' as const;
export const LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY =
  'LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY' as const;
export const CORRUPTION_RECOVERY_PANEL_OPENED =
  'CORRUPTION_RECOVERY_PANEL_OPENED' as const;
export const CORRUPTION_RECOVERY_PANEL_CLOSED =
  'CORRUPTION_RECOVERY_PANEL_CLOSED' as const;
export const LOST_TRANSLATIONS_FETCH_FINISHED =
  'LOST_TRANSLATIONS_FETCH_FINISHED' as const;
export const LOST_TRANSLATIONS_FETCH_FAILED =
  'LOST_TRANSLATIONS_FETCH_FAILED' as const;
export const LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED =
  'LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED' as const;
export const LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED =
  'LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED' as const;
export const LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED =
  'LOST_TRANSLATIONS_REPORT_DOWNLOAD_REQUESTED' as const;
export const LOST_TRANSLATIONS_REPORT_DOWNLOAD_SUCCEEDED =
  'LOST_TRANSLATIONS_REPORT_DOWNLOAD_SUCCEEDED' as const;
export const LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED =
  'LOST_TRANSLATIONS_REPORT_DOWNLOAD_FAILED' as const;
export const CONTENT_EDITOR_IMAGE_IMPORT_FINISHED =
  'CONTENT_EDITOR_IMAGE_IMPORT_FINISHED' as const;
export const ASSET_UPLOAD_PROGRESSED = 'ASSET_UPLOAD_PROGRESSED' as const;
export const ASSET_REPLACE_UPLOAD_PROGRESSED =
  'ASSET_REPLACE_UPLOAD_PROGRESSED' as const;
export const ASSET_ALT_CHANGED_REMOTELY = 'ASSET_ALT_CHANGED_REMOTELY' as const;
export const ASSET_DETAILS_CHANGE_UNSAVED =
  'ASSET_DETAILS_CHANGE_UNSAVED' as const;
export const SHOW_ASSET_DETAILS_UNSAVED_CONFIRMATION =
  'SHOW_ASSET_DETAILS_UNSAVED_CONFIRMATION' as const;
export const ASSET_COLLAPSE_LIBRARY_SECTIONS =
  'ASSET_COLLAPSE_LIBRARY_SECTIONS' as const;
export const WORKSPACE_LOADED = 'WORKSPACE_LOADED' as const;
export const ATOMS_IFRAME_LOADED = 'ATOMS_IFRAME_LOADED' as const;
export const BETA_FEATURE_ENABLED = 'BETA_FEATURE_ENABLED' as const;
export const BETA_FEATURE_TOGGLED = 'BETA_FEATURE_TOGGLED' as const;
export const ADD_PANEL_QUERY_UPDATED = 'ADD_PANEL_QUERY_UPDATED' as const;
export const BETA_FEATURE_UPDATE_ERRORED =
  'BETA_FEATURE_UPDATE_ERRORED' as const;
export const BETA_FEATURE_UPDATE_SUCCEEDED =
  'BETA_FEATURE_UPDATE_SUCCEEDED' as const;
export const CANVAS_APP_NODE_HOVERED = 'CANVAS_APP_NODE_HOVERED' as const;
export const CANVAS_APP_NODE_CLICKED = 'CANVAS_APP_NODE_CLICKED' as const;
export const OVERLAY_SHOWN = 'OVERLAY_SHOWN' as const;
export const CANVAS_APP_ANNOTATION_CLICKED =
  'CANVAS_APP_ANNOTATION_CLICKED' as const;
export const CANVAS_BAR_RULER_VISIBILITY_CHANGED =
  'CANVAS_BAR_RULER_VISIBILITY_CHANGED' as const;
export const CANVAS_BAR_POSITION_CHANGED =
  'CANVAS_BAR_POSITION_CHANGED' as const;
export const CANVAS_APP_READY = 'CANVAS_APP_READY' as const;
export const CANVAS_APP_IFRAME_LOADED_STATE_CHANGED =
  'CANVAS_APP_IFRAME_LOADED_STATE_CHANGED' as const;
export const RAW_BRANCHED_DATA_RECEIVED = 'RAW_BRANCHED_DATA_RECEIVED' as const;
export const BRANCH_DELETE_STARTED = 'BRANCH_DELETE_STARTED' as const;
export const BRANCH_DELETE_SUCCEEDED = 'BRANCH_DELETE_SUCCEEDED' as const;
export const BRANCH_DELETE_FAILED = 'BRANCH_DELETE_FAILED' as const;
export const BRANCH_DELETE_CANCELLED = 'BRANCH_DELETE_CANCELLED' as const;
export const BRANCH_MERGE_INITIATED = 'BRANCH_MERGE_INITIATED' as const;
export const BRANCH_MERGE_STARTED = 'BRANCH_MERGE_STARTED' as const;
export const BRANCH_MERGE_SUCCEEDED = 'BRANCH_MERGE_SUCCEEDED' as const;
export const BRANCH_MERGE_CONFLICTED = 'BRANCH_MERGE_CONFLICTED' as const;
export const BRANCH_MERGE_FAILED = 'BRANCH_MERGE_FAILED' as const;
export const BRANCH_MERGE_CANCELLED = 'BRANCH_MERGE_CANCELLED' as const;
export const BRANCH_MERGE_RESOLUTIONS_SUBMITTED =
  'BRANCH_MERGE_RESOLUTIONS_SUBMITTED' as const;
export const BRANCH_REDIRECT_OPENED = 'BRANCH_REDIRECT_OPENED' as const;
export const BRANCH_REDIRECT_DEFERRED = 'BRANCH_REDIRECT_DEFERRED' as const;
export const BRANCH_REDIRECT_COMPLETED = 'BRANCH_REDIRECT_COMPLETED' as const;
export const BRANCH_MODE_DETECTED = 'BRANCH_MODE_DETECTED' as const;
export const BRANCH_CONFLICTS_RESOLUTION_SELECTED =
  'BRANCH_CONFLICTS_RESOLUTION_SELECTED' as const;
export const BRANCH_CONFLICTS_RESOLVE_ALL =
  'BRANCH_CONFLICTS_RESOLVE_ALL' as const;
export const BRANCH_CONFLICTS_SUBMIT_REQUESTED =
  'BRANCH_CONFLICTS_SUBMIT_REQUESTED' as const;
export const BRANCH_CONFLICTS_SUBMIT_SUCCEEDED =
  'BRANCH_CONFLICTS_SUBMIT_SUCCEEDED' as const;
export const BRANCH_CONFLICTS_SUBMIT_FAILED =
  'BRANCH_CONFLICTS_SUBMIT_FAILED' as const;
export const BRANCH_CONFLICTS_CANCELLED = 'BRANCH_CONFLICTS_CANCELLED' as const;
export const BRANCH_UPDATE_INITIATED = 'BRANCH_UPDATE_INITIATED' as const;
export const BRANCH_UPDATE_STARTED = 'BRANCH_UPDATE_STARTED' as const;
export const BRANCH_UPDATE_SUCCEEDED = 'BRANCH_UPDATE_SUCCEEDED' as const;
export const BRANCH_UPDATE_CONFLICTED = 'BRANCH_UPDATE_CONFLICTED' as const;
export const BRANCH_UPDATE_FAILED = 'BRANCH_UPDATE_FAILED' as const;
export const BRANCH_UPDATE_CANCELLED = 'BRANCH_UPDATE_CANCELLED' as const;
export const BRANCH_UPDATE_SKIPPED = 'BRANCH_UPDATE_SKIPPED' as const;
export const BRANCH_UPDATE_RESOLUTIONS_SUBMITTED =
  'BRANCH_UPDATE_RESOLUTIONS_SUBMITTED' as const;
export const BREAKPOINTS_CONFIG_LOADED = 'BREAKPOINTS_CONFIG_LOADED' as const;
export const CONTENT_EDITOR_ELEMENT_ADDED =
  'CONTENT_EDITOR_ELEMENT_ADDED' as const;
export const CONTENT_EDITOR_ELEMENT_INSERTED =
  'CONTENT_EDITOR_ELEMENT_INSERTED' as const;
export const CONTENT_EDITOR_COMPONENT_REMOVED =
  'CONTENT_EDITOR_COMPONENT_REMOVED' as const;
export const CONTENT_EDITOR_COMPONENT_ADDED =
  'CONTENT_EDITOR_COMPONENT_ADDED' as const;
export const CONTENT_EDITOR_FORMAT_TOGGLED =
  'CONTENT_EDITOR_FORMAT_TOGGLED' as const;
export const CONTENT_EDITOR_CHARS_INSERTED =
  'CONTENT_EDITOR_CHARS_INSERTED' as const;
export const CONTENT_EDITOR_CUSTOM_CLASS_ACCEPTED =
  'CONTENT_EDITOR_CUSTOM_CLASS_ACCEPTED' as const;
export const CONTENT_EDITOR_CUSTOM_CLASS_CANCELLED =
  'CONTENT_EDITOR_CUSTOM_CLASS_CANCELLED' as const;
export const CONTENT_EDITOR_CUSTOM_CLASS_REMOVED =
  'CONTENT_EDITOR_CUSTOM_CLASS_REMOVED' as const;
export const CONTENT_EDITOR_MOUSE_DOWN = 'CONTENT_EDITOR_MOUSE_DOWN' as const;
export const CONTENT_EDITOR_MOUSE_UP = 'CONTENT_EDITOR_MOUSE_UP' as const;
export const CONTENT_EDITOR_CUT = 'CONTENT_EDITOR_CUT' as const;
export const CONTENT_EDITOR_PASTE = 'CONTENT_EDITOR_PASTE' as const;
export const CONTENT_EDITOR_KEY_DOWN = 'CONTENT_EDITOR_KEY_DOWN' as const;
export const CONTENT_EDITOR_KEY_UP = 'CONTENT_EDITOR_KEY_UP' as const;
export const CONTENT_EDITOR_FIGURE_MOUSE_UP =
  'CONTENT_EDITOR_FIGURE_MOUSE_UP' as const;
export const CONTENT_EDITOR_KEYBOARD_SHORTCUT_PRESSED =
  'CONTENT_EDITOR_KEYBOARD_SHORTCUT_PRESSED' as const;
export const CONTENT_EDITOR_SELECTION_RECT_UPDATED =
  'CONTENT_EDITOR_SELECTION_RECT_UPDATED' as const;
export const CONTENT_EDITOR_SELECTION_UPDATED =
  'CONTENT_EDITOR_SELECTION_UPDATED' as const;
export const CONTENT_EDITOR_TEXT_SHORTCUT_ACTIVATED =
  'CONTENT_EDITOR_TEXT_SHORTCUT_ACTIVATED' as const;
export const CONTENT_EDITOR_MARKDOWN_FORMATTING_ACTIVATED =
  'CONTENT_EDITOR_MARKDOWN_FORMATTING_ACTIVATED' as const;
export const CONTENT_EDITOR_FIGURE_ALIGNMENT_CHANGED =
  'CONTENT_EDITOR_FIGURE_ALIGNMENT_CHANGED' as const;
export const CONTENT_EDITOR_FIGURE_SETTINGS_OPENED =
  'CONTENT_EDITOR_FIGURE_SETTINGS_OPENED' as const;
export const CONTENT_EDITOR_CODE_BLOCK_SETTINGS_OPENED =
  'CONTENT_EDITOR_CODE_BLOCK_SETTINGS_OPENED' as const;
export const CONTENT_EDITOR_COMPONENT_SETTINGS_OPENED =
  'CONTENT_EDITOR_COMPONENT_SETTINGS_OPENED' as const;
export const CONTENT_EDITOR_DATA_TYPE_CHANGED =
  'CONTENT_EDITOR_DATA_TYPE_CHANGED' as const;
export const CONTENT_EDITOR_INSTANCE_OVERRIDES_RESET =
  'CONTENT_EDITOR_INSTANCE_OVERRIDES_RESET' as const;
export const CONTENT_EDITOR_LINK_CANCELLED =
  'CONTENT_EDITOR_LINK_CANCELLED' as const;
export const CONTENT_EDITOR_LINK_REMOVED =
  'CONTENT_EDITOR_LINK_REMOVED' as const;
export const CONTENT_EDITOR_LINK_ACCEPTED =
  'CONTENT_EDITOR_LINK_ACCEPTED' as const;
export const CONTENT_EDITOR_LINK_TOOLBAR_SAVED =
  'CONTENT_EDITOR_LINK_TOOLBAR_SAVED' as const;
export const CONTENT_EDITOR_FIGURE_TITLE_UPDATED =
  'CONTENT_EDITOR_FIGURE_TITLE_UPDATED' as const;
export const CONTENT_EDITOR_VIDEO_CANCELLED =
  'CONTENT_EDITOR_VIDEO_CANCELLED' as const;
export const CONTENT_EDITOR_IMAGE_UPLOAD_PROGRESS_CHANGED =
  'CONTENT_EDITOR_IMAGE_UPLOAD_PROGRESS_CHANGED' as const;
export const CONTENT_EDITOR_IMAGE_PREVIEW_SET =
  'CONTENT_EDITOR_IMAGE_PREVIEW_SET' as const;
export const CONTENT_EDITOR_IMAGE_UPLOAD_FINISHED =
  'CONTENT_EDITOR_IMAGE_UPLOAD_FINISHED' as const;
export const CONTENT_EDITOR_IMAGE_UPLOAD_ERRORED =
  'CONTENT_EDITOR_IMAGE_UPLOAD_ERRORED' as const;
export const CONTENT_EDITOR_IMAGE_UPLOAD_FAILED =
  'CONTENT_EDITOR_IMAGE_UPLOAD_FAILED' as const;
export const CONTENT_EDITOR_IMAGE_REPLACEMENT_STARTED =
  'CONTENT_EDITOR_IMAGE_REPLACEMENT_STARTED' as const;
export const CONTENT_EDITOR_IMAGE_REPLACEMENT_SUCCEEDED =
  'CONTENT_EDITOR_IMAGE_REPLACEMENT_SUCCEEDED' as const;
export const CONTENT_EDITOR_IMAGE_REPLACEMENT_FAILED =
  'CONTENT_EDITOR_IMAGE_REPLACEMENT_FAILED' as const;
export const CONTENT_EDITOR_IMAGE_SPINNER_POSITIONS_CHANGED =
  'CONTENT_EDITOR_IMAGE_SPINNER_POSITIONS_CHANGED' as const;
export const CONTENT_EDITOR_FIGURE_ALIGNED_HEIGHT_UPDATED_FROM_SETTINGS =
  'CONTENT_EDITOR_FIGURE_ALIGNED_HEIGHT_UPDATED_FROM_SETTINGS' as const;
export const CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED =
  'CONTENT_EDITOR_FIGURE_ALT_TEXT_UPDATED' as const;
export const CONTENT_EDITOR_FIGURE_MAX_WIDTH_UPDATED =
  'CONTENT_EDITOR_FIGURE_MAX_WIDTH_UPDATED' as const;
export const CONTENT_EDITOR_FIGURE_MAX_WIDTH_HEIGHT_UPDATED =
  'CONTENT_EDITOR_FIGURE_MAX_WIDTH_HEIGHT_UPDATED' as const;
export const CONTENT_EDITOR_FIGURE_LINK_REMOVED =
  'CONTENT_EDITOR_FIGURE_LINK_REMOVED' as const;
export const CONTENT_EDITOR_FIGURE_LINK_UPDATED =
  'CONTENT_EDITOR_FIGURE_LINK_UPDATED' as const;
export const CONTENT_EDITOR_UNDO = 'CONTENT_EDITOR_UNDO' as const;
export const CONTENT_EDITOR_REDO = 'CONTENT_EDITOR_REDO' as const;
export const CONTENT_EDITOR_IMAGE_IMPORT_STARTED =
  'CONTENT_EDITOR_IMAGE_IMPORT_STARTED' as const;
export const CONTENT_EDITOR_IMAGE_IMPORT_FAILED =
  'CONTENT_EDITOR_IMAGE_IMPORT_FAILED' as const;
export const CONTENT_EDITOR_TEXT_VALUE_REPLACED =
  'CONTENT_EDITOR_TEXT_VALUE_REPLACED' as const;
export const CONTENT_EDITOR_LINK_TOOLBAR_CHANGES_MADE =
  'CONTENT_EDITOR_LINK_TOOLBAR_CHANGES_MADE' as const;
export const CONTENT_EDITOR_LINK_MODE_STARTED =
  'CONTENT_EDITOR_LINK_MODE_STARTED' as const;
export const EDITOR_COMMERCE_SUBSECTION_CLICKED =
  'EDITOR_COMMERCE_SUBSECTION_CLICKED' as const;
export const EDITOR_SECTION_AND_COMMERCE_SUBSECTION_CLICKED =
  'EDITOR_SECTION_AND_COMMERCE_SUBSECTION_CLICKED' as const;
export const CONTRIBUTOR_EDITABLE_TOGGLED =
  'CONTRIBUTOR_EDITABLE_TOGGLED' as const;
export const CSS_PREVIEW_COPIED_TO_CLIPBOARD =
  'CSS_PREVIEW_COPIED_TO_CLIPBOARD' as const;
export const USER_CAPABILITIES_CHANGED = 'USER_CAPABILITIES_CHANGED' as const;
export const ELEMENT_CONVERTED = 'ELEMENT_CONVERTED' as const;
export const ELEMENT_CONVERT_SUCCEEDED = 'ELEMENT_CONVERT_SUCCEEDED' as const;
export const ELEMENT_WRAPPED = 'ELEMENT_WRAPPED' as const;
export const ELEMENT_UNWRAPPED = 'ELEMENT_UNWRAPPED' as const;
export const CANVAS_DYN_LIST_CHECKED_FOR_FILTERS =
  'CANVAS_DYN_LIST_CHECKED_FOR_FILTERS' as const;
export const CANVAS_DYN_ITEM_SAVED = 'CANVAS_DYN_ITEM_SAVED' as const;
export const CANVAS_DYN_ITEM_SAVED_BEGIN =
  'CANVAS_DYN_ITEM_SAVED_BEGIN' as const;
export const CANVAS_DYN_ITEM_SAVED_ERROR =
  'CANVAS_DYN_ITEM_SAVED_ERROR' as const;
export const CANVAS_IMG_UPLOADED = 'CANVAS_IMG_UPLOADED' as const;
export const CANVAS_IMG_UPLOADED_BEGIN = 'CANVAS_IMG_UPLOADED_BEGIN' as const;
export const CANVAS_IMG_UPLOADED_ERROR = 'CANVAS_IMG_UPLOADED_ERROR' as const;
export const CANVAS_NODE_CLICKED = 'CANVAS_NODE_CLICKED' as const;
export const CANVAS_NODE_HOVERED = 'CANVAS_NODE_HOVERED' as const;
export const CANVAS_SCROLL_POSITION_UPDATED =
  'CANVAS_SCROLL_POSITION_UPDATED' as const;
export const CANVAS_USER_SCROLL_STARTED = 'CANVAS_USER_SCROLL_STARTED' as const;
export const CANVAS_USER_SCROLL_ENDED = 'CANVAS_USER_SCROLL_ENDED' as const;
export const CANVAS_SCROLL_SIZE_UPDATED = 'CANVAS_SCROLL_SIZE_UPDATED' as const;
export const CANVAS_STYLE_SAVED = 'CANVAS_STYLE_SAVED' as const;
export const CANVAS_TIP_SETTING_TOGGLED = 'CANVAS_TIP_SETTING_TOGGLED' as const;
export const CANVAS_SPACING_PEEKED = 'CANVAS_SPACING_PEEKED' as const;
export const CANVAS_DYNAMO_STORED = 'CANVAS_DYNAMO_STORED' as const;
export const CANVAS_WIDTH_SET = 'CANVAS_WIDTH_SET' as const;
export const CANVAS_SCALE_SET = 'CANVAS_SCALE_SET' as const;
export const CANVAS_SIZE_RESET_CLICKED = 'CANVAS_SIZE_RESET_CLICKED' as const;
export const WINDOW_AND_CANVAS_RESIZED = 'WINDOW_AND_CANVAS_RESIZED' as const;
export const CLASS_AUTO_CREATED = 'CLASS_AUTO_CREATED' as const;
export const CMS_ITEM_SAVE_BUTTON_CLICKED =
  'CMS_ITEM_SAVE_BUTTON_CLICKED' as const;
export const CMS_ITEM_SAVE_FAILED = 'CMS_ITEM_SAVE_FAILED' as const;
export const CMS_ITEM_PREVIEW_LINK_COPIED_TO_CLIPBOARD =
  'CMS_ITEM_PREVIEW_LINK_COPIED_TO_CLIPBOARD' as const;
export const CMS_LOGO_IMPORTED = 'CMS_LOGO_IMPORTED' as const;
export const CMS_RTF_COMPONENT_IDS_RECEIVED =
  'CMS_RTF_COMPONENT_IDS_RECEIVED' as const;
export const CMS_CAPABILITIES_RECEIVED = 'CMS_CAPABILITIES_RECEIVED' as const;
export const CMS_ASSETS_COMPRESSION_REQUESTED =
  'CMS_ASSETS_COMPRESSION_REQUESTED' as const;
export const CMS_ASSETS_COMPRESSION_FINISHED =
  'CMS_ASSETS_COMPRESSION_FINISHED' as const;
export const CMS_ASSETS_COMPRESSION_FAILED =
  'CMS_ASSETS_COMPRESSION_FAILED' as const;
export const CMS_ITEM_AUTOSAVE_CHANGES_SAVED =
  'CMS_ITEM_AUTOSAVE_CHANGES_SAVED' as const;
export const CMS_ITEM_DRAFT_CREATED = 'CMS_ITEM_DRAFT_CREATED' as const;
export const CMS_ITEM_DRAFT_CHANGES_STATUS_CHANGED =
  'CMS_ITEM_DRAFT_CHANGES_STATUS_CHANGED' as const;
export const CMS_ITEM_DRAFT_STATUS_CHANGED =
  'CMS_ITEM_DRAFT_STATUS_CHANGED' as const;
export const CMS_ITEM_QUEUED_STATUS_CHANGED =
  'CMS_ITEM_QUEUED_STATUS_CHANGED' as const;
export const CMS_ITEM_FAILED_SCHEDULED_CHANGED =
  'CMS_ITEM_FAILED_SCHEDULED_CHANGED' as const;
export const CMS_ITEM_SCHEDULED_CHANGED = 'CMS_ITEM_SCHEDULED_CHANGED' as const;
export const CMS_ITEM_ARCHIVED_STATUS_CHANGED =
  'CMS_ITEM_ARCHIVED_STATUS_CHANGED' as const;
export const COLLECTION_ADDED_REMOTELY = 'COLLECTION_ADDED_REMOTELY' as const;
export const COLLECTION_FIELD_USAGES_REQUESTED =
  'COLLECTION_FIELD_USAGES_REQUESTED' as const;
export const COLLECTION_FIELD_USAGES_REQUESTED_BEGIN =
  'COLLECTION_FIELD_USAGES_REQUESTED_BEGIN' as const;
export const COLLECTION_FIELD_USAGE_PAGE_DATA_REQUESTED =
  'COLLECTION_FIELD_USAGE_PAGE_DATA_REQUESTED' as const;
export const COLLECTION_FIELD_USAGE_SYMBOL_DATA_REQUESTED =
  'COLLECTION_FIELD_USAGE_SYMBOL_DATA_REQUESTED' as const;
export const COLLECTION_FIELD_USAGES_MODAL_CLOSED =
  'COLLECTION_FIELD_USAGES_MODAL_CLOSED' as const;
export const COLLECTION_FIELD_USAGE_MODAL_AND_CMS_CLOSED =
  'COLLECTION_FIELD_USAGE_MODAL_AND_CMS_CLOSED' as const;
export const COLLECTION_PRESET_APPLIED = 'COLLECTION_PRESET_APPLIED' as const;
export const COLLECTION_REMOVED_REMOTELY =
  'COLLECTION_REMOVED_REMOTELY' as const;
export const COLLECTION_UPDATED_REMOTELY =
  'COLLECTION_UPDATED_REMOTELY' as const;
export const COLLECTIONS_RECEIVED = 'COLLECTIONS_RECEIVED' as const;
export const COLLECTION_ITEM_COUNTS_RECEIVED =
  'COLLECTION_ITEM_COUNTS_RECEIVED' as const;

// Ecommerce Reactor
export const COMMERCE_DRIVERS_FETCH_STARTED =
  'COMMERCE_DRIVERS_FETCH_STARTED' as const;
export const COMMERCE_DRIVERS_FETCH_SUCCEEDED =
  'COMMERCE_DRIVERS_FETCH_SUCCEEDED' as const;
export const COMMERCE_DRIVERS_FETCH_FAILED =
  'COMMERCE_DRIVERS_FETCH_FAILED' as const;
export const COMMERCE_DRIVER_FIELD_EDITED =
  'COMMERCE_DRIVER_FIELD_EDITED' as const;
export const COMMERCE_REACTOR_DRIVER_SAVE =
  'COMMERCE_REACTOR_DRIVER_SAVE' as const;
export const COMMERCE_REACTOR_DRIVER_SAVE_SUCCEEDED =
  'COMMERCE_REACTOR_DRIVER_SAVE_SUCCEEDED' as const;
export const COMMERCE_REACTOR_DRIVER_SAVE_FAILED =
  'COMMERCE_REACTOR_DRIVER_SAVE_FAILED' as const;
export const COMMERCE_REACTOR_DRIVER_SELECTED =
  'COMMERCE_REACTOR_DRIVER_SELECTED' as const;
export const COMMERCE_DRIVER_CHANGES_ABANDONDED =
  'COMMERCE_DRIVER_CHANGES_ABANDONDED' as const;
export const COMMERCE_REACTOR_DRIVER_DISCONNECTED =
  'COMMERCE_REACTOR_DRIVER_DISCONNECTED' as const;
export const COMMERCE_REACTOR_DRIVER_DISCONNECTED_SUCCEEDED =
  'COMMERCE_REACTOR_DRIVER_DISCONNECTED_SUCCEEDED' as const;
/*****/
export const COMMERCE_ADD_SHIPPING_ZONE_CLICKED =
  'COMMERCE_ADD_SHIPPING_ZONE_CLICKED' as const;
export const COMMERCE_ADD_SHIPPING_METHOD_SELECTED =
  'COMMERCE_ADD_SHIPPING_METHOD_SELECTED' as const;
export const COMMERCE_CORRUPTED_VARIANTS_FIXED =
  ' COMMERCE_CORRUPTED_VARIANTS_FIXED' as const;
export const COMMERCE_CORRUPTED_VARIANTS_MODAL_CANCELED =
  'COMMERCE_CORRUPTED_VARIANTS_MODAL_CANCELED' as const;
// CC-240: dispatched upstream when an orphaned default-sku is detected, so
// ProductItemStore (and any panels keyed to the product) can reset to a
// clean state instead of editing on top of stale data.
export const COMMERCE_CORRUPTED_PRODUCT_DETECTED =
  'COMMERCE_CORRUPTED_PRODUCT_DETECTED' as const;
export const COMMERCE_SETTINGS_FETCH = 'COMMERCE_SETTINGS_FETCH' as const;
export const COMMERCE_SETTINGS_FETCH_SUCCEEDED =
  'COMMERCE_SETTINGS_FETCH_SUCCEEDED' as const;
export const COMMERCE_SETTINGS_FETCH_FAILED =
  'COMMERCE_SETTINGS_FETCH_FAILED' as const;
export const COMMERCE_NEXUS_ADDRESS_MODAL_OPEN_CLICKED =
  'COMMERCE_NEXUS_ADDRESS_MODAL_OPEN_CLICKED' as const;
export const COMMERCE_NEXUS_ADDRESS_MODAL_OPEN_CANCELED =
  'COMMERCE_NEXUS_ADDRESS_MODAL_OPEN_CANCELED' as const;
export const COMMERCE_STORE_SETUP_MODAL_OPENED =
  'COMMERCE_STORE_SETUP_MODAL_OPENED' as const;
export const COMMERCE_STORE_SETUP_MODAL_CANCELED =
  'COMMERCE_STORE_SETUP_MODAL_CANCELED' as const;
export const COMMERCE_CANCEL_SHIPPING_METHOD_CLICKED =
  'COMMERCE_CANCEL_SHIPPING_METHOD_CLICKED' as const;
export const COMMERCE_SAVE_SHIPPING_METHOD_CLICKED =
  'COMMERCE_SAVE_SHIPPING_METHOD_CLICKED' as const;
export const COMMERCE_SHIPPING_METHOD_FIELD_CHANGED =
  'COMMERCE_SHIPPING_METHOD_FIELD_CHANGED' as const;
export const COMMERCE_SHIPPING_METHOD_EDIT_CLICKED =
  'COMMERCE_SHIPPING_METHOD_EDIT_CLICKED' as const;
export const COMMERCE_SHIPPING_METHOD_DELETE_CLICKED =
  'COMMERCE_SHIPPING_METHOD_DELETE_CLICKED' as const;
export const COMMERCE_SHIPPING_METHOD_SUBMITTED =
  'COMMERCE_SHIPPING_METHOD_SUBMITTED' as const;
export const COMMERCE_EDIT_SHIPPING_ZONE_CLICKED =
  'COMMERCE_EDIT_SHIPPING_ZONE_CLICKED' as const;
export const COMMERCE_SHIPPING_ZONE_EDIT_COUNTRIES_CLICKED =
  'COMMERCE_SHIPPING_ZONE_EDIT_COUNTRIES_CLICKED' as const;
export const COMMERCE_CLOSE_COUNTRY_MODAL_CLICKED =
  'COMMERCE_CLOSE_COUNTRY_MODAL_CLICKED' as const;
export const COMMERCE_SAVE_SHIPPING_ZONE_COUNTRIES_CLICKED =
  'COMMERCE_SAVE_SHIPPING_ZONE_COUNTRIES_CLICKED' as const;
export const COMMERCE_SHIPPING_ZONE_FIELD_EDITED =
  'COMMERCE_SHIPPING_ZONE_FIELD_EDITED' as const;
export const COMMERCE_DELETE_SHIPPING_ZONE_COUNTRY =
  'COMMERCE_DELETE_SHIPPING_ZONE_COUNTRY' as const;
export const COMMERCE_NEW_SHIPPING_METHOD_RANGE_CLICKED =
  'COMMERCE_NEW_SHIPPING_METHOD_RANGE_CLICKED' as const;
export const COMMERCE_NEW_SHIPPING_METHOD_RANGE_REQUESTED =
  'COMMERCE_NEW_SHIPPING_METHOD_RANGE_REQUESTED' as const;
export const COMMERCE_CURRENT_SHIPPING_METHOD_RANGE_DELETE_CLICKED =
  'COMMERCE_CURRENT_SHIPPING_METHOD_RANGE_DELETE_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_SELECTED =
  'COMMERCE_GETTING_STARTED_SELECTED' as const;
export const COMMERCE_GETTING_STARTED_DESELECTED =
  'COMMERCE_GETTING_STARTED_DESELECTED' as const;
export const COMMERCE_DISCOUNTS_EXPORT_SUCCEEDED =
  'COMMERCE_DISCOUNTS_EXPORT_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_EXPORT_STARTED =
  'COMMERCE_DISCOUNTS_EXPORT_STARTED' as const;
export const COMMERCE_DISCOUNTS_GENERATE_CODE_CLICKED =
  'COMMERCE_DISCOUNTS_GENERATE_CODE_CLICKED' as const;
export const COMMERCE_DISCOUNTS_LIST_PANE_VIEW_SELECTED =
  'COMMERCE_DISCOUNTS_LIST_PANE_VIEW_SELECTED' as const;
export const COMMERCE_DISCOUNTS_LIST_PANE_VIEW_DESELECTED =
  'COMMERCE_DISCOUNTS_LIST_PANE_VIEW_DESELECTED' as const;
export const COMMERCE_DISCOUNTS_PANE_VIEW_ADD_EDIT_SELECTED =
  'COMMERCE_DISCOUNTS_PANE_VIEW_ADD_EDIT_SELECTED' as const;
export const COMMERCE_DISCOUNTS_PANE_VIEW_ADD_EDIT_DESELECTED =
  'COMMERCE_DISCOUNTS_PANE_VIEW_ADD_EDIT_DESELECTED' as const;
export const COMMERCE_ORDER_MANAGER_SELECTED =
  'COMMERCE_ORDER_MANAGER_SELECTED' as const;
export const COMMERCE_ORDER_MANAGER_DESELECTED =
  'COMMERCE_ORDER_MANAGER_DESELECTED' as const;
export const COMMERCE_SUBSCRIPTIONS_LIST_PANE_SELECTED =
  'COMMERCE_SUBSCRIPTIONS_LIST_PANE_SELECTED' as const;
export const COMMERCE_SUBSCRIPTIONS_LIST_PANE_DESELECTED =
  'COMMERCE_SUBSCRIPTIONS_LIST_PANE_DESELECTED' as const;
export const COMMERCE_GETTING_STARTED_ADD_BUSINESS_ADDRESS_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_BUSINESS_ADDRESS_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ADD_SHIPPING_METHOD_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_SHIPPING_METHOD_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_REVIEW_CURRENCY_SETTINGS_CLICKED =
  'COMMERCE_GETTING_STARTED_REVIEW_CURRENCY_SETTINGS_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_REVIEW_TAX_SETTINGS_CLICKED =
  'COMMERCE_GETTING_STARTED_REVIEW_TAX_SETTINGS_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ADD_PAYMENT_PROVIDER_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_PAYMENT_PROVIDER_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ADD_PRODUCT_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_PRODUCT_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ADD_HOSTING_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_HOSTING_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ENABLE_SSL_CLICKED =
  'COMMERCE_GETTING_STARTED_ENABLE_SSL_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ADD_CART_CLICKED =
  'COMMERCE_GETTING_STARTED_ADD_CART_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_STATUS_FETCH =
  'COMMERCE_GETTING_STARTED_STATUS_FETCH' as const;
export const COMMERCE_GETTING_STARTED_STATUS_FETCH_SUCCEEDED =
  'COMMERCE_GETTING_STARTED_STATUS_FETCH_SUCCEEDED' as const;
export const COMMERCE_GETTING_STARTED_GO_TO_PRODUCT_PAGE_CLICKED =
  'COMMERCE_GETTING_STARTED_GO_TO_PRODUCT_PAGE_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_GO_TO_CHECKOUT_PAGE_CLICKED =
  'COMMERCE_GETTING_STARTED_GO_TO_CHECKOUT_PAGE_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_CHECK_EMAIL_SETTINGS_CLICKED =
  'COMMERCE_GETTING_STARTED_CHECK_EMAIL_SETTINGS_CLICKED' as const;
export const COMMERCE_GETTING_STARTED_ENABLE_CHECKOUT_CLICKED =
  'COMMERCE_GETTING_STARTED_ENABLE_CHECKOUT_CLICKED' as const;
export const COMMERCE_TAX_SETTINGS_FIRST_TIME_VIEWED =
  'COMMERCE_TAX_SETTINGS_FIRST_TIME_VIEWED' as const;
export const COMMERCE_EMAIL_SETTINGS_FIRST_TIME_VIEWED =
  'COMMERCE_EMAIL_SETTINGS_FIRST_TIME_VIEWED' as const;
export const COMMERCE_PRODUCT_PAGE_FIRST_TIME_VIEWED =
  'COMMERCE_PRODUCT_PAGE_FIRST_TIME_VIEWED' as const;
export const COMMERCE_CHECKOUT_PAGE_FIRST_TIME_VIEWED =
  'COMMERCE_CHECKOUT_PAGE_FIRST_TIME_VIEWED' as const;
export const COMMERCE_CART_FIRST_TIME_ADDED =
  'COMMERCE_CART_FIRST_TIME_ADDED' as const;
export const COND_VIS_PARAM_ADDED = 'COND_VIS_PARAM_ADDED' as const;
export const COND_VIS_PARAM_REMOVED = 'COND_VIS_PARAM_REMOVED' as const;
export const COND_VIS_PARAM_CHANGED = 'COND_VIS_PARAM_CHANGED' as const;
export const COMMERCE_SETTINGS_LOCAL_CHANGE_REQUESTED =
  'COMMERCE_SETTINGS_LOCAL_CHANGE_REQUESTED' as const;
export const COMMERCE_SETTINGS_ADDRESS_ERRORS_UPDATED =
  'COMMERCE_SETTINGS_ADDRESS_ERRORS_UPDATED' as const;
export const COMMERCE_SETTINGS_LOCAL_CHANGES_ABANDONED =
  'COMMERCE_SETTINGS_LOCAL_CHANGES_ABANDONED' as const;
export const COMMERCE_SETTINGS_SAVE = 'COMMERCE_SETTINGS_SAVE' as const;
export const COMMERCE_SETTINGS_SAVE_SUCCEEDED =
  'COMMERCE_SETTINGS_SAVE_SUCCEEDED' as const;
export const COMMERCE_SETTINGS_SAVE_SUCCEEDED_ERROR =
  'COMMERCE_SETTINGS_SAVE_SUCCEEDED_ERROR' as const;
export const COMMERCE_SETTINGS_SUBMITTED =
  'COMMERCE_SETTINGS_SUBMITTED' as const;
export const COMMERCE_SETTINGS_PANEL_CLOSED =
  'COMMERCE_SETTINGS_PANEL_CLOSED' as const;
export const COMMERCE_SETTINGS_PANEL_CLOSED_CHANGE_REQUESTED =
  'COMMERCE_SETTINGS_PANEL_CLOSED_CHANGE_REQUESTED' as const;
export const COMMERCE_SETTINGS_SELECTED = 'COMMERCE_SETTINGS_SELECTED' as const;
export const COMMERCE_CONNECT_REQUESTED = 'COMMERCE_CONNECT_REQUESTED' as const;
export const COMMERCE_CONNECT_SUCCEEDED = 'COMMERCE_CONNECT_SUCCEEDED' as const;
export const COMMERCE_CONNECT_FAILED = 'COMMERCE_CONNECT_FAILED' as const;
export const COMMERCE_STATUS_FETCH = 'COMMERCE_STATUS_FETCH' as const;
export const COMMERCE_STATUS_FETCH_SUCCEEDED =
  'COMMERCE_STATUS_FETCH_SUCCEEDED' as const;
export const COMMERCE_STATUS_FETCH_FAILED =
  'COMMERCE_STATUS_FETCH_FAILED' as const;
export const COMMERCE_PAYPAL_STATUS_FETCH =
  'COMMERCE_PAYPAL_STATUS_FETCH' as const;
export const COMMERCE_PAYPAL_STATUS_FETCH_SUCCEEDED =
  'COMMERCE_PAYPAL_STATUS_FETCH_SUCCEEDED' as const;
export const COMMERCE_PAYPAL_STATUS_FETCH_FAILED =
  'COMMERCE_PAYPAL_STATUS_FETCH_FAILED' as const;
export const COMMERCE_PAYPAL_STATUS_UPDATE =
  'COMMERCE_PAYPAL_STATUS_UPDATE' as const;
export const COMMERCE_PAYPAL_CHECKOUT_PAGE_CREATED =
  'COMMERCE_PAYPAL_CHECKOUT_PAGE_CREATED' as const;
export const COMMERCE_PAYPAL_STATUS_UPDATE_SUCCEEDED =
  'COMMERCE_PAYPAL_STATUS_UPDATE_SUCCEEDED' as const;
export const COMMERCE_PAYPAL_STATUS_UPDATE_FAILED =
  'COMMERCE_PAYPAL_STATUS_UPDATE_FAILED' as const;
export const COMMERCE_CONFIRMATION_FOR_CURRENCY_CHANGE_REQUESTED =
  'COMMERCE_CONFIRMATION_FOR_CURRENCY_CHANGE_REQUESTED' as const;
export const COMMERCE_CONFIRMATION_FOR_CURRENCY_CHANGE_CANCELED =
  'COMMERCE_CONFIRMATION_FOR_CURRENCY_CHANGE_CANCELED' as const;
export const COMMERCE_STRIPE_URL_FETCH = 'COMMERCE_STRIPE_URL_FETCH' as const;
export const COMMERCE_STRIPE_URL_FETCH_SUCCEEDED =
  'COMMERCE_STRIPE_URL_FETCH_SUCCEEDED' as const;
export const COMMERCE_STRIPE_URL_FETCH_ERROR =
  'COMMERCE_STRIPE_URL_FETCH_ERROR' as const;
export const COMMERCE_STRIPE_DISCONNECT_REQUESTED =
  'COMMERCE_STRIPE_DISCONNECT_REQUESTED' as const;
export const COMMERCE_STRIPE_DISCONNECT_SUCCEEDED =
  'COMMERCE_STRIPE_DISCONNECT_SUCCEEDED' as const;
export const COMMERCE_STRIPE_DISCONNECT_FAILED =
  'COMMERCE_STRIPE_DISCONNECT_FAILED' as const;
export const COMMERCE_PAYPAL_URL_FETCH_FAILED =
  'COMMERCE_PAYPAL_URL_FETCH_FAILED' as const;
export const COMMERCE_PAYPAL_DISCONNECT_REQUESTED =
  'COMMERCE_PAYPAL_DISCONNECT_REQUESTED' as const;
export const COMMERCE_PAYPAL_DISCONNECT_SUCCEEDED =
  'COMMERCE_PAYPAL_DISCONNECT_SUCCEEDED' as const;
export const COMMERCE_PAYPAL_DISCONNECT_FAILED =
  'COMMERCE_PAYPAL_DISCONNECT_FAILED' as const;
export const COMMERCE_PAYPAL_BUTTON_NOTIFICATION_GO_TO_PAYMENT_SETTINGS_CLICKED =
  'COMMERCE_PAYPAL_BUTTON_NOTIFICATION_GO_TO_PAYMENT_SETTINGS_CLICKED' as const;
export const COMMERCE_PAYPAL_PAGE_NOTIFICATION_GO_TO_PAYMENT_SETTINGS_CLICKED =
  'COMMERCE_PAYPAL_PAGE_NOTIFICATION_GO_TO_PAYMENT_SETTINGS_CLICKED' as const;
export const COMMERCE_PUBLISH_BLOCKED_NOTIFICATION_GO_TO_STRIPE_SETTINGS_CLICKED =
  'COMMERCE_PUBLISH_BLOCKED_NOTIFICATION_GO_TO_STRIPE_SETTINGS_CLICKED' as const;
export const COMMERCE_PUBLISH_BLOCKED_NOTIFICATION_GO_TO_PAYPAL_SETTINGS_CLICKED =
  'COMMERCE_PUBLISH_BLOCKED_NOTIFICATION_GO_TO_PAYPAL_SETTINGS_CLICKED' as const;
export const COMMERCE_CART_OPEN_STATE_UPDATED =
  'COMMERCE_CART_OPEN_STATE_UPDATED' as const;
export const __DEPRECATED__COMMERCE_ADD_TO_CART_CLICK_NOT_ALLOWED =
  '__DEPRECATED__COMMERCE_ADD_TO_CART_CLICK_NOT_ALLOWED' as const;
export const __DEPRECATED__COMMERCE_CART_OPEN_CLICK_NOT_ALLOWED =
  '__DEPRECATED__COMMERCE_CART_OPEN_CLICK_NOT_ALLOWED' as const;
export const COMMERCE_AUTO_TAX_COUNTRY_SELECTED =
  'COMMERCE_AUTO_TAX_COUNTRY_SELECTED' as const;
export const COMMERCE_EMAIL_SETTINGS_CLICKED =
  'COMMERCE_EMAIL_SETTINGS_CLICKED' as const;
export const COMMERCE_NEXUS_ADDRESS_EDIT_REQUESTED =
  'COMMERCE_NEXUS_ADDRESS_EDIT_REQUESTED' as const;
export const COMMERCE_NEXUS_ADDRESS_REMOVE_REQUESTED =
  'COMMERCE_NEXUS_ADDRESS_REMOVE_REQUESTED' as const;
export const COMMERCE_TAXES_SETTINGS_SECTION_SELECTED =
  'COMMERCE_TAXES_SETTINGS_SECTION_SELECTED' as const;
export const COMMERCE_NOTIFICATIONS_SETTINGS_SELECTED =
  'COMMERCE_NOTIFICATIONS_SETTINGS_SELECTED' as const;
export const COMMERCE_STRIPE_CONNECTION_REQUESTED =
  'COMMERCE_STRIPE_CONNECTION_REQUESTED' as const;
export const COMMERCE_ENABLED_MODAL_VIEWED =
  'COMMERCE_ENABLED_MODAL_VIEWED' as const;
export const COMMERCE_ORDER_MANAGER_VIEWED =
  'COMMERCE_ORDER_MANAGER_VIEWED' as const;
export const COMMERCE_ITEM_SAVED = 'COMMERCE_ITEM_SAVED' as const;
export const COMMERCE_ITEM_CREATION_RECOVERY_STARTED =
  'COMMERCE_ITEM_CREATION_RECOVERY_STARTED' as const;
export const COMMERCE_INCLUDE_DOWNLOADS_TOGGLED =
  'COMMERCE_INCLUDE_DOWNLOADS_TOGGLED' as const;
export const COMMERCE_SUBSCRIPTIONS_FETCH_SUCCEEDED =
  'COMMERCE_SUBSCRIPTIONS_FETCH_SUCCEEDED' as const;
export const COMMERCE_SUBSCRIPTIONS_FETCH_FAILED =
  'COMMERCE_SUBSCRIPTIONS_FETCH_FAILED' as const;
export const COMMERCE_SUBSCRIPTION_SELECTED =
  'COMMERCE_SUBSCRIPTION_SELECTED' as const;
export const COMMERCE_SUBSCRIPTION_DESELECTED =
  'COMMERCE_SUBSCRIPTION_DESELECTED' as const;
export const COMMERCE_SELECTED_SUBSCRIPTIONS_CANCEL_SUCCEEDED =
  'COMMERCE_SELECTED_SUBSCRIPTIONS_CANCEL_SUCCEEDED' as const;
export const COMMERCE_SELECTED_SUBSCRIPTIONS_CANCEL_FAILED =
  'COMMERCE_SELECTED_SUBSCRIPTIONS_CANCEL_FAILED' as const;

export const CONFIRM_FEEDBACK_MODAL_CLOSE =
  'CONFIRM_FEEDBACK_MODAL_CLOSE' as const;
export const CONFIRM_OPENED = 'CONFIRM_OPENED' as const;
export const CONFIRM_PROCESSING_STARTED = 'CONFIRM_PROCESSING_STARTED' as const;
export const CONFIRM_REJECTED = 'CONFIRM_REJECTED' as const;
export const CONFIRM_RESOLVED = 'CONFIRM_RESOLVED' as const;
export const CONTENT_EDITOR_EMBED_TEXT_SAVED =
  'CONTENT_EDITOR_EMBED_TEXT_SAVED' as const;
export const CONTEXT_DROPDOWN_TOGGLED = 'CONTEXT_DROPDOWN_TOGGLED' as const;
export const CONTEXT_DROPDOWN_TRIGGER_CLICKED =
  'CONTEXT_DROPDOWN_TRIGGER_CLICKED' as const;
export const CONTEXT_MENU_ARCHIVE = 'CONTEXT_MENU_ARCHIVE' as const;
export const RENAME_TRIGGERED = 'RENAME_TRIGGERED' as const;
export const FLUSH_RENAME_TRIGGERED = 'FLUSH_RENAME_TRIGGERED' as const;
export const CONTEXT_MENU_ADD_CLASS = 'CONTEXT_MENU_ADD_CLASS' as const;
export const CONTEXT_MENU_RENAME_CLASS = 'CONTEXT_MENU_RENAME_CLASS' as const;
export const CONTEXT_MENU_DUPLICATE = 'CONTEXT_MENU_DUPLICATE' as const;
export const CONTEXT_MENU_MOUSE_DOWN = 'CONTEXT_MENU_MOUSE_DOWN' as const;
export const CONTEXT_MENU_MOUSE_UP = 'CONTEXT_MENU_MOUSE_UP' as const;
export const CONTEXT_MENU_MOVE_TO_PARENT =
  'CONTEXT_MENU_MOVE_TO_PARENT' as const;
export const CONTEXT_MENU_OPEN_MINI_SETTINGS =
  'CONTEXT_MENU_OPEN_MINI_SETTINGS' as const;
export const CONTEXT_MENU_SELECT_PARENT = 'CONTEXT_MENU_SELECT_PARENT' as const;
export const ASSISTANT_WHTML_PREVIEW_ADDED =
  'ASSISTANT_WHTML_PREVIEW_ADDED' as const;
export const AIDA_NEW_SECTION_ADDED = 'AIDA_NEW_SECTION_ADDED' as const;
export const COPY = 'COPY' as const;
export const CUT = 'CUT' as const;
export const EDIT_FIELD_ON_CANVAS_STARTED =
  'EDIT_FIELD_ON_CANVAS_STARTED' as const;
export const TEXT_OF_NODE_SELECTED_IN_INSTANCE_EDITING_ENDED =
  'TEXT_OF_NODE_SELECTED_IN_INSTANCE_EDITING_ENDED' as const;
export const EDIT_RICH_TEXT_FIELD_ON_CANVAS_STARTED =
  'EDIT_RICH_TEXT_FIELD_ON_CANVAS_STARTED' as const;
/* Credentials */
export const CREDENTIALS_RECEIVED = 'CREDENTIALS_RECEIVED' as const;
export const CREDENTIALS_REQUEST_STARTED =
  'CREDENTIALS_REQUEST_STARTED' as const;
export const CREDENTIALS_REQUEST_FAILED = 'CREDENTIALS_REQUEST_FAILED' as const;
export const CREDENTIALS_CREATED = 'CREDENTIALS_CREATED' as const;
export const CREDENTIALS_CREATE_STARTED = 'CREDENTIALS_CREATE_STARTED' as const;
export const CREDENTIALS_CREATE_FAILED = 'CREDENTIALS_CREATE_FAILED' as const;
export const CREDENTIALS_UPDATED = 'CREDENTIALS_UPDATED' as const;
export const CREDENTIALS_UPDATE_STARTED = 'CREDENTIALS_UPDATE_STARTED' as const;
export const CREDENTIALS_UPDATE_FAILED = 'CREDENTIALS_UPDATE_FAILED' as const;
export const CREDENTIALS_DELETED = 'CREDENTIALS_DELETED' as const;
export const CREDENTIALS_DELETE_STARTED = 'CREDENTIALS_DELETE_STARTED' as const;
export const CREDENTIALS_DELETE_FAILED = 'CREDENTIALS_DELETE_FAILED' as const;
/*******/
export const COMPONENTS_VERSION_SYNCED = 'COMPONENTS_VERSION_SYNCED' as const;
export const DASHBOARD_NAV_ATTEMPTED = 'DASHBOARD_NAV_ATTEMPTED' as const;
export const DASHBOARD_NAV_ATTEMPTED_BEGIN =
  'DASHBOARD_NAV_ATTEMPTED_BEGIN' as const;
export const DASHBOARD_NAV_ATTEMPTED_ERROR =
  'DASHBOARD_NAV_ATTEMPTED_ERROR' as const;
export const DATA_MANAGER_MODAL_OPENED = 'DATA_MANAGER_MODAL_OPENED' as const;
export const DATA_MANAGER_MODAL_CLOSED = 'DATA_MANAGER_MODAL_CLOSED' as const;
export const DATA_MANAGER_PANE_SCROLLED = 'DATA_MANAGER_PANE_SCROLLED' as const;
export const DATA_MANAGER_HIDDEN = 'DATA_MANAGER_HIDDEN' as const;
export const DATA_MANAGER_HIDDEN_ERROR = 'DATA_MANAGER_HIDDEN_ERROR' as const;
export const DATA_MANAGER_TUTORIAL_HIDDEN =
  'DATA_MANAGER_TUTORIAL_HIDDEN' as const;
export const DATABASE_CREATED = 'DATABASE_CREATED' as const;
export const DEFAULT_VARIANT_UPDATED = 'DEFAULT_VARIANT_UPDATED' as const;
export const DELETE_KEY_PRESSED = 'DELETE_KEY_PRESSED' as const;
export const DESIGN_SYSTEM_INSPECTOR_DIALOG_VISIBILITY_CHANGED =
  'DESIGN_SYSTEM_INSPECTOR_DIALOG_VISIBILITY_CHANGED' as const;
export const TOGGLE_NAVIGATOR_REFACTOR = 'TOGGLE_NAVIGATOR_REFACTOR' as const;
export const DESIGNER_NAV_ATTEMPTED = 'DESIGNER_NAV_ATTEMPTED' as const;
export const DETAIL_ITEM_FETCHED = 'DETAIL_ITEM_FETCHED' as const;
export const DOCUMENT_SELECTED_FOR_UPLOAD =
  'DOCUMENT_SELECTED_FOR_UPLOAD' as const;
export const DOM_NODES_VERSION_SYNCED = 'DOM_NODES_VERSION_SYNCED' as const;
export const EXTENSION_EFFECT_STARTED = 'EXTENSION_EFFECT_STARTED' as const;
export const EXTENSION_EFFECT_TIMED_OUT = 'EXTENSION_EFFECT_TIMED_OUT' as const;

export const extensionEffectTimedOut = (payload: {effectId: string}) =>
  ({type: EXTENSION_EFFECT_TIMED_OUT, payload}) as const;
export const VIDEO_EMBED_RESOLVED = 'VIDEO_EMBED_RESOLVED' as const;

export const videoEmbedResolved = (payload: {
  elementId: {component: string; element: string};
  resolvedSettings: Record<string, unknown>;
}) => ({type: VIDEO_EMBED_RESOLVED, payload}) as const;
export const EXTENSION_OPEN = 'EXTENSION_OPEN' as const;
export const EXTENSION_CLOSE = 'EXTENSION_CLOSE' as const;
export const EXTENSION_DEV_URL_UPDATE = 'EXTENSION_DEV_URL_UPDATE' as const;
export const EXTENSION_DEV_LAUNCH_BY_DEFAULT_UPDATE =
  'EXTENSION_DEV_LAUNCH_BY_DEFAULT_UPDATE' as const;
export const EXTENSION_DEV_MANIFEST_SUCCEEDED =
  'EXTENSION_DEV_MANIFEST_SUCCEEDED' as const;
export const EXTENSION_DEV_MANIFEST_FAILED =
  'EXTENSION_DEV_MANIFEST_FAILED' as const;
export const EXTERNAL_CUSTOM_CODE_LOADED =
  'EXTERNAL_CUSTOM_CODE_LOADED' as const;
export const INTERACTIONS_VERSION_SYNCED =
  'INTERACTIONS_VERSION_SYNCED' as const;
export const STYLES_VERSION_SYNCED = 'STYLES_VERSION_SYNCED' as const;
export const DRAG_STOP = 'DRAG_STOP' as const;
export const DRAGGING_CANVAS_RESIZE_ENDED =
  'DRAGGING_CANVAS_RESIZE_ENDED' as const;
export const DRAGGING_CANVAS_RESIZE_STARTED =
  'DRAGGING_CANVAS_RESIZE_STARTED' as const;
export const DRAGGING_CANVAS_RESIZE_UPDATED =
  'DRAGGING_CANVAS_RESIZE_UPDATED' as const;
export const CANVAS_RESIZE_LABEL_TOGGLED =
  'CANVAS_RESIZE_LABEL_TOGGLED' as const;
export const CANVAS_RESIZE_ADVANCED_SETTINGS_CLICKED =
  'CANVAS_RESIZE_ADVANCED_SETTINGS_CLICKED' as const;
export const SPACING_DRAG_MARKED_SEEN = 'SPACING_DRAG_MARKED_SEEN' as const;
export const SPACING_HIGHLIGHT_FLASH_REQUESTED =
  'SPACING_HIGHLIGHT_FLASH_REQUESTED' as const;
export const SPACING_HIGHLIGHT_FLASH_CLEARED =
  'SPACING_HIGHLIGHT_FLASH_CLEARED' as const;
export const DRAGGING_IMAGE_RESIZE_CANCELED =
  'DRAGGING_IMAGE_RESIZE_CANCELED' as const;
export const DRAGGING_IMAGE_RESIZE_ENDED =
  'DRAGGING_IMAGE_RESIZE_ENDED' as const;
export const DRAGGING_IMAGE_RESIZE_STARTED =
  'DRAGGING_IMAGE_RESIZE_STARTED' as const;

export const DATA_AS_FILE_DOWNLOAD_REQUESTED =
  'DATA_AS_FILE_DOWNLOAD_REQUESTED' as const;
export const DATA_AS_FILE_DOWNLOADS_STARTED =
  'DATA_AS_FILE_DOWNLOADS_STARTED' as const;
export const PUBLISH_WORKFLOW_MODAL_OPENED =
  'PUBLISH_WORKFLOW_MODAL_OPENED' as const;
export const PUBLISH_WORKFLOW_MODAL_CLOSED =
  'PUBLISH_WORKFLOW_MODAL_CLOSED' as const;
export const PUBLISH_WORKFLOW_PUBLISH_CLICKED =
  'PUBLISH_WORKFLOW_PUBLISH_CLICKED' as const;
export const PUBLISH_WORKFLOW_PUBLISH_RESUMED =
  'PUBLISH_WORKFLOW_PUBLISH_RESUMED' as const;
export const PUBLISH_WORKFLOW_TARGET_SELECTED =
  'PUBLISH_WORKFLOW_TARGET_SELECTED' as const;
export const PUBLISH_WORKFLOW_PUBLISH_TYPE_SELECTED =
  'PUBLISH_WORKFLOW_PUBLISH_TYPE_SELECTED' as const;
export const PUBLISH_WORKFLOW_PUBLISH_SCOPE_TOGGLE_CLICKED =
  'PUBLISH_WORKFLOW_PUBLISH_SCOPE_TOGGLE_CLICKED' as const;
export const CHANGE_SUMMARY_LOADING_STARTED =
  'CHANGE_SUMMARY_LOADING_STARTED' as const;
export const CHANGE_SUMMARY_LOADING_FINISHED =
  'CHANGE_SUMMARY_LOADING_FINISHED' as const;
export const PUBLISH_ELIGIBILITY_LOADING_STARTED =
  'PUBLISH_ELIGIBILITY_LOADING_STARTED' as const;
export const PUBLISH_ELIGIBILITY_LOADING_FINISHED =
  'PUBLISH_ELIGIBILITY_LOADING_FINISHED' as const;
export const DOMAIN_TO_DOMAIN_PUBLISHED = 'DOMAIN_TO_DOMAIN_PUBLISHED' as const;
export const DOMAIN_TO_DOMAIN_PUBLISHED_ERROR =
  'DOMAIN_TO_DOMAIN_PUBLISHED_ERROR' as const;
export const DROP_PARENT_NODE_UPDATED = 'DROP_PARENT_NODE_UPDATED' as const;
export const SAMPLE_ITEMS_GENERATION_FAILED =
  'SAMPLE_ITEMS_GENERATION_FAILED' as const;
export const SAMPLE_ITEMS_GENERATION_STARTED =
  'SAMPLE_ITEMS_GENERATION_STARTED' as const;
export const SAMPLE_ITEMS_GENERATION_SUCCEEDED =
  'SAMPLE_ITEMS_GENERATION_SUCCEEDED' as const;
export const AI_SAMPLE_ITEMS_GENERATION_CLICKED =
  'AI_SAMPLE_ITEMS_GENERATION_CLICKED' as const;
export const AI_GENERATED_RESPONSE_USER_FEEDBACK_STARTED =
  'AI_GENERATED_RESPONSE_USER_FEEDBACK_STARTED' as const;
export const AI_GENERATE_NEW_ITEM_MODAL_OPENED =
  'AI_GENERATE_NEW_ITEM_MODAL_OPENED' as const;
export const AI_GENERATE_NEW_ITEM_MODAL_CLOSED =
  'AI_GENERATE_NEW_ITEM_MODAL_CLOSED' as const;
export const SAMPLE_ITEMS_GENERATION_SUCCEEDED_REMOTELY =
  'SAMPLE_ITEMS_GENERATION_SUCCEEDED_REMOTELY' as const;
export const SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED =
  'SAMPLE_PRODUCT_ITEMS_GENERATION_SUCCEEDED' as const;
export const DYN_NODE_ITEM_COUNT_UPDATED =
  'DYN_NODE_ITEM_COUNT_UPDATED' as const;
export const DYN_ASSET_PROCESSING_TOGGLED =
  'DYN_ASSET_PROCESSING_TOGGLED' as const;
export const DYN_BINDING_CHANGED = 'DYN_BINDING_CHANGED' as const;
export const DYN_COLLECTION_DUPLICATED = 'DYN_COLLECTION_DUPLICATED' as const;
export const DYN_COLLECTION_DELETED = 'DYN_COLLECTION_DELETED' as const;
export const DYN_COLLECTION_DESELECTED = 'DYN_COLLECTION_DESELECTED' as const;
export const DYN_COLLECTION_DESELECTED_BEGIN =
  'DYN_COLLECTION_DESELECTED_BEGIN' as const;
export const DYN_COLLECTION_DESELECTED_ERROR =
  'DYN_COLLECTION_DESELECTED_ERROR' as const;
export const DYN_COLLECTION_ERROR_RESET = 'DYN_COLLECTION_ERROR_RESET' as const;
export const DYN_COLLECTION_MINTED = 'DYN_COLLECTION_MINTED' as const;
export const DYN_COLLECTION_MINTED_ERROR =
  'DYN_COLLECTION_MINTED_ERROR' as const;
export const DYN_COLLECTION_MODIFIED = 'DYN_COLLECTION_MODIFIED' as const;
export const DYN_COLLECTION_SAVED = 'DYN_COLLECTION_SAVED' as const;
export const DYN_COLLECTION_SELECTED = 'DYN_COLLECTION_SELECTED' as const;
export const DYN_COLLECTION_SELECTED_ERROR =
  'DYN_COLLECTION_SELECTED_ERROR' as const;
export const DYN_COLLECTION_SETTINGS_DISPLAYED =
  'DYN_COLLECTION_SETTINGS_DISPLAYED' as const;
export const DYN_COLLECTION_SETTINGS_DISPLAYED_ERROR =
  'DYN_COLLECTION_SETTINGS_DISPLAYED_ERROR' as const;
export const DYN_COLLECTION_SETTINGS_HIDDEN =
  'DYN_COLLECTION_SETTINGS_HIDDEN' as const;
export const DYN_COLLECTION_SETTINGS_HIDDEN_ERROR =
  'DYN_COLLECTION_SETTINGS_HIDDEN_ERROR' as const;
export const DYN_COLLECTION_SUBMITTED = 'DYN_COLLECTION_SUBMITTED' as const;

export const DYN_CONTENT_EDITOR_FIELD_EDITING_STARTED =
  'DYN_CONTENT_EDITOR_FIELD_EDITING_STARTED' as const;
export const DYN_CONTENT_EDITOR_FIELD_EDITING_ENDED =
  'DYN_CONTENT_EDITOR_FIELD_EDITING_ENDED' as const;
export const BINDING_CONTEXT_CHANGED = 'BINDING_CONTEXT_CHANGED' as const;
export const BINDING_CONTEXT_CURRENT_ITEM_UPDATED =
  'BINDING_CONTEXT_CURRENT_ITEM_UPDATED' as const;
export const BINDING_CONTEXT_ITEM_SWITCHED =
  'BINDING_CONTEXT_ITEM_SWITCHED' as const;
export const DYN_FIELD_DELETED = 'DYN_FIELD_DELETED' as const;
export const DYN_FIELD_DESELECTED = 'DYN_FIELD_DESELECTED' as const;
export const DYN_FIELD_DESELECTED_ERROR = 'DYN_FIELD_DESELECTED_ERROR' as const;
export const DYN_FIELD_ERROR_RESET = 'DYN_FIELD_ERROR_RESET' as const;
export const DYN_FIELD_HOVERED = 'DYN_FIELD_HOVERED' as const;
export const DYN_FIELD_MINTED = 'DYN_FIELD_MINTED' as const;
export const DYN_FIELD_DUPLICATED = 'DYN_FIELD_DUPLICATED' as const;
export const DYN_FIELD_MODIFIED_ON_CANVAS =
  'DYN_FIELD_MODIFIED_ON_CANVAS' as const;
export const DYN_FIELD_SAVE_ON_CANVAS_SUCCESS =
  'DYN_FIELD_SAVE_ON_CANVAS_SUCCESS' as const;
export const DYN_FIELD_SAVE_ON_CANVAS_ERROR =
  'DYN_FIELD_SAVE_ON_CANVAS_ERROR' as const;
export const DYN_FIELD_SAVE_ON_CANVAS_DISCARDED =
  'DYN_FIELD_SAVE_ON_CANVAS_DISCARDED' as const;
export const DYN_FIELD_SAVED = 'DYN_FIELD_SAVED' as const;
export const DYN_FIELD_SELECTED = 'DYN_FIELD_SELECTED' as const;
export const DYN_FIELD_SELECTED_ERROR = 'DYN_FIELD_SELECTED_ERROR' as const;
export const FEATURE_CONFIG_UPDATED = 'FEATURE_CONFIG_UPDATED' as const;
export const DYN_FIELD_SUBMITTED = 'DYN_FIELD_SUBMITTED' as const;
export const DYN_FIELD_TYPE_GRID_CONTAINER_HOVERED =
  'DYN_FIELD_TYPE_GRID_CONTAINER_HOVERED' as const;
export const DYN_FIELD_TYPE_GRID_CONTAINER_UNHOVERED =
  'DYN_FIELD_TYPE_GRID_CONTAINER_UNHOVERED' as const;
export const DYN_FIELD_UNHOVERED = 'DYN_FIELD_UNHOVERED' as const;
export const DYN_HELP_SECTION_DESELECTED =
  'DYN_HELP_SECTION_DESELECTED' as const;
export const DYN_HELP_SECTION_SELECTED = 'DYN_HELP_SECTION_SELECTED' as const;
export const DYN_INLINE_ITEM_MINTED = 'DYN_INLINE_ITEM_MINTED' as const;
export const DYN_ITEM_DELETED = 'DYN_ITEM_DELETED' as const;
export const DYN_ITEM_UNPUBLISH_STARTED = 'DYN_ITEM_UNPUBLISH_STARTED' as const;
export const DYN_ITEM_UNPUBLISH_FINISHED =
  'DYN_ITEM_UNPUBLISH_FINISHED' as const;
export const DYN_ITEM_UNPUBLISH_CANCELED =
  'DYN_ITEM_UNPUBLISH_CANCELED' as const;
export const DYN_ITEM_SAVE_CANCELED = 'DYN_ITEM_SAVE_CANCELED' as const;
export const DYN_ITEM_UNPUBLISH_FAILED = 'DYN_ITEM_UNPUBLISH_FAILED' as const;
export const DYN_ITEM_UNPUBLISHED_REMOTELY =
  'DYN_ITEM_UNPUBLISHED_REMOTELY' as const;
export const DYN_ITEM_DELETED_BEGIN = 'DYN_ITEM_DELETED_BEGIN' as const;
export const DYN_ITEM_DELETED_ERROR = 'DYN_ITEM_DELETED_ERROR' as const;
export const DYN_ITEM_DELETED_USAGE_ERROR =
  'DYN_ITEM_DELETED_USAGE_ERROR' as const;
export const DYN_ITEM_DELETED_USAGE_MODAL_CLICKED =
  'DYN_ITEM_DELETED_USAGE_MODAL_CLICKED' as const;
export const DYN_ITEM_DELETED_USAGE_MODAL_CLOSED =
  'DYN_ITEM_DELETED_USAGE_MODAL_CLOSED' as const;
export const DYN_ITEM_DESELECTED = 'DYN_ITEM_DESELECTED' as const;
export const DYN_ITEM_DESELECTED_ERROR = 'DYN_ITEM_DESELECTED_ERROR' as const;
export const DYN_ITEM_DIMENSIONS_UPDATED =
  'DYN_ITEM_DIMENSIONS_UPDATED' as const;
export const DYN_ITEM_ERROR_RESET = 'DYN_ITEM_ERROR_RESET' as const;
export const DYN_ITEM_IMAGE_UPLOAD_PROGRESSED =
  'DYN_ITEM_IMAGE_UPLOAD_PROGRESSED' as const;
export const DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED =
  'DYN_VARIANT_IMAGE_UPLOAD_PROGRESSED' as const;
export const DYN_ITEM_MINTED = 'DYN_ITEM_MINTED' as const;
export const DYN_ITEM_MINTED_ERROR = 'DYN_ITEM_MINTED_ERROR' as const;
export const DYN_ITEM_MODIFIED = 'DYN_ITEM_MODIFIED' as const;
export const DYN_ITEM_BATCH_MODIFIED = 'DYN_ITEM_BATCH_MODIFIED' as const;
export const DYN_ITEM_SAVED = 'DYN_ITEM_SAVED' as const;
export const DYN_ITEM_SAVED_AND_PUBLISHED =
  'DYN_ITEM_SAVED_AND_PUBLISHED' as const;
export const DYN_ITEM_SAVED_BEGIN = 'DYN_ITEM_SAVED_BEGIN' as const;
export const DYN_ITEM_SAVED_ERROR = 'DYN_ITEM_SAVED_ERROR' as const;
export const DYN_SELECTED_ITEM_VALIDATED =
  'DYN_SELECTED_ITEM_VALIDATED' as const;
export const DYN_ITEM_SELECTED = 'DYN_ITEM_SELECTED' as const;
export const DYN_ITEM_SELECTED_ERROR = 'DYN_ITEM_SELECTED_ERROR' as const;
export const DYN_ITEM_SUBMITTED = 'DYN_ITEM_SUBMITTED' as const;
export const DYN_ITEM_UNDRAFTED = 'DYN_ITEM_UNDRAFTED' as const;
export const DYN_ITEM_VALIDATION_DRAFT_STATUS_UPDATED =
  'DYN_ITEM_VALIDATION_DRAFT_STATUS_UPDATED' as const;
export const DYN_ITEM_VIDEO_FETCH_FAILED =
  'DYN_ITEM_VIDEO_FETCH_FAILED' as const;
export const DYN_ITEM_SCHEDULE_PICKER_OPENED =
  'DYN_ITEM_SCHEDULE_PICKER_OPENED' as const;
export const DYN_ITEM_SCHEDULE_PICKER_CLOSED =
  'DYN_ITEM_SCHEDULE_PICKER_CLOSED' as const;
export const DYN_VARIANT_MODIFIED = 'DYN_VARIANT_MODIFIED' as const;
export const DYN_LIST_UI_STATE_CHANGED = 'DYN_LIST_UI_STATE_CHANGED' as const;
export const DYN_LIST_UI_STATE_REMOVED = 'DYN_LIST_UI_STATE_REMOVED' as const;
export const DYN_LIST_GRID_LAYOUT_CHANGED =
  'DYN_LIST_GRID_LAYOUT_CHANGED' as const;
export const DYN_LIST_OFFSET_CHANGED = 'DYN_LIST_OFFSET_CHANGED' as const;
export const DYN_LIST_OFFSET_LIMIT_CHANGED =
  'DYN_LIST_OFFSET_LIMIT_CHANGED' as const;
export const DYN_LIST_LIMIT_CHANGED = 'DYN_LIST_LIMIT_CHANGED' as const;
export const DYN_LIST_QUERY_MODE_CHANGED =
  'DYN_LIST_QUERY_MODE_CHANGED' as const;
export const DYN_LIST_SETTINGS_ANALYTICS_EVENT_OCCURRED =
  'DYN_LIST_SETTINGS_ANALYTICS_EVENT' as const;
export const DYN_LIST_RENDERED_TOTAL_PAGES_UPDATED =
  'DYN_LIST_RENDERED_TOTAL_PAGES_UPDATED' as const;
export const DYN_ITEM_VIDEO_FETCH_STARTED =
  'DYN_ITEM_VIDEO_FETCH_STARTED' as const;
export const DYN_ITEM_VIDEO_FETCH_FINISHED =
  'DYN_ITEM_VIDEO_FETCH_FINISHED' as const;
export const DYN_ITEM_SAVE_AND_PUBLISH_ATTEMPTED =
  'DYN_ITEM_SAVE_AND_PUBLISH_ATTEMPTED' as const;
export const DYN_ITEM_SAVE_AND_UNPUBLISH_ATTEMPTED =
  'DYN_ITEM_SAVE_AND_UNPUBLISH_ATTEMPTED' as const;
export const DYN_ITEM_REFERENCES_FOR_SCHEDULE_MODAL_DISPLAYED =
  'DYN_ITEM_REFERENCES_FOR_SCHEDULE_MODAL_DISPLAYED' as const;
export const DYN_ITEM_SCHEDULE_SUCCEEDED =
  'DYN_ITEM_SCHEDULE_SUCCEEDED' as const;
export const DYN_ITEM_SCHEDULE_FAILED = 'DYN_ITEM_SCHEDULE_FAILED' as const;
export const DYN_ITEM_UNSCHEDULE_SUCCEEDED =
  'DYN_ITEM_UNSCHEDULE_SUCCEEDED' as const;
export const DYN_ITEM_UNSCHEDULE_FAILED = 'DYN_ITEM_UNSCHEDULE_FAILED' as const;
export const DYN_ITEM_STATUS_MENU_OPENED =
  'DYN_ITEM_STATUS_MENU_OPENED' as const;
export const DYN_PAGES_FETCHED = 'DYN_PAGES_FETCHED' as const;
export const DYN_QUERY_FILTER_FOR_CHANGED =
  'DYN_QUERY_FILTER_FOR_CHANGED' as const;
export const DYN_QUERY_PARAM_ADDED = 'DYN_QUERY_PARAM_ADDED' as const;
export const DYN_QUERY_PARAM_REMOVED = 'DYN_QUERY_PARAM_REMOVED' as const;
export const DYN_QUERY_PARAM_CHANGED = 'DYN_QUERY_PARAM_CHANGED' as const;
export const DYN_QUERY_SORT_ADDED = 'DYN_QUERY_SORT_ADDED' as const;
export const DYN_QUERY_SORT_REMOVED = 'DYN_QUERY_SORT_REMOVED' as const;
export const DYN_QUERY_SORT_CHANGED = 'DYN_QUERY_SORT_CHANGED' as const;
export const DYN_QUERY_SORT_MOVED = 'DYN_QUERY_SORT_MOVED' as const;
export const DYN_VARIANTS_PROCESSED = 'DYN_VARIANTS_PROCESSED' as const;
export const DYN_USE_PRIMARY_LOCALE_CHANGED =
  'DYN_USE_PRIMARY_LOCALE_CHANGED' as const;
export const EDITOR_PAGE_ACCESSED = 'EDITOR_PAGE_ACCESSED' as const;
export const EDITOR_PAGE_CHANGED = 'EDITOR_PAGE_CHANGED' as const;
export const EDITOR_PAGE_FETCHED = 'EDITOR_PAGE_FETCHED' as const;
export const EDIT_ELEMENT_ENTERED = 'EDIT_ELEMENT_ENTERED' as const;
export const EDITOR_PAGES_FETCHED = 'EDITOR_PAGES_FETCHED' as const;
export const EDITOR_EDIT_MODE_REQUESTED = 'EDITOR_EDIT_MODE_REQUESTED' as const;
export const EMBED_EDITOR_HIDDEN = 'EMBED_EDITOR_HIDDEN' as const;
export const EMBED_EDITOR_SHOWN = 'EMBED_EDITOR_SHOWN' as const;
export const EMBED_EDITOR_FULLSCREEN_TOGGLED =
  'EMBED_EDITOR_FULLSCREEN_TOGGLED' as const;
export const EMBED_EDITOR_LINE_WRAPPING_TOGGLED =
  'EMBED_EDITOR_LINE_WRAPPING_TOGGLED' as const;
export const EMBED_EDITOR_OPENED = 'EMBED_EDITOR_OPENED' as const;
export const EMBED_EDITOR_TEXT_CHANGED = 'EMBED_EDITOR_TEXT_CHANGED' as const;
export const EMBED_EDITOR_BINDING_DIRTY_CHANGED =
  'EMBED_EDITOR_BINDING_DIRTY_CHANGED' as const;
export const EMBED_EDITOR_BINDINGS_CHANGED =
  'EMBED_EDITOR_BINDINGS_CHANGED' as const;
export const SECTION_SELECTION_CHANGE_REQUESTED =
  'SECTION_SELECTION_CHANGE_REQUESTED' as const;
export const SECTION_COMMERCE_SELECTION_CHANGE_REQUESTED =
  'SECTION_COMMERCE_SELECTION_CHANGE_REQUESTED' as const;
export const SETTINGS_COMMERCE_SELECTION_CHANGE_REQUESTED =
  'SETTINGS_COMMERCE_SELECTION_CHANGE_REQUESTED' as const;
export const EMBEDLY_ERROR_RECEIVED = 'EMBEDLY_ERROR_RECEIVED' as const;
export const ENQUEUE_RESTORE_VERSION = 'ENQUEUE_RESTORE_VERSION' as const;
export const ENQUEUE_RESTORE_PREVIEW_VERSION =
  'ENQUEUE_RESTORE_PREVIEW_VERSION' as const;
export const EXCESS_REQUEST_ATTEMPTED = 'EXCESS_REQUEST_ATTEMPTED' as const;
export const ENTITLEMENTS_FETCH = 'ENTITLEMENTS_FETCH' as const;
export const ENTITLEMENTS_FETCH_SUCCEEDED =
  'ENTITLEMENTS_FETCH_SUCCEEDED' as const;
export const ENTITLEMENTS_FETCH_FAILED = 'ENTITLEMENTS_FETCH_FAILED' as const;
export const WORKSPACE_DIMENSIONS_UPDATED_BY_EXPERIMENTS =
  'WORKSPACE_DIMENSIONS_UPDATED_BY_EXPERIMENTS' as const;
export const EXPORT_DATA_RECEIVED = 'EXPORT_DATA_RECEIVED' as const;
export const EXPORT_QUEUE_DATA_RECEIVED = 'EXPORT_QUEUE_DATA_RECEIVED' as const;
export const EXPORT_QUEUE_TASK_STATUS_RECEIVED =
  'EXPORT_QUEUE_TASK_STATUS_RECEIVED' as const;
export const EXPORT_QUEUE_TASK_STATUS_FAILED =
  'EXPORT_QUEUE_TASK_STATUS_FAILED' as const;
export const EXPORT_QUEUE_DATA_FAILED = 'EXPORT_QUEUE_DATA_FAILED' as const;
export const EXPORT_MODAL_DISMISSED = 'EXPORT_MODAL_DISMISSED' as const;
export const EXPORT_MODAL_TAB_CLICKED = 'EXPORT_MODAL_TAB_CLICKED' as const;
export const EXPORT_MODAL_MASTERSYNC_DONE =
  'EXPORT_MODAL_MASTERSYNC_DONE' as const;
export const EXPORT_MODAL_OPENED = 'EXPORT_MODAL_OPENED' as const;
export const EXPORT_MODAL_OPTIONS_KEYBOARD_SHORTCUT_PRESSED =
  'EXPORT_MODAL_OPTIONS_KEYBOARD_SHORTCUT_PRESSED' as const;
export const EXPORT_MODAL_OPTIONS_BEAUTIFY_CLICKED =
  'EXPORT_MODAL_OPTIONS_BEAUTIFY_CLICKED' as const;
export const EXPORT_MODAL_ZIP_ERRORED = 'EXPORT_MODAL_ZIP_ERRORED' as const;
export const EXPORT_MODAL_ZIP_FAILED = 'EXPORT_MODAL_ZIP_FAILED' as const;
export const EXPORT_MODAL_ZIP_GENERATED = 'EXPORT_MODAL_ZIP_GENERATED' as const;
export const EXPORT_MODAL_ZIP_PREPARE_REQUESTED =
  'EXPORT_MODAL_ZIP_PREPARE_REQUESTED' as const;
export const FILE_DRAG_CANCELED = 'FILE_DRAG_CANCELED' as const;
export const FILE_DRAG_STARTED = 'FILE_DRAG_STARTED' as const;
export const FILE_EXTENSION_VALIDATION_FAILED =
  'FILE_EXTENSION_VALIDATION_FAILED' as const;
export const FILE_HASHING_FINISHED = 'FILE_HASHING_FINISHED' as const;
export const FILE_HASHING_FAILED = 'FILE_HASHING_FAILED' as const;
export const FILE_READ_FOR_HASHING_FINISHED =
  'FILE_READ_FOR_HASHING_FINISHED' as const;
export const FILE_READ_FOR_HASHING_FAILED =
  'FILE_READ_FOR_HASHING_FAILED' as const;
export const FILE_READ_FOR_VALIDATION_FINISHED =
  'FILE_READ_FOR_VALIDATION_FINISHED' as const;
export const FILE_READ_FOR_VALIDATION_FAILED =
  'FILE_READ_FOR_VALIDATION_FAILED' as const;
export const FINDER_ITEM_DRAG_STARTED = 'FINDER_ITEM_DRAG_STARTED' as const;
export const FINDER_ITEM_HOVERED = 'FINDER_ITEM_HOVERED' as const;
export const FINDER_ITEM_SECONDARY_ACTION_CLICKED =
  'FINDER_ITEM_SECONDARY_ACTION_CLICKED' as const;
export const FINDER_ITEM_SELECTED = 'FINDER_ITEM_SELECTED' as const;
export const FINDER_TRIGGERED = 'FINDER_TRIGGERED' as const;
export const FINDER_LOST_FOCUS = 'FINDER_LOST_FOCUS' as const;
export const FILE_READ_STARTED = 'FILE_READ_STARTED' as const;
export const FILES_SELECTED_FOR_UPLOAD = 'FILES_SELECTED_FOR_UPLOAD' as const;

export const FLEX_ONCANVAS_CONTROLS_VIEWED =
  'FLEX_ONCANVAS_CONTROLS_VIEWED' as const;
//added for PLEX-154 page branching awareness experiment
export const PAGE_BRANCHING_AWARENESS_CTA_CLICKED =
  'PAGE_BRANCHING_AWARENESS_CTA_CLICKED' as const;
//added for PLEX-154 page branching awareness experiment
export const IN_APP_MESSAGE_CTA_CLICKED = 'IN_APP_MESSAGE_CTA_CLICKED' as const;

export const VALUE_ADDED = 'VALUE_ADDED' as const;
export const VALUE_REMOVED = 'VALUE_REMOVED' as const;
export const VALUE_DUPLICATED = 'VALUE_DUPLICATED' as const;
export const VALUE_CHANGED = 'VALUE_CHANGED' as const;
export const VALUE_SELECTED = 'VALUE_SELECTED' as const;
export const VALUE_DELETE_KEY_PRESSED = 'VALUE_DELETE_KEY_PRESSED' as const;
export const VIEW_ALL_CLICKED_EVENT = 'VIEW_ALL_CLICKED_EVENT' as const;
export const FOCUS_CHANGED = 'FOCUS_CHANGED' as const;
export const FOCUS_CHANGED_ERROR = 'FOCUS_CHANGED_ERROR' as const;
export const FOCUS_SELECTION_CHANGE_REQUESTED =
  'FOCUS_SELECTION_CHANGE_REQUESTED' as const;
export const FORGOT_PASSWORD_RESPONDED = 'FORGOT_PASSWORD_RESPONDED' as const;
export const FORGOT_PASSWORD_RESPONDED_BEGIN =
  'FORGOT_PASSWORD_RESPONDED_BEGIN' as const;
export const FORGOT_PASSWORD_RESPONDED_ERROR =
  'FORGOT_PASSWORD_RESPONDED_ERROR' as const;
export const FORM_CLOSED = 'FORM_CLOSED' as const;
export const FORM_EMAIL_TARGET_UPDATE_REQUESTED =
  'FORM_EMAIL_TARGET_UPDATE_REQUESTED' as const;
export const FORM_EMAIL_TARGET_UPDATED_SUCCESS =
  'FORM_EMAIL_TARGET_UPDATED_SUCCESS' as const;
export const FORM_EMAIL_TARGET_UPDATED_ERROR =
  'FORM_EMAIL_TARGET_UPDATED_ERROR' as const;
export const FORM_FIELD_BLURRED = 'FORM_FIELD_BLURRED' as const;
export const FORM_FIELD_DYNAMIC_NOTICE_UPDATED =
  'FORM_FIELD_DYNAMIC_NOTICE_UPDATED' as const;
export const FORM_FIELD_FOCUSED = 'FORM_FIELD_FOCUSED' as const;
export const FORM_FIELD_EDITED = 'FORM_FIELD_EDITED' as const;
export const FORM_SLUG_VALIDATION_FETCHED =
  'FORM_SLUG_VALIDATION_FETCHED' as const;
export const FORMS_DATA_FETCHED = 'FORMS_DATA_FETCHED' as const;
export const FORMS_DATA_HIDDEN = 'FORMS_DATA_HIDDEN' as const;
export const FORMS_DATA_SHOWN = 'FORMS_DATA_SHOWN' as const;
export const FORMS_DATA_EXPORTED = 'FORMS_DATA_EXPORTED' as const;
export const FORMS_DATA_EXPORTED_ERROR = 'FORMS_DATA_EXPORTED_ERROR' as const;
export const FORMS_FILE_UPLOAD_URL_FETCHED =
  'FORMS_FILE_UPLOAD_URL_FETCHED' as const;
export const FORMS_TABLE_COLUMN_TOGGLED = 'FORMS_TABLE_COLUMN_TOGGLED' as const;
export const FORMS_PAGINATION_FETCH_ATTEMPTED =
  'FORMS_PAGINATION_FETCH_ATTEMPTED' as const;
export const GO_BACK_TO_BEST_RULE_CLICKED =
  'GO_BACK_TO_BEST_RULE_CLICKED' as const;
export const GRID_DATA_ROW_CHANGED = 'GRID_DATA_ROW_CHANGED' as const;
export const GRID_CELL_HOVERED = 'GRID_CELL_HOVERED' as const;
export const GRID_ITEM_AREA_CHANGE_COMMITTED =
  'GRID_ITEM_AREA_CHANGE_COMMITTED' as const;
export const GRID_ITEM_AREA_PREVIEW_CHANGED =
  'GRID_ITEM_AREA_PREVIEW_CHANGED' as const;
export const GRID_ITEM_AREA_PREVIEW_CANCELLED =
  'GRID_ITEM_AREA_PREVIEW_CANCELLED' as const;
export const GRID_ITEM_AREA_PREVIEW_STARTED =
  'GRID_ITEM_AREA_PREVIEW_STARTED' as const;
export const GRID_ITEM_FLOW_MODE_TOGGLED =
  'GRID_ITEM_FLOW_MODE_TOGGLED' as const;
export const GRID_ITEM_SPANNED = 'GRID_ITEM_SPANNED' as const;
export const GRID_ITEM_SPAN_STARTED = 'GRID_ITEM_SPAN_STARTED' as const;
export const GRID_ITEM_STYLE_RESET = 'GRID_ITEM_STYLE_RESET' as const;
export const GRID_TRACK_HOVERED = 'GRID_TRACK_HOVERED' as const;
export const GRID_AREA_HOVERED = 'GRID_AREA_HOVERED' as const;
export const GRID_TRACK_CLICKED = 'GRID_TRACK_CLICKED' as const;
export const GRID_TRACK_CONTEXT_MENU_OPENED =
  'GRID_TRACK_CONTEXT_MENU_OPENED' as const;
export const GRID_AREA_CONTEXT_MENU_OPENED =
  'GRID_AREA_CONTEXT_MENU_OPENED' as const;
export const GRID_CONTEXT_MENU_CLEARED = 'GRID_CONTEXT_MENU_CLEARED' as const;
export const GRID_CONTEXT_MORE_OPTIONS_SELECTED =
  'GRID_CONTEXT_MORE_OPTIONS_SELECTED' as const;
export const GRID_AREA_CLICKED = 'GRID_AREA_CLICKED' as const;
export const GRID_COLOR_CHANGED = 'GRID_COLOR_CHANGED' as const;
export const GRID_EDIT_MODE_TOGGLED = 'GRID_EDIT_MODE_TOGGLED' as const;
export const GRID_EDIT_PARENT_MODE_TOGGLED =
  'GRID_EDIT_PARENT_MODE_TOGGLED' as const;
export const GSAP_INTEGRATION_SETTINGS_SAVE_REQUESTED =
  'GSAP_INTEGRATION_SETTINGS_SAVE_REQUESTED' as const;
export const GSAP_INTEGRATION_SETTINGS_SAVE_SUCCEEDED =
  'GSAP_INTEGRATION_SETTINGS_SAVE_SUCCEEDED' as const;
export const GSAP_INTEGRATION_SETTINGS_SAVE_FAILED =
  'GSAP_INTEGRATION_SETTINGS_SAVE_FAILED' as const;
export const GSAP_LIBRARY_TOGGLED = 'GSAP_LIBRARY_TOGGLED' as const;
export const GSAP_PLUGIN_OR_EASE_TOGGLED =
  'GSAP_PLUGIN_OR_EASE_TOGGLED' as const;
export const HANDLE_DRAG = 'HANDLE_DRAG' as const;
export const HANDLE_SORT = 'HANDLE_SORT' as const;
export const HASHING_STARTED = 'HASHING_STARTED' as const;
export const HELP_MODAL_SEARCH = 'HELP_MODAL_SEARCH' as const;
export const HELP_MODAL_SEARCH_FAILED = 'HELP_MODAL_SEARCH_FAILED' as const;
export const HELP_MODAL_SEARCH_RECEIVED = 'HELP_MODAL_SEARCH_FETCHED' as const;
export const HELP_MODAL_OPEN = 'HELP_MODAL_OPEN' as const;
export const HELP_MODAL_CLOSE = 'HELP_MODAL_CLOSE' as const;
export const IDENTITY_CAPTURE_MODAL_OPENED =
  'IDENTITY_CAPTURE_MODAL_OPENED' as const;
export const IDENTITY_CAPTURE_MODAL_CLOSED =
  'IDENTITY_CAPTURE_MODAL_CLOSED' as const;
export const HELP_TOPIC_TOGGLED = 'HELP_TOPIC_TOGGLED' as const;
export const HIDE_RELATIVE_PARENT_OUTLINE =
  'HIDE_RELATIVE_PARENT_OUTLINE' as const;

export const HOME_PAGE_CHANGED = 'HOME_PAGE_CHANGED' as const;
export const HOMEPAGE_NAV_ATTEMPTED = 'HOMEPAGE_NAV_ATTEMPTED' as const;
export const HOVERED_NODE_ANIMATION_ENDED =
  'HOVERED_NODE_ANIMATION_ENDED' as const;
export const HOVERED_NODE_ANIMATION_STARTED =
  'HOVERED_NODE_ANIMATION_STARTED' as const;
export const HOVERED_NODE_BOUNDS_UPDATED =
  'HOVERED_NODE_BOUNDS_UPDATED' as const;
export const NODE_HOVERED = 'NODE_HOVERED' as const;
export const HOVERED_PARENT_NODE_UPDATED =
  'HOVERED_PARENT_NODE_UPDATED' as const;
export const HTML_EMBED_TEXT_SAVED = 'HTML_EMBED_TEXT_SAVED' as const;
export const EMBED_EDITOR_CONTENT_SAVED = 'EMBED_EDITOR_CONTENT_SAVED' as const;
export const EMBED_LEGACY_HTML_CLEARED_FOR_ELEMENT =
  'EMBED_LEGACY_HTML_CLEARED_FOR_ELEMENT' as const;
export const HUD_HIDE = 'HUD_HIDE' as const;
export const HUD_SHOW = 'HUD_SHOW' as const;
export const STYLE_PANEL_SPACING_UI_HOVERED =
  'STYLE_PANEL_SPACING_UI_HOVERED' as const;
export const STYLE_PANEL_SPACING_SIDE_HOVERED =
  'STYLE_PANEL_SPACING_SIDE_HOVERED' as const;
export const STYLE_PANEL_SPACING_POPOVER_CHANGED =
  'STYLE_PANEL_SPACING_POPOVER_CHANGED' as const;
export const STYLE_PANEL_SPACING_DRAG_STARTED =
  'STYLE_PANEL_SPACING_DRAG_STARTED' as const;
export const STYLE_PANEL_SPACING_DRAG_ENDED =
  'STYLE_PANEL_SPACING_DRAG_ENDED' as const;
export const STYLE_PANEL_SPACING_CLEANUP =
  'STYLE_PANEL_SPACING_CLEANUP' as const;
export const IMAGE_WIDTH_CHANGED = 'IMAGE_WIDTH_CHANGED' as const;
export const IMAGE_UPLOAD_STARTED = 'IMAGE_UPLOAD_STARTED' as const;
export const IMAGE_UPLOAD_FINISHED = 'IMAGE_UPLOAD_FINISHED' as const;
export const IMAGE_UPLOADING_PREVIEW_FAILED =
  'IMAGE_UPLOADING_PREVIEW_FAILED' as const;
export const IMAGE_ID_CHANGED = 'IMAGE_ID_CHANGED' as const;
export const IMAGE_RESIZE_DATA_CHANGED = 'IMAGE_RESIZE_DATA_CHANGED' as const;
export const IMAGE_LOADING_STARTED = 'IMAGE_LOADING_STARTED' as const;
export const IMAGE_ASSET_LOADING_FINISHED =
  'IMAGE_ASSET_LOADING_FINISHED' as const;
export const IMAGE_ASSET_LOADING_FAILED = 'IMAGE_ASSET_LOADING_FAILED' as const;
export const IMAGE_ASSET_THUMBNAIL_FINISHED =
  'IMAGE_ASSET_THUMBNAIL_FINISHED' as const;
export const IMAGE_ASSET_THUMBNAIL_FAILED =
  'IMAGE_ASSET_THUMBNAIL_FAILED' as const;
export const IMAGE_READ_FOR_VALIDATION_FINISHED =
  'IMAGE_READ_FOR_VALIDATION_FINISHED' as const;
export const IMAGE_READ_FOR_VALIDATION_FAILED =
  'IMAGE_READ_FOR_VALIDATION_FAILED' as const;
export const IMAGE_REF_SET_MAX_EXCEEDED = 'IMAGE_REF_SET_MAX_EXCEEDED' as const;
export const IMAGE_SCALING_STARTED = 'IMAGE_SCALING_STARTED' as const;
export const IMAGE_SELECTED_FOR_UPLOAD = 'IMAGE_SELECTED_FOR_UPLOAD' as const;
export const IMAGES_SELECTED_FOR_UPLOAD = 'IMAGES_SELECTED_FOR_UPLOAD' as const;
export const IMPORTED_TIMEZONE = 'IMPORTED_TIMEZONE' as const;
export const INPUT_SETTINGS_FIELD_ID_CHANGED =
  'INPUT_SETTINGS_FIELD_ID_CHANGED' as const;
export const INITIAL_STATE_COLLECTED = 'INITIAL_STATE_COLLECTED' as const;
export const INVALID_NODE_MOVE_ATTEMPTED =
  'INVALID_NODE_MOVE_ATTEMPTED' as const;
export const INVALID_PASTE_REJECTED = 'INVALID_PASTE_REJECTED' as const;
export const INVALID_PAGE_DETECTED = 'INVALID_PAGE_DETECTED' as const;
export const INVALID_PARAMETER_DEFINITION_ATTEMPTED =
  'INVALID_PARAMETER_DEFINITION_ATTEMPTED' as const;
export const ITEM_ADDED_REMOTELY = 'ITEM_ADDED_REMOTELY' as const;
export const ITEMS_BY_COLLECTION_MODAL_CANCELED =
  'ITEMS_BY_COLLECTION_MODAL_CANCELED' as const;
export const ITEM_DUPLICATED = 'ITEM_DUPLICATED' as const;
export const ITEM_DUPLICATED_ERROR = 'ITEM_DUPLICATED_ERROR' as const;
export const ITEM_REMOVED_REMOTELY = 'ITEM_REMOVED_REMOTELY' as const;
export const ITEM_TABLE_COLUMN_SORTED = 'ITEM_TABLE_COLUMN_SORTED' as const;
export const ITEM_TABLE_COLUMN_TOGGLED = 'ITEM_TABLE_COLUMN_TOGGLED' as const;
export const ITEM_UPDATED_REMOTELY = 'ITEM_UPDATED_REMOTELY' as const;
export const CMS_ITEM_LOCALIZED = 'CMS_ITEM_LOCALIZED' as const;
export const IX_ALL_SETTINGS_PANELS_CLOSED =
  'IX_ALL_SETTINGS_PANELS_CLOSED' as const;
export const IX_ALL_STEP_TRANSFORMS_REMOVED =
  'IX_ALL_STEP_TRANSFORMS_REMOVED' as const;
export const IX_CLOSED_SETTINGS_POPOVERS =
  'IX_CLOSED_SETTINGS_POPOVERS' as const;
export const IX_MACRO_ASSIGNED = 'IX_MACRO_ASSIGNED' as const;
export const IX_MACRO_CREATED = 'IX_MACRO_CREATED' as const;
export const IX_MACRO_DUPLICATED = 'IX_MACRO_DUPLICATED' as const;
export const IX_MACRO_INLINE_STYLES_APPLIED =
  'IX_MACRO_INLINE_STYLES_APPLIED' as const;
export const IX_MACRO_INLINE_STYLES_CLEARED =
  'IX_MACRO_INLINE_STYLES_CLEARED' as const;
export const IX_MACRO_NAME_CHANGED = 'IX_MACRO_NAME_CHANGED' as const;
export const IX_MACRO_NAME_ERROR = 'IX_MACRO_NAME_ERROR' as const;
export const IX_MACRO_REMOVED = 'IX_MACRO_REMOVED' as const;
export const IX_MACRO_SETTINGS_ENGAGED = 'IX_MACRO_SETTINGS_ENGAGED' as const;
export const IX_MACRO_SETTINGS_OPENED = 'IX_MACRO_SETTINGS_OPENED' as const;
export const IX_MACRO_SORT_CHANGED = 'IX_MACRO_SORT_CHANGED' as const;
export const IX_MACRO_STEP_CHANGED = 'IX_MACRO_STEP_CHANGED' as const;
export const IX_MACRO_STEP_TRANSFORMS_SORTED =
  'IX_MACRO_STEP_TRANSFORMS_SORTED' as const;
export const IX_MACRO_STYLE_CHANGED = 'IX_MACRO_STYLE_CHANGED' as const;
export const IX_MACRO_STYLE_TRANSFORM_REMOVED =
  'IX_MACRO_STYLE_TRANSFORM_REMOVED' as const;
export const IX_MACRO_STYLE_TRANSFORMS_REMOVED =
  'IX_MACRO_STYLE_TRANSFORMS_REMOVED' as const;
export const IX_MACRO_STYLE_TRANSFORMS_SORTED =
  'IX_MACRO_STYLE_TRANSFORMS_SORTED' as const;
export const IX_MACRO_STYLES_REMOVED = 'IX_MACRO_STYLES_REMOVED' as const;
export const IX_MACRO_TRANSFORM_CHANGED = 'IX_MACRO_TRANSFORM_CHANGED' as const;
export const IX_MACRO_TRANSFORM_CREATED = 'IX_MACRO_TRANSFORM_CREATED' as const;
export const IX_MACRO_TRANSFORM_MODE_CHANGED =
  'IX_MACRO_TRANSFORM_MODE_CHANGED' as const;
export const IX_MACRO_TRIGGER_CHANGED = 'IX_MACRO_TRIGGER_CHANGED' as const;
export const IX_MACROS_STATE_CHANGED = 'IX_MACROS_STATE_CHANGED' as const;
export const IX_SELECTED_NODE_CHANGED = 'IX_SELECTED_NODE_CHANGED' as const;
export const IX_SETTINGS_MODE_CHANGED = 'IX_SETTINGS_MODE_CHANGED' as const;
export const IX_SETTINGS_PANEL_CLICKED = 'IX_SETTINGS_PANEL_CLICKED' as const;
export const IX_SETTINGS_PANEL_CLOSED = 'IX_SETTINGS_PANEL_CLOSED' as const;
export const IX_SETTINGS_VISIBLE_CHANGED =
  'IX_SETTINGS_VISIBLE_CHANGED' as const;
export const IX_STEP_CREATED = 'IX_STEP_CREATED' as const;
export const IX_STEP_DUPLICATED = 'IX_STEP_DUPLICATED' as const;
export const IX_STEP_PROP_REMOVED = 'IX_STEP_PROP_REMOVED' as const;
export const IX_STEP_REMOVED = 'IX_STEP_REMOVED' as const;
export const IX_STEP_SETTINGS_OPENED = 'IX_STEP_SETTINGS_OPENED' as const;
export const IX_STEP_SORT_CHANGED = 'IX_STEP_SORT_CHANGED' as const;
export const IX_STEP_TRANSFORM_REMOVED = 'IX_STEP_TRANSFORM_REMOVED' as const;
export const IX_STEP_TRANSITION_CHANGED = 'IX_STEP_TRANSITION_CHANGED' as const;
export const IX_STEP_TRANSITION_REMOVED = 'IX_STEP_TRANSITION_REMOVED' as const;
export const IX_STYLE_SETTINGS_OPENED = 'IX_STYLE_SETTINGS_OPENED' as const;
export const IX_TRANSFORM_SETTINGS_OPENED =
  'IX_TRANSFORM_SETTINGS_OPENED' as const;
export const IX_TRANSITION_SETTINGS_OPENED =
  'IX_TRANSITION_SETTINGS_OPENED' as const;
export const IX_TRIGGER_DUPLICATED = 'IX_TRIGGER_DUPLICATED' as const;
export const IX_TRIGGER_MENU_OPENED = 'IX_TRIGGER_MENU_OPENED' as const;
export const IX_TRIGGER_PREVIEW_ENDED = 'IX_TRIGGER_PREVIEW_ENDED' as const;
export const IX_TRIGGER_PREVIEW_STARTED = 'IX_TRIGGER_PREVIEW_STARTED' as const;
export const IX_TRIGGER_REMOVED = 'IX_TRIGGER_REMOVED' as const;
export const IX_TRIGGER_SETTINGS_OPENED = 'IX_TRIGGER_SETTINGS_OPENED' as const;
export const IX_TRIGGER_TYPE_SELECTED = 'IX_TRIGGER_TYPE_SELECTED' as const;
export const IX_VERSION_CHANGED = 'IX_VERSION_CHANGED' as const;
export const SITE_INSTALLED_RESOURCES_RELOADED =
  'SITE_INSTALLED_RESOURCES_RELOADED' as const;
export const IX2_DATA_LOADED = 'IX2_DATA_LOADED' as const;
export const IX2_KEEP_ACTIVE = 'IX2_KEEP_ACTIVE' as const;
export const IX2_ACTION_ITEM_CREATED = 'IX2_ACTION_ITEM_CREATED' as const;
export const IX2_ACTION_ITEM_INSERTED = 'IX2_ACTION_ITEM_INSERTED' as const;
export const IX2_ACTION_ITEM_UPDATED = 'IX2_ACTION_ITEM_UPDATED' as const;
export const IX2_ACTION_LIST_CREATED = 'IX2_ACTION_LIST_CREATED' as const;
export const IX2_ACTION_LIST_DELETED = 'IX2_ACTION_LIST_DELETED' as const;
export const IX2_ACTION_LIST_DUPLICATED = 'IX2_ACTION_LIST_DUPLICATED' as const;
export const IX2_SELECTED_ACTION_ITEMS_DUPLICATED =
  'IX2_SELECTED_ACTION_ITEMS_DUPLICATED' as const;
export const IX2_ACTION_LIST_RENAMED = 'IX2_ACTION_LIST_RENAMED' as const;
export const IX2_RELINK_MODE_TURNED_ON = 'IX2_RELINK_MODE_TURNED_ON' as const;
export const IX2_UI_ACTION_LIST_NAME_INPUT_BLURRED =
  'IX2_UI_ACTION_LIST_NAME_INPUT_BLURRED' as const;
export const IX2_UI_ACTION_TYPE_SELECTED =
  'IX2_UI_ACTION_TYPE_SELECTED' as const;
export const IX2_UI_INTERACTION_ITEM_TRASH_ICON_CLICKED =
  'IX2_UI_INTERACTION_ITEM_TRASH_ICON_CLICKED' as const;
export const IX2_UI_ACTION_LIST_SELECTED =
  'IX2_UI_ACTION_LIST_SELECTED' as const;
export const IX2_UI_EVENT_PLAY_IN_REVERSE_TOGGLED =
  'IX2_UI_EVENT_PLAY_IN_REVERSE_TOGGLED' as const;
export const IX2_UI_EVENT_LOOP_TOGGLED = 'IX2_UI_EVENT_LOOP_TOGGLED' as const;
export const IX2_UI_QUICK_EFFECT_IN_EFFECT_CLICKED =
  'IX2_UI_QUICK_EFFECT_IN_EFFECT_CLICKED' as const;
export const IX2_UI_QUICK_EFFECT_OUT_EFFECT_CLICKED =
  'IX2_UI_QUICK_EFFECT_OUT_EFFECT_CLICKED' as const;
export const IX2_UI_QUICK_EFFECT_DELAY_CHANGED =
  'IX2_UI_QUICK_EFFECT_DELAY_CHANGED' as const;
export const IX2_UI_QUICK_EFFECT_DIRECTION_SELECTED =
  'IX2_UI_QUICK_EFFECT_DIRECTION_SELECTED' as const;
export const IX2_UI_QUICK_EFFECT_PLUGIN_REVERSE_TOGGLED =
  'IX2_UI_QUICK_EFFECT_PLUGIN_REVERSE_TOGGLED' as const;
export const IX2_UI_QUICK_EFFECT_PLUGIN_LOOP_TOGGLED =
  'IX2_UI_QUICK_EFFECT_PLUGIN_LOOP_TOGGLED' as const;
export const IX2_UI_EVENT_SCROLL_OFFSET_CHANGED =
  'IX2_UI_EVENT_SCROLL_OFFSET_CHANGED' as const;
export const IX2_UI_INTERACTION_MENU_ITEM_SELECTED =
  'IX2_UI_INTERACTION_MENU_ITEM_SELECTED' as const;
export const IX2_UI_INTERACTION_CONFIGURATON_CLOSED =
  'IX2_UI_INTERACTION_CONFIGURATON_CLOSED' as const;
export const IX2_UI_AFFECTED_ELEMENTS_CHANGED =
  'IX2_UI_AFFECTED_ELEMENTS_CHANGED' as const;
export const IX2_UI_INITIAL_STATE_CHECKBOX_CLICKED =
  'IX2_UI_INITIAL_STATE_CHECKBOX_CLICKED' as const;
export const IX2_UI_AFFECT_ACTION_ITEM_TARGET_TYPE_CHANGED =
  'IX2_UI_AFFECT_ACTION_ITEM_TARGET_TYPE_CHANGED' as const;
export const IX2_UI_AFFECT_ACTION_ITEM_TARGET_EVENT_TYPE_CHANGED =
  'IX2_UI_AFFECT_ACTION_ITEM_TARGET_EVENT_TYPE_CHANGED' as const;
export const IX2_UI_PARAMETER_STARTS_ENTERING_BUTTON_CLICKED =
  'IX2_UI_PARAMETER_STARTS_ENTERING_BUTTON_CLICKED' as const;
export const IX2_UI_PARAMETER_START_VISIBLE_BUTTON_CLICKED =
  'IX2_UI_PARAMETER_START_VISIBLE_BUTTON_CLICKED' as const;
export const IX2_UI_PARAMETER_START_ADD_OFFSET_CHECKBOX_CLICKED =
  'IX2_UI_PARAMETER_START_ADD_OFFSET_CHECKBOX_CLICKED' as const;
export const IX2_UI_PARAMETER_START_ADD_OFFSET_VALUE_CHANGED =
  'IX2_UI_PARAMETER_START_ADD_OFFSET_VALUE_CHANGED' as const;
export const IX2_UI_PARAMETER_END_EXITING_CLICKED =
  'IX2_UI_PARAMETER_END_EXITING_CLICKED' as const;
export const IX2_UI_PARAMETER_END_VISIBLE_CLICKED =
  'IX2_UI_PARAMETER_END_VISIBLE_CLICKED' as const;
export const IX2_UI_PARAMETER_X_RESTING_VALUE_CHANGED =
  'IX2_UI_PARAMETER_X_RESTING_VALUE_CHANGED' as const;
export const IX2_UI_PARAMETER_Y_RESTING_VALUE_CHANGED =
  'IX2_UI_PARAMETER_Y_RESTING_VALUE_CHANGED' as const;
export const IX2_UI_PARAMETER_SMOOTHING_VALUE_CHANGED =
  'IX2_UI_PARAMETER_SMOOTHING_VALUE_CHANGED' as const;
export const IX2_UI_PARAMETER_END_ADD_OFFSET_CHECKBOX_CLICKED =
  'IX2_UI_PARAMETER_END_ADD_OFFSET_CHECKBOX_CLICKED' as const;
export const IX2_UI_PARAMETER_END_ADD_OFFSET_VALUE_CHANGED =
  'IX2_UI_PARAMETER_END_ADD_OFFSET_VALUE_CHANGED' as const;
export const IX2_UI_ACTION_LIST_CREATE_CLICKED =
  'IX2_UI_ACTION_LIST_CREATE_CLICKED' as const;
export const IX2_UI_ACTION_LIST_RENAME_SAVED =
  'IX2_UI_ACTION_LIST_RENAME_SAVED' as const;
export const IX2_UI_ACTION_LIST_DUPLICATE_CLICKED =
  'IX2_UI_ACTION_LIST_DUPLICATE_CLICKED' as const;
export const IX2_UI_ACTION_LIST_DELETE_CLICKED =
  'IX2_UI_ACTION_LIST_DELETE_CLICKED' as const;
export const IX2_UI_ACTION_LIST_CLICKED = 'IX2_UI_ACTION_LIST_CLICKED' as const;

export const IX2_UI_INTERACTION_CLICKED = 'IX2_UI_INTERACTION_CLICKED' as const;
export const IX2_UI_APPLIES_TO_STYLEBLOCK_CHANGED =
  'IX2_UI_APPLIES_TO_STYLEBLOCK_CHANGED' as const;
export const IX2_UI_ACTION_ITEM_APPLIES_TO_STYLEBLOCK_CHANGED =
  'IX2_UI_ACTION_ITEM_APPLIES_TO_STYLEBLOCK_CHANGED' as const;
export const IX2_UI_ACTION_LIST_TIMELINE_MOUSE_LEFT_PLAYLIST_CONTROL =
  'IX2_UI_ACTION_LIST_TIMELINE_MOUSE_LEFT_PLAYLIST_CONTROL' as const;
export const IX2_UI_ACTION_LIST_TIMELINE_MOUSE_ENTER_PLAYLIST_CONTROL =
  'IX2_UI_ACTION_LIST_TIMELINE_MOUSE_ENTER_PLAYLIST_CONTROL' as const;
export const IX2_UI_ACTION_ITEM_CREATE_CLICKED =
  'IX2_UI_ACTION_ITEM_CREATE_CLICKED' as const;
export const IX2_UI_TIMELINE_DELETE_KEY_PRESSED =
  'IX2_UI_TIMELINE_DELETE_KEY_PRESSED' as const;
export const IX2_UI_ACTION_ITEM_OPACITY_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_OPACITY_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_FILTER_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_FILTER_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_FONT_VARIATION_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_FONT_VARIATION_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_MOVE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_MOVE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_SPLINE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_SPLINE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_RIVE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_RIVE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_VARIABLE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_VARIABLE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_ROTATE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_ROTATE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_SKEW_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_SKEW_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_SCALE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_SCALE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_LOOP_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_LOOP_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_LOTTIE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_LOTTIE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_START_DROPPED =
  'IX2_UI_ACTION_ITEM_START_DROPPED' as const;
export const IX2_UI_TIMELINE_NEW_ACTION_ITEM_INSERTED =
  'IX2_UI_TIMELINE_NEW_ACTION_ITEM_INSERTED' as const;
export const IX2_UI_TIMELINE_ACTION_ITEM_DELETED =
  'IX2_UI_TIMELINE_ACTION_ITEM_DELETED' as const;
export const IX2_UI_TIMELINE_ACTION_ITEM_RELINK_CLICKED =
  'IX2_UI_TIMELINE_ACTION_ITEM_RELINK_CLICKED' as const;
export const IX2_RELINK_MODE_CANCELLED = 'IX2_RELINK_MODE_CANCELLED' as const;
export const IX2_UI_TIMELINE_ACTION_ITEM_DUPLICATED =
  'IX2_UI_TIMELINE_ACTION_ITEM_DUPLICATED' as const;
export const IX2_UI_ACTION_ITEM_END_DROPPED =
  'IX2_UI_ACTION_ITEM_END_DROPPED' as const;
export const IX2_UI_ACTION_ITEM_SIZE_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_SIZE_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_HIDE_SHOW_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_HIDE_SHOW_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_BACKGROUND_COLOR_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_BACKGROUND_COLOR_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_BORDER_COLOR_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_BORDER_COLOR_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_TYPOGRAPHY_COLOR_SETTING_UPDATED =
  'IX2_UI_ACTION_ITEM_TYPOGRAPHY_COLOR_SETTING_UPDATED' as const;
export const IX2_UI_ACTION_ITEM_GROUP_DROPPED =
  'IX2_UI_ACTION_ITEM_GROUP_DROPPED' as const;
export const IX2_UI_ACTION_ITEM_GAP_DROPPED =
  'IX2_UI_ACTION_ITEM_GAP_DROPPED' as const;
export const IX2_UI_TIMELINE_ACTION_LIST_ESCAPED =
  'IX2_UI_TIMELINE_ACTION_LIST_ESCAPED' as const;
export const IX2_UI_TIMELINE_ACTION_LIST_EMPTY_SPACE_CLICKED =
  'IX2_UI_TIMELINE_ACTION_LIST_EMPTY_SPACE_CLICKED' as const;
export const IX2_UI_ACTION_ITEM_BEGAN_DRAGGING =
  'IX2_UI_ACTION_ITEM_BEGAN_DRAGGING' as const;
export const IX2_UI_ACTION_LIST_PREVIEW_BUTTON_CLICKED =
  'IX2_UI_ACTION_LIST_PREVIEW_BUTTON_CLICKED' as const;
export const __TEST_IX2_MANUAL_PLAYBACK_START_REQUESTED =
  '__TEST_IX2_MANUAL_PLAYBACK_START_REQUESTED' as const;
export const __TEST_IX2_MANUAL_PLAYBACK_STOP_REQUESTED =
  '__TEST_IX2_MANUAL_PLAYBACK_STOP_REQUESTED' as const;
export const __TEST_IX2_TEST_FRAME_RENDERED =
  '__TEST_IX2_TEST_FRAME_RENDERED' as const;
export const IX2_UI_ACTION_LIST_NEXT_BUTTON_CLICKED =
  'IX2_UI_ACTION_LIST_NEXT_BUTTON_CLICKED' as const;
export const IX2_UI_ACTION_LIST_PREVIOUS_BUTTON_CLICKED =
  'IX2_UI_ACTION_LIST_PREVIOUS_BUTTON_CLICKED' as const;
export const IX2_UI_EVENTS_TAB_PREVIEW_BUTTON_MOUSE_OUT =
  'IX2_UI_EVENTS_TAB_PREVIEW_BUTTON_MOUSE_OUT' as const;
export const IX2_UI_EVENTS_TAB_PREVIEW_BUTTON_MOUSE_OVER =
  'IX2_UI_EVENTS_TAB_PREVIEW_BUTTON_MOUSE_OVER' as const;
export const IX2_UI_ACTION_ITEM_SELECTED =
  'IX2_UI_ACTION_ITEM_SELECTED' as const;
export const IX2_UI_TIMED_ACTION_START_CHANGED =
  'IX2_UI_TIMED_ACTION_START_CHANGED' as const;
export const IX2_UI_TIMED_ACTION_EASING_CHANGED =
  'IX2_UI_TIMED_ACTION_EASING_CHANGED' as const;
export const IX2_UI_TIMED_ACTION_DELAY_CHANGED =
  'IX2_UI_TIMED_ACTION_DELAY_CHANGED' as const;
export const IX2_UI_TIMED_ACTION_DURATION_CHANGED =
  'IX2_UI_TIMED_ACTION_DURATION_CHANGED' as const;
export const IX2_UI_CONTINUOUS_ACTION_KEYFRAME_CHANGED =
  'IX2_UI_CONTINUOUS_ACTION_KEYFRAME_CHANGED' as const;
export const IX2_UI_CONTINUOUS_ACTION_EASING_CHANGED =
  'IX2_UI_CONTINUOUS_ACTION_EASING_CHANGED' as const;
export const IX2_UI_INTERACTION_MEDIA_QUERY_TOGGLED =
  'IX2_UI_INTERACTION_MEDIA_QUERY_TOGGLED' as const;
export const IX2_UI_INTERACTION_APPLY_TO_CHANGED =
  'IX2_UI_INTERACTION_APPLY_TO_CHANGED' as const;
export const IX2_UI_ACTION_ITEM_EPHEMERAL_CLEARED =
  'IX2_UI_ACTION_ITEM_EPHEMERAL_CLEARED' as const;
export const IX2_SELECTED_ACTION_ITEMS_CHANGED =
  'IX2_SELECTED_ACTION_ITEMS_CHANGED' as const;
export const IX2_SELECTED_ACTION_ITEMS_DELETED =
  'IX2_SELECTED_ACTION_ITEMS_DELETED' as const;
export const IX2_SELECTED_ACTION_ITEMS_MOVED =
  'IX2_SELECTED_ACTION_ITEMS_MOVED' as const;
export const IX2_SELECTED_ACTION_ITEM_TARGETS_CHANGED =
  'IX2_SELECTED_ACTION_ITEM_TARGETS_CHANGED' as const;
export const IX2_SELECTED_ACTION_ITEM_TARGET_TYPES_CHANGED =
  'IX2_SELECTED_ACTION_ITEM_TARGET_TYPES_CHANGED' as const;
export const IX2_ACTION_ITEM_STYLEBLOCK_CHANGED =
  'IX2_ACTION_ITEM_STYLEBLOCK_CHANGED' as const;
export const IX2_SELECTED_ACTION_ITEM_TARGET_EVENT_TYPES_CHANGED =
  'IX2_SELECTED_ACTION_ITEM_TARGET_EVENT_TYPES_CHANGED' as const;
export const IX2_INITIAL_STATE_SET = 'IX2_INITIAL_STATE_SET' as const;
export const IX2_INTERACTION_CREATED = 'IX2_INTERACTION_CREATED' as const;
export const IX2_INTERACTION_DUPLICATED = 'IX2_INTERACTION_DUPLICATED' as const;
export const IX2_TARGETS_REPLACED = 'IX2_TARGETS_REPLACED' as const;
export const IX2_SYMBOLS_UNLINKED = 'IX2_SYMBOLS_UNLINKED' as const;
export const IX2_PAGE_DUPLICATED = 'IX2_PAGE_DUPLICATED' as const;
export const IX2_PAGE_DELETED = 'IX2_PAGE_DELETED' as const;
export const IX2_ORPHANED_STYLE_BLOCK_TARGETS_CLEANED =
  'IX2_ORPHANED_STYLE_BLOCK_TARGETS_CLEANED' as const;
export const IX2_EVENT_UPDATED = 'IX2_EVENT_UPDATED' as const;
export const IX2_INTERACTION_UPDATED = 'IX2_INTERACTION_UPDATED' as const;
export const IX2_INTERACTION_DELETED = 'IX2_INTERACTION_DELETED' as const;
export const IX2_EVENT_AFFECTED_ELEMENTS_CHANGED =
  'IX2_EVENT_AFFECTED_ELEMENTS_CHANGED' as const;
export const IX2_SELECTED_ACTION_LIST_ID_CHANGED =
  'IX2_SELECTED_ACTION_LIST_ID_CHANGED' as const;
export const IX2_SELECTED_INTERACTION_ID_CHANGED =
  'IX2_SELECTED_INTERACTION_ID_CHANGED' as const;

export const IX2_PROXY_DISPATCHED = 'IX2_PROXY_DISPATCHED' as const;
export const IX2_ENGINE_CLEAR_REQUESTED = 'IX2_ENGINE_CLEAR_REQUESTED' as const;
export const IX2_ENGINE_STORE_CHANGED = 'IX2_ENGINE_STORE_CHANGED' as const;
export const IX2_PLAY_ANIMATION_REQUESTED =
  'IX2_PLAY_ANIMATION_REQUESTED' as const;
export const KEYBOARD_SHORTCUTS_DISABLED =
  'KEYBOARD_SHORTCUTS_DISABLED' as const;
export const KEYBOARD_SHORTCUTS_ENABLED = 'KEYBOARD_SHORTCUTS_ENABLED' as const;
export const KEYBOARD_SHORTCUTS_VISIBILITY_CHANGED =
  'KEYBOARD_SHORTCUTS_VISIBILITY_CHANGED' as const;
export const THEME_PREFERENCES_MODAL_VISIBILITY_CHANGED =
  'THEME_PREFERENCES_MODAL_VISIBILITY_CHANGED' as const;
export const CREATE_BRANCH_MODAL_VISIBILITY_CHANGED =
  'CREATE_BRANCH_MODAL_VISIBILITY_CHANGED' as const;
export const SITE_BRANCH_MANAGER_MODAL_VISIBILITY_CHANGED =
  'SITE_BRANCH_MANAGER_MODAL_VISIBILITY_CHANGED' as const;
export const LEAVE_DESIGNER_ATTEMPTED = 'LEAVE_DESIGNER_ATTEMPTED' as const;
export const LEFT_SIDEBAR_HELP_TOGGLED = 'LEFT_SIDEBAR_HELP_TOGGLED' as const;
export const LEFT_SIDEBAR_SETTING_SELECTED =
  'LEFT_SIDEBAR_SETTING_SELECTED' as const;
export const LEFT_SIDEBAR_TIP_TOGGLED = 'LEFT_SIDEBAR_TIP_TOGGLED' as const;
export const LEFT_SIDEBAR_SCREENSHOT_TOOLTIP_TRIGGERED =
  'LEFT_SIDEBAR_SCREENSHOT_TOOLTIP_TRIGGERED' as const;
export const LIBRARY_EXPORTS_SAVE_FAILED =
  'LIBRARY_EXPORTS_SAVE_FAILED' as const;
export const LIBRARY_EXPORTS_SAVE_SUCCEEDED =
  'LIBRARY_EXPORTS_SAVE_SUCCEEDED' as const;
export const LIBRARIES_PANEL_FTUX_DISPLAYED =
  'LIBRARIES_PANEL_FTUX_DISPLAYED' as const;
export const LIBRARIES_PANEL_FTUX_CLICKED =
  'LIBRARIES_PANEL_FTUX_CLICKED' as const;
export const LINK_CLICKED = 'LINK_CLICKED' as const;
export const LINK_SETTINGS_ATTACHMENT_CHANGED =
  'LINK_SETTINGS_ATTACHMENT_CHANGED' as const;
export const LINK_SETTINGS_ATTACHMENT_DROPPED =
  'LINK_SETTINGS_ATTACHMENT_DROPPED' as const;
export const LINK_SETTINGS_OVERLAY_TARGET_CREATE_REQUESTED =
  'LINK_SETTINGS_OVERLAY_TARGET_CREATE_REQUESTED' as const;
export const LINKED_RULE_CLICKED = 'LINKED_RULE_CLICKED' as const;
export const LOCAL_DATA_IMPORTED = 'LOCAL_DATA_IMPORTED' as const;
export const LOCAL_LIBRARY_RECEIVED = 'LOCAL_LIBRARY_RECEIVED' as const;
export const LOG_FLUX_ACTIONS_TOGGLED = 'LOG_FLUX_ACTIONS_TOGGLED' as const;
export const LOG_TELEMETRY_TOGGLED = 'LOG_TELEMETRY_TOGGLED' as const;
export const LOGIN_CONFIRMED = 'LOGIN_CONFIRMED' as const;
export const LOGIN_RESPONDED = 'LOGIN_RESPONDED' as const;
export const LOGIN_RESPONDED_BEGIN = 'LOGIN_RESPONDED_BEGIN' as const;
export const LOGIN_RESPONDED_ERROR = 'LOGIN_RESPONDED_ERROR' as const;
export const LOGOUT_RESPONDED = 'LOGOUT_RESPONDED' as const;
export const MAIL_PREVIEW_SENT = 'MAIL_PREVIEW_SENT' as const;
export const MAIL_PREVIEW_SENT_ERROR = 'MAIL_PREVIEW_SENT_ERROR' as const;
export const MAIL_PREVIEW_SENT_SUCCEEDED =
  'MAIL_PREVIEW_SENT_SUCCEEDED' as const;
export const MAIL_SETTING_MODIFIED = 'MAIL_SETTING_MODIFIED' as const;
export const MAIL_SETTINGS_CANCELED = 'MAIL_SETTINGS_CANCELED' as const;
export const MAIL_SETTINGS_LOCAL_CHANGES_ABANDONED =
  'MAIL_SETTINGS_LOCAL_CHANGES_ABANDONED' as const;
export const MAIL_SETTINGS_SAVED = 'MAIL_SETTINGS_SAVED' as const;
export const MAIL_SETTINGS_SAVED_ERROR = 'MAIL_SETTINGS_SAVED_ERROR' as const;
export const MAIL_SETTINGS_SAVED_SUCCEEDED =
  'MAIL_SETTINGS_SAVED_SUCCEEDED' as const;
export const MAIL_SETTINGS_SECTION_CLICKED =
  'MAIL_SETTINGS_SECTION_CLICKED' as const;
export const MAIL_SETTINGS_LOGO_LOADING_SUCCESS =
  'MAIL_SETTINGS_LOGO_LOADING_SUCCESS' as const;
export const MAIL_SETTINGS_LOGO_LOADING_PROGRESS =
  'MAIL_SETTINGS_LOGO_LOADING_PROGRESS' as const;
export const MAIL_SETTINGS_LOGO_LOADING_PREVIEW =
  'MAIL_SETTINGS_LOGO_LOADING_PREVIEW' as const;
export const MAIL_SETTINGS_LOGO_LOADING_FAILED =
  'MAIL_SETTINGS_LOGO_LOADING_FAILED' as const;
export const MAIL_SETTINGS_TEST_EMAIL_CANCELED =
  'MAIL_SETTINGS_TEST_EMAIL_CANCELED' as const;
export const MAIL_SETTINGS_TEST_EMAIL_CHANGED =
  'MAIL_SETTINGS_TEST_EMAIL_CHANGED' as const;
export const MAIL_SETTINGS_TEST_EMAIL_OPEN_CLICKED =
  'MAIL_SETTINGS_TEST_EMAIL_OPEN_CLICKED' as const;
export const MAIL_SETTINGS_FETCH_SUCCEEDED =
  'MAIL_SETTINGS_FETCH_SUCCEEDED' as const;
export const MAIL_SETTINGS_FETCH_FAILED = 'MAIL_SETTINGS_FETCH_FAILED' as const;
export const MANUAL_SEARCH_INDEXING_TRIGGERED =
  'MANUAL_SEARCH_INDEXING_TRIGGERED' as const;
export const MASTER_SYNC_STARTED = 'MASTER_SYNC_STARTED' as const;
export const SERVER_SYNC_STARTED_REMOTELY =
  'SERVER_SYNC_STARTED_REMOTELY' as const;
export const EXISTING_BREAKPOINT_SELECTED =
  'EXISTING_BREAKPOINT_SELECTED' as const;
export const SELECTED_RULES_CHANGED = 'SELECTED_RULES_CHANGED' as const;
export const MENU_CLOSED = 'MENU_CLOSED' as const;
export const MENU_OPENED = 'MENU_OPENED' as const;
export const MENU_TAB_CLOSED = 'MENU_TAB_CLOSED' as const;
export const MESSAGE_TIMEOUT_ENDED = 'MESSAGE_TIMEOUT_ENDED' as const;
export const MINI_SETTINGS_ALL_HIDDEN = 'MINI_SETTINGS_ALL_HIDDEN' as const;
export const MINI_SETTINGS_SHOWN = 'MINI_SETTINGS_SHOWN' as const;
export const MODIFIER_KEY_DOWN = 'MODIFIER_KEY_DOWN' as const;
export const MODIFIER_KEY_UP = 'MODIFIER_KEY_UP' as const;
export const MODIFIER_KEYS_CHANGED = 'MODIFIER_KEYS_CHANGED' as const;
export const MORE_ACTIONS_CLICKED_EVENT = 'MORE_ACTIONS_CLICKED_EVENT' as const;
export const NATIVE_LINK_CLICKED = 'NATIVE_LINK_CLICKED' as const;
export const NAVBAR_COLLAPSE_CHANGED = 'NAVBAR_COLLAPSE_CHANGED' as const;
export const NAVBAR_MENU_OPEN_TOGGLED = 'NAVBAR_MENU_OPEN_TOGGLED' as const;
export const NAVIGATOR_NODE_TOGGLED = 'NAVIGATOR_NODE_TOGGLED' as const;
export const NAVIGATOR_NODES_COLLAPSED = 'NAVIGATOR_NODES_COLLAPSED' as const;
export const NAVIGATOR_NODES_EXPANDED = 'NAVIGATOR_NODES_EXPANDED' as const;
export const NAVIGATOR_NODES_EXPAND_TOGGLED =
  'NAVIGATOR_NODES_EXPAND_TOGGLED' as const;
export const NAVIGATOR_SELECTED_SUBTREE_EXPAND_TOGGLED =
  'NAVIGATOR_SELECTED_SUBTREE_EXPAND_TOGGLED' as const;
export const NAVIGATOR_NODE_SET_EXPANDED_NODES =
  'NAVIGATOR_NODE_SET_EXPANDED_NODES' as const;
export const NAVIGATOR_POTENTIAL_PARENT_HOVERED =
  'NAVIGATOR_POTENTIAL_PARENT_HOVERED' as const;
export const ELEMENT_RENAMED = 'ELEMENT_RENAMED' as const;
export const ELEMENT_NAME_RESET = 'ELEMENT_NAME_RESET' as const;
export const NAVIGATOR_SCROLLED = 'NAVIGATOR_SCROLLED' as const;
export const NODE_DRAG_CANCELLED = 'NODE_DRAG_CANCELLED' as const;
export const NODE_DRAG_STARTED = 'NODE_DRAG_STARTED' as const;
export const NODE_UNSELECTED = 'NODE_UNSELECTED' as const;
export const NODE_TEXT_EDITING_ENDED = 'NODE_TEXT_EDITING_ENDED' as const;
export const NODE_TEXT_EDITING_STARTED = 'NODE_TEXT_EDITING_STARTED' as const;
export const NOTE_DISMISSED = 'NOTE_DISMISSED' as const;
export const NOTIFICATION_ADDED = 'NOTIFICATION_ADDED' as const;
export const NOTIFICATION_DISMISSED = 'NOTIFICATION_DISMISSED' as const;
export const NOTIFICATIONS_EXPIRED = 'NOTIFICATIONS_EXPIRED' as const;
export const OPTIMIZATION_MODAL_UPDATED = 'OPTIMIZATION_MODAL_UPDATED' as const;
export const OPENED_PASSWORD_PAGE_FOR_THE_FIRST_TIME =
  'OPENED_PASSWORD_PAGE_FOR_THE_FIRST_TIME' as const;
export const ORDERS_FETCH_STARTED = 'ORDERS_FETCH_STARTED' as const;
export const ORDERS_FETCH_SUCCEEDED = 'ORDERS_FETCH_SUCCEEDED' as const;
export const ORDERS_FETCH_FAILED = 'ORDERS_FETCH_FAILED' as const;
export const ORDERS_BULK_SELECTION_TOGGLED =
  'ORDERS_BULK_SELECTION_TOGGLED' as const;
export const ORDERS_ITEM_SELECTED_TOGGLED =
  'ORDERS_ITEM_SELECTED_TOGGLED' as const;

export const ORDERS_TABLE_SORT_STARTED = 'ORDERS_TABLE_SORT_STARTED' as const;
export const ORDERS_TABLE_SORT_SUCCEEDED =
  'ORDERS_TABLE_SORT_SUCCEEDED' as const;
export const ORDERS_TABLE_SORT_FAILED = 'ORDERS_TABLE_SORT_FAILED' as const;
export const ORDERS_TABLE_PAGE_CHANGE_STARTED =
  'ORDERS_TABLE_PAGE_CHANGE_STARTED' as const;
export const ORDERS_TABLE_PAGE_CHANGE_SUCCEEDED =
  'ORDERS_TABLE_PAGE_CHANGE_SUCCEEDED' as const;
export const ORDERS_TABLE_PAGE_CHANGE_FAILED =
  'ORDERS_TABLE_PAGE_CHANGE_FAILED' as const;
export const ORDER_SUMMARY_STARTED = 'ORDER_SUMMARY_STARTED' as const;
export const ORDER_SUMMARY_SUCCEEDED = 'ORDER_SUMMARY_SUCCEEDED' as const;
export const ORDER_SUMMARY_FAILED = 'ORDER_SUMMARY_FAILED' as const;
export const ORDERS_SUMMARY_SYNC_UNFULFILLED_COUNT =
  'ORDERS_SUMMARY_SYNC_UNFULFILLED_COUNT' as const;

export const ORDERS_TABLE_STATUS_FILTER_STARTED =
  'ORDERS_TABLE_STATUS_FILTER_STARTED' as const;
export const ORDERS_TABLE_STATUS_FILTER_SUCCEEDED =
  'ORDERS_TABLE_STATUS_FILTER_SUCCEEDED' as const;
export const ORDERS_TABLE_STATUS_FILTER_FAILED =
  'ORDERS_TABLE_STATUS_FILTER_FAILED' as const;

export const ORDERS_TABLE_SEARCH_LOCAL_CHANGED =
  'ORDERS_TABLE_SEARCH_LOCAL_CHANGED' as const;

export const ORDERS_TABLE_SEARCH_STARTED =
  'ORDERS_TABLE_SEARCH_STARTED' as const;
export const ORDERS_TABLE_SEARCH_SUCCEEDED =
  'ORDERS_TABLE_SEARCH_SUCCEEDED' as const;
export const ORDERS_TABLE_SEARCH_FAILED = 'ORDERS_TABLE_SEARCH_FAILED' as const;

export const ORDER_DETAIL_CLOSED = 'ORDER_DETAIL_CLOSED' as const;
export const ORDER_DETAIL_SELECTED = 'ORDER_DETAIL_SELECTED' as const;

export const ORDERS_BULK_FULFILL_STARTED =
  'ORDERS_BULK_FULFILL_STARTED' as const;
export const ORDERS_BULK_FULFILL_SUCCEEDED =
  'ORDERS_BULK_FULFILL_SUCCEEDED' as const;
export const ORDERS_BULK_FULFILL_FAILED = 'ORDERS_BULK_FULFILL_FAILED' as const;

export const ORDERS_BULK_UNFULFILL_STARTED =
  'ORDERS_BULK_UNFULFILL_STARTED' as const;
export const ORDERS_BULK_UNFULFILL_SUCCEEDED =
  'ORDERS_BULK_UNFULFILL_SUCCEEDED' as const;
export const ORDERS_BULK_UNFULFILL_FAILED =
  'ORDERS_BULK_UNFULFILL_FAILED' as const;

export const ORDERS_BULK_REFUND_STARTED = 'ORDERS_BULK_REFUND_STARTED' as const;
export const ORDERS_BULK_REFUND_SUCCEEDED =
  'ORDERS_BULK_REFUND_SUCCEEDED' as const;
export const ORDERS_BULK_REFUND_FAILED = 'ORDERS_BULK_REFUND_FAILED' as const;

export const PUBLISHING_OPTION_CHANGE_REQUESTED =
  'PUBLISHING_OPTION_CHANGE_REQUESTED' as const;
export const PUBLISHING_OPTION_CHANGE_FAILED =
  'PUBLISHING_OPTION_CHANGE_FAILED' as const;
export const PUBLISHING_OPTION_CHANGE_SUCCEEDED =
  'PUBLISHING_OPTION_CHANGE_SUCCEEDED' as const;
export const PUBLISHING_OPTIONS_FETCHED = 'PUBLISHING_OPTIONS_FETCHED' as const;

export const CHANGE_ORDER_STATE_MODAL_OPENED =
  'CHANGE_ORDER_STATE_MODAL_OPENED' as const;
export const CHANGE_ORDER_STATE_MODAL_CLOSED =
  'CHANGE_ORDER_STATE_MODAL_CLOSED' as const;

export const SET_ORDER_FULFILL_STARTED = 'SET_ORDER_FULFILL_STARTED' as const;
export const SET_ORDER_FULFILL_SUCCEEDED =
  'SET_ORDER_FULFILL_SUCCEEDED' as const;
export const SET_ORDER_FULFILL_FAILED = 'SET_ORDER_FULFILL_FAILED' as const;

export const RESEND_ORDER_EMAIL_REQUESTED =
  'RESEND_ORDER_EMAIL_REQUESTED' as const;
export const RESEND_ORDER_EMAIL_FAILED = 'RESEND_ORDER_EMAIL_FAILED' as const;
export const RESEND_ORDER_EMAIL_SUCCEEDED =
  'RESEND_ORDER_EMAIL_SUCCEEDED' as const;

export const ORDERS_TABLE_SELECT_ALL = 'ORDERS_TABLE_SELECT_ALL' as const;

export const SET_ORDER_REFUNDED_STARTED = 'SET_ORDER_REFUNDED_STARTED' as const;
export const SET_ORDER_REFUNDED_SUCCEEDED =
  'SET_ORDER_REFUNDED_SUCCEEDED' as const;
export const SET_ORDER_REFUNDED_FAILED = 'SET_ORDER_REFUNDED_FAILED' as const;

export const SET_ORDER_UNFULFILL_STARTED =
  'SET_ORDER_UNFULFILL_STARTED' as const;
export const SET_ORDER_UNFULFILL_SUCCEEDED =
  'SET_ORDER_UNFULFILL_SUCCEEDED' as const;
export const SET_ORDER_UNFULFILL_FAILED = 'SET_ORDER_UNFULFILL_FAILED' as const;

export const ORDER_EXPORT_MODAL_OPENED = 'ORDER_EXPORT_MODAL_OPENED' as const;
export const ORDER_EXPORT_MODAL_DISMISSED =
  'ORDER_EXPORT_MODAL_DISMISSED' as const;
export const ORDER_EXPORT_OPTIONS_CHANGED =
  'ORDER_EXPORT_OPTIONS_CHANGED' as const;
export const ORDER_EXPORT_STARTED = 'ORDER_EXPORT_STARTED' as const;
export const ORDER_EXPORT_SUCCEEDED = 'ORDER_EXPORT_SUCCEEDED' as const;
export const ORDER_EXPORT_FAILED = 'ORDER_EXPORT_FAILED' as const;
export const PAGE_SELECTED_CLICKED = 'PAGE_SELECTED_CLICKED' as const;
export const PAGE_FILTER_SELECTED_CLICKED =
  'PAGE_FILTER_SELECTED_CLICKED' as const;
export const PAGE_FILTER_OPENED = 'PAGE_FILTER_OPENED' as const;
export const PAGE_SWITCHER_OPENED = 'PAGE_SWITCHER_OPENED' as const;
export const BRANCH_CREATE_STARTED = 'BRANCH_CREATE_STARTED' as const;
export const BRANCH_CREATE_SUCCEEDED = 'BRANCH_CREATE_SUCCEEDED' as const;
export const BRANCH_CREATE_FAILED = 'BRANCH_CREATE_FAILED' as const;
export const SITE_BRANCH_CREATE_STARTED = 'SITE_BRANCH_CREATE_STARTED' as const;
export const SITE_BRANCH_CREATE_SUCCEEDED =
  'SITE_BRANCH_CREATE_SUCCEEDED' as const;
export const SITE_BRANCH_CREATE_FAILED = 'SITE_BRANCH_CREATE_FAILED' as const;
export const SITE_BRANCH_SWITCH_STARTED = 'SITE_BRANCH_SWITCH_STARTED' as const;
export const SITE_BRANCH_SWITCH_FAILED = 'SITE_BRANCH_SWITCH_FAILED' as const;
export const SITE_BRANCH_ENTERED = 'SITE_BRANCH_ENTERED' as const;
export const SITE_BRANCH_EXITED = 'SITE_BRANCH_EXITED' as const;
export const SITE_BRANCH_DELETE_STARTED = 'SITE_BRANCH_DELETE_STARTED' as const;
export const SITE_BRANCH_DELETE_FAILED = 'SITE_BRANCH_DELETE_FAILED' as const;
export const SITE_BRANCH_DELETE_SUCCEEDED =
  'SITE_BRANCH_DELETE_SUCCEEDED' as const;
export const SITE_BRANCH_DELETE_REDIRECT_OPENED =
  'SITE_BRANCH_DELETE_REDIRECT_OPENED' as const;
export const SITE_BRANCH_DELETE_REDIRECT_COMPLETED =
  'SITE_BRANCH_DELETE_REDIRECT_COMPLETED' as const;
export const SITE_BRANCH_UPDATE_INITIATED =
  'SITE_BRANCH_UPDATE_INITIATED' as const;
export const SITE_BRANCH_UPDATE_AVAILABLE =
  'SITE_BRANCH_UPDATE_AVAILABLE' as const;
export const SITE_BRANCH_UPDATE_STARTED = 'SITE_BRANCH_UPDATE_STARTED' as const;
export const SITE_BRANCH_UPDATE_SUCCEEDED =
  'SITE_BRANCH_UPDATE_SUCCEEDED' as const;
export const SITE_BRANCH_UPDATE_CONFLICTED =
  'SITE_BRANCH_UPDATE_CONFLICTED' as const;
export const SITE_BRANCH_UPDATE_FAILED = 'SITE_BRANCH_UPDATE_FAILED' as const;
export const SITE_BRANCH_UPDATE_CANCELLED =
  'SITE_BRANCH_UPDATE_CANCELLED' as const;
export const SITE_BRANCH_UPDATE_SKIPPED = 'SITE_BRANCH_UPDATE_SKIPPED' as const;
export const SITE_BRANCH_UPDATE_RESOLUTIONS_SUBMITTED =
  'SITE_BRANCH_UPDATE_RESOLUTIONS_SUBMITTED' as const;
export const SITE_BRANCH_DETAILS_UPDATED =
  'SITE_BRANCH_DETAILS_UPDATED' as const;
export const SITE_BRANCH_MERGE_INITIATED =
  'SITE_BRANCH_MERGE_INITIATED' as const;
/** Update preflight clear — opens the merge confirm modal (`checking` → `preparing`). */
export const SITE_BRANCH_MERGE_CHECK_PASSED =
  'SITE_BRANCH_MERGE_CHECK_PASSED' as const;
export const SITE_BRANCH_MERGE_STARTED = 'SITE_BRANCH_MERGE_STARTED' as const;
export const SITE_BRANCH_MERGE_SUCCEEDED =
  'SITE_BRANCH_MERGE_SUCCEEDED' as const;
export const SITE_BRANCH_MERGE_CONFLICTED =
  'SITE_BRANCH_MERGE_CONFLICTED' as const;
export const SITE_BRANCH_MERGE_FAILED = 'SITE_BRANCH_MERGE_FAILED' as const;
export const SITE_BRANCH_MERGE_CANCELLED =
  'SITE_BRANCH_MERGE_CANCELLED' as const;
export const SITE_BRANCH_MERGE_RESOLUTIONS_SUBMITTED =
  'SITE_BRANCH_MERGE_RESOLUTIONS_SUBMITTED' as const;
export const SITE_BRANCH_MERGED_REMOTELY =
  'SITE_BRANCH_MERGED_REMOTELY' as const;
export const SITE_BRANCHES_FETCH_REQUESTED =
  'SITE_BRANCHES_FETCH_REQUESTED' as const;
export const SITE_BRANCHES_FETCH_SUCCEEDED =
  'SITE_BRANCHES_FETCH_SUCCEEDED' as const;
export const SITE_BRANCHES_FETCH_FAILED = 'SITE_BRANCHES_FETCH_FAILED' as const;
export const SITE_BRANCH_CHANGES_FETCH_REQUESTED =
  'SITE_BRANCH_CHANGES_FETCH_REQUESTED' as const;
export const SITE_BRANCH_CHANGES_FETCH_SUCCEEDED =
  'SITE_BRANCH_CHANGES_FETCH_SUCCEEDED' as const;
export const SITE_BRANCH_CHANGES_FETCH_FAILED =
  'SITE_BRANCH_CHANGES_FETCH_FAILED' as const;
export const SITE_BRANCH_CHANGES_MARKED_STALE =
  'SITE_BRANCH_CHANGES_MARKED_STALE' as const;
export const SITE_BRANCH_CHANGES_RECEIVED_REMOTELY =
  'SITE_BRANCH_CHANGES_RECEIVED_REMOTELY' as const;
export const PAGE_BRANCHED_REMOTELY = 'PAGE_BRANCHED_REMOTELY' as const;
export const PAGE_DELETE_BEGAN = 'PAGE_DELETE_BEGAN' as const;
export const PAGE_DELETED_SUCCESS = 'PAGE_DELETED_SUCCESS' as const;
export const PAGE_DUPLICATED = 'PAGE_DUPLICATED' as const;
export const PAGE_ERROR_RESET = 'PAGE_ERROR_RESET' as const;
export const PAGE_EXPANDED_FOLDERS_INITIATED =
  'PAGE_EXPANDED_FOLDERS_INITIATED' as const;
export const PAGE_EXTERNAL_CODE_FETCH_SUCCEEDED =
  'PAGE_EXTERNAL_CODE_FETCH_SUCCEEDED' as const;
export const PAGE_EXTERNAL_CODE_FETCH_FAILED =
  'PAGE_EXTERNAL_CODE_FETCH_FAILED' as const;
export const PAGE_EXTERNAL_CODE_DELETE_FAILED =
  'PAGE_EXTERNAL_CODE_DELETE_FAILED' as const;
export const PAGE_EXTERNAL_CODE_DELETE_SUCCEEDED =
  'PAGE_EXTERNAL_CODE_DELETE_SUCCEEDED' as const;
export const PAGE_EXTERNAL_CODE_DELETED = 'PAGE_EXTERNAL_CODE_DELETED' as const;
export const PAGE_FOLDER_COLLAPSED = 'PAGE_FOLDER_COLLAPSED' as const;
export const PAGE_FOLDER_DELETED = 'PAGE_FOLDER_DELETED' as const;
export const PAGE_FOLDER_EXPANDED = 'PAGE_FOLDER_EXPANDED' as const;
export const PAGE_FOLDER_MINTED = 'PAGE_FOLDER_MINTED' as const;
export const PAGE_FOLDER_SAVED = 'PAGE_FOLDER_SAVED' as const;
export const PAGE_FOLDER_SUBMITTED = 'PAGE_FOLDER_SUBMITTED' as const;
export const PAGE_MINTED = 'PAGE_MINTED' as const;
export const PAGE_NODE_DYN_BINDING_REMOVED =
  'PAGE_NODE_DYN_BINDING_REMOVED' as const;
export const PAGE_SAVED = 'PAGE_SAVED' as const;
export const PAGE_SAVED_REMOTELY = 'PAGE_SAVED_REMOTELY' as const;
export const PAGE_SETTINGS_CLOSED = 'PAGE_SETTINGS_CLOSED' as const;
export const PAGE_SETTINGS_MODIFIED = 'PAGE_SETTINGS_MODIFIED' as const;
export const PAGE_SETTINGS_DIFF_CHANGED = 'PAGE_SETTINGS_DIFF_CHANGED' as const;
export const PAGE_SETTINGS_OPENED = 'PAGE_SETTINGS_OPENED' as const;
export const PAGE_SETTINGS_GENERATION_SUCCEEDED =
  'PAGE_SETTINGS_GENERATION_SUCCEEDED' as const;
export const PAGE_SUBMITTED = 'PAGE_SUBMITTED' as const;

/**
 * Triggers an action when client receives data from the server after
 * a page switch, but before this data is processed and merged into Flux.
 */
export const PAGE_SWITCHED = 'PAGE_SWITCHED' as const;

/** Carries the target page id, so a superseded switch can't claim it. */
export const PAGE_SWITCH_USER_INITIATED = 'PAGE_SWITCH_USER_INITIATED' as const;

/** Holds the page-load overlay while a translation is expected. */
export const AUTO_TRANSLATE_ON_OPEN_ANTICIPATED =
  'AUTO_TRANSLATE_ON_OPEN_ANTICIPATED' as const;

export const AUTO_TRANSLATE_ON_OPEN_SETTLED =
  'AUTO_TRANSLATE_ON_OPEN_SETTLED' as const;

/** Clears the target, so a later automatic switch can't inherit it. */
export const PAGE_SWITCH_INTENT_CONSUMED =
  'PAGE_SWITCH_INTENT_CONSUMED' as const;

/**
 * Triggers an action when page switch is finished, which means all
 * data from the server has been processed and merged into Flux.
 */
export const PAGE_SWITCHED_FINISHED = 'PAGE_SWITCHED_FINISHED' as const;

/**
 * Dispatched from `Canvas`'s `componentDidUpdate` after a React commit in
 * which `currentPageId` changed — i.e. the canvas DOM reflects the
 * switched page. Unlike `PAGE_SWITCHED_FINISHED` (data merged into Flux,
 * fired before React renders), this is a render-completion signal.
 */
export const PAGE_SWITCH_RENDERED = 'PAGE_SWITCH_RENDERED' as const;

/**
 * Triggers an action when navigating back from component editing
 * (context editing) to a regular page. This fires after the page
 * switch is complete.
 */
export const NAVIGATED_BACK_FROM_COMPONENT_EDIT =
  'NAVIGATED_BACK_FROM_COMPONENT_EDIT' as const;

export const PAGE_SWITCHED_BEGIN = 'PAGE_SWITCHED_BEGIN' as const;
export const PAGE_SWITCHED_ERROR = 'PAGE_SWITCHED_ERROR' as const;
export const PAGE_SYNCED = 'PAGE_SYNCED' as const;
export const PAGE_SYNCED_BEGIN = 'PAGE_SYNCED_BEGIN' as const;
export const PAGE_SYNCED_ERROR = 'PAGE_SYNCED_ERROR' as const;
export const PAGE_AUTOMATIC_REDIRECT_CHANGED =
  'PAGE_AUTOMATIC_REDIRECT_CHANGED' as const;
export const PAGE_AUTOMATIC_REDIRECT_REQUESTED =
  'PAGE_AUTOMATIC_REDIRECTION_REQUESTED' as const;
export const PAGE_AUTOMATIC_REDIRECT_SUCCEEDED =
  'PAGE_AUTOMATIC_REDIRECTION_SUCCEEDED' as const;
export const PAGE_AUTOMATIC_REDIRECT_FAILED =
  'PAGE_AUTOMATIC_REDIRECTION_FAILED' as const;
export const PAGE_DRAFT_AUTOMATIC_REDIRECT_REQUESTED =
  'PAGE_DRAFT_AUTOMATIC_REDIRECT_REQUESTED' as const;
export const PAGE_BRANCH_WARNING_DISMISSED =
  'PAGE_BRANCH_WARNING_DISMISSED' as const;
export const PAGE_BRANCH_WARNING_OPENED = 'PAGE_BRANCH_WARNING_OPENED' as const;
export const PAGE_SWITCH_BLOCKED_ON_LOCKED_BRANCH =
  'PAGE_SWITCH_BLOCKED_ON_LOCKED_BRANCH' as const;
export const PAGINATION_LIMIT_UPDATED = 'PAGINATION_LIMIT_UPDATED' as const;
export const PAGINATION_ENABLED = 'PAGINATION_ENABLED' as const;
export const PAGINATION_DISABLED = 'PAGINATION_DISABLED' as const;
export const PAGINATION_COUNT_SHOWN = 'PAGINATION_COUNT_SHOWN' as const;
export const PAGINATION_COUNT_HIDDEN = 'PAGINATION_COUNT_HIDDEN' as const;
export const PAGINATION_PAGE_SWITCHED = 'PAGINATION_PAGE_SWITCHED' as const;
export const PANEL_REF_UPDATED = 'PANEL_REF_UPDATED' as const;
export const NAVIGATOR_CONTAINER_HEIGHT_UPDATED =
  'NAVIGATOR_CONTAINER_HEIGHT_UPDATED' as const;
export const PASTE = 'PASTE' as const;
export const PLUGINS_INITIALIZED = 'PLUGINS_INITIALIZED' as const;
export const NEW_BREAKPOINT_SELECTED = 'NEW_BREAKPOINT_SELECTED' as const;
export const PREVIEW_URL_COPIED_TO_CLIPBOARD =
  'PREVIEW_URL_COPIED_TO_CLIPBOARD' as const;
export const PRODUCTS_LIST_CHANGED = 'PRODUCTS_LIST_CHANGED' as const;
export const PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED =
  'PRODUCT_ITEM_INVENTORIES_FETCH_SUCCEEDED' as const;
export const PRODUCT_ITEM_INVENTORIES_FETCH_FAILED =
  'PRODUCT_ITEM_INVENTORIES_FETCH_FAILED' as const;
export const PRODUCT_ITEM_INVENTORY_SAVE_FAILED =
  'PRODUCT_ITEM_INVENTORY_SAVE_FAILED' as const;
export const PRODUCT_ITEM_INVENTORY_SAVE_SUCCEEDED =
  'PRODUCT_ITEM_INVENTORY_SAVE_SUCCEEDED' as const;
export const PRODUCT_ITEM_META_MODIFIED = 'PRODUCT_ITEM_META_MODIFIED' as const;
export const PRODUCT_OPTION_SET_UNMOUNTED =
  'PRODUCT_OPTION_SET_UNMOUNTED' as const;
export const PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED =
  'PRODUCT_OPTION_SET_WITH_DEFAULT_OPTION_CREATED' as const;
export const PRODUCT_OPTION_SET_OPTION_ADDED =
  'PRODUCT_OPTION_SET_OPTION_ADDED' as const;
export const PRODUCT_OPTION_SET_REMOVED = 'PRODUCT_OPTION_SET_REMOVED' as const;
export const PRODUCT_OPTION_SET_OPTION_REMOVED =
  'PRODUCT_OPTION_SET_OPTION_REMOVED' as const;
export const PSEUDO_MODE_SELECTED = 'PSEUDO_MODE_SELECTED' as const;
export const VARIANT_STATE_SELECTED = 'VARIANT_STATE_SELECTED' as const;
export const PUBLISH_BLURRED = 'PUBLISH_BLURRED' as const;
export const PUBLISH_DROPDOWN_OPENED = 'PUBLISH_DROPDOWN_OPENED' as const;
export const PUBLISH_DROPDOWN_TOGGLED = 'PUBLISH_DROPDOWN_TOGGLED' as const;
export const PUBLISH_FOCUSED = 'PUBLISH_FOCUSED' as const;
export const PUBLISHED_DOMAIN_TOGGLED = 'PUBLISHED_DOMAIN_TOGGLED' as const;
export const PUBLISH_SCHEDULED_ITEMS_DISPLAYED =
  'PUBLISH_SCHEDULED_ITEMS_DISPLAYED' as const;
export const RAW_ASSETS_DATA_RECEIVED = 'RAW_ASSETS_DATA_RECEIVED' as const;
export const RAW_COLLECTION_RECEIVED = 'RAW_COLLECTION_RECEIVED' as const;
export const SCHEDULED_ITEMS_RECEIVED = 'SCHEDULED_ITEMS_RECEIVED' as const;
export const RAW_DOMAIN_DATA_RECEIVED = 'RAW_DOMAIN_DATA_RECEIVED' as const;
export const RAW_ITEMS_RECEIVED = 'RAW_ITEMS_RECEIVED' as const;
export const RAW_IX_DATA_IMPORTED = 'RAW_IX_DATA_IMPORTED' as const;

/**
 * Triggers an action when client has processed data from the server after
 * loading the designer initially, and merged it into Flux.
 */
export const RAW_SITE_DATA_PROCESSED = 'RAW_SITE_DATA_PROCESSED' as const;

/**
 * Triggers an action when client receives data from the server after
 * loading the designer initially, but before this data is processed
 * and merged into Flux.
 */
export const RAW_SITE_DATA_RECEIVED = 'RAW_SITE_DATA_RECEIVED' as const;
export const SHARE_SITE_DROPDOWN_OPENED = 'SHARE_SITE_DROPDOWN_OPENED' as const;
export const READ_ONLY_MODE_INITIALIZED = 'READ_ONLY_MODE_INITIALIZED' as const;
export const ANONYMOUS_COMMENT_INITIALIZED =
  'ANONYMOUS_COMMENT_INITIALIZED' as const;
export const REALTIME_CONNECTED = 'REALTIME_CONNECTED' as const;
export const REALTIME_DISCONNECTED = 'REALTIME_DISCONNECTED' as const;
export const REALTIME_LIVESYNCING_DISABLED =
  'REALTIME_LIVESYNCING_DISABLED' as const;
export const REALTIME_LIVESYNCING_ENABLED =
  'REALTIME_LIVESYNCING_ENABLED' as const;
export const REALTIME_ONBOARDING_VIEWED = 'REALTIME_ONBOARDING_VIEWED' as const;
export const REDO = 'REDO' as const;
export const REMOTE_MESSAGES_SENT = 'REMOTE_MESSAGES_SENT' as const;
export const REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED =
  'REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED' as const;
export const REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_SUCCEEDED =
  'REMOVE_OPTIMIZATION_WRAPPERS_ENQUEUE_SUCCEEDED' as const;
export const REMOVE_OPTIMIZATION_WRAPPERS_SUCCEEDED =
  'REMOVE_OPTIMIZATION_WRAPPERS_SUCCEEDED' as const;
export const REMOVE_OPTIMIZATION_WRAPPERS_FAILED =
  'REMOVE_OPTIMIZATION_WRAPPERS_FAILED' as const;
export const APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED =
  'APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_STARTED' as const;
export const APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_SUCCEEDED =
  'APPLY_OPTIMIZATION_WRAPPERS_ENQUEUE_SUCCEEDED' as const;
export const APPLY_OPTIMIZATION_WRAPPERS_SUCCEEDED =
  'APPLY_OPTIMIZATION_WRAPPERS_SUCCEEDED' as const;
export const APPLY_OPTIMIZATION_WRAPPERS_FAILED =
  'APPLY_OPTIMIZATION_WRAPPERS_FAILED' as const;
export const RENAME_VERSION_ATTEMPTED = 'RENAME_VERSION_ATTEMPTED' as const;
export const RENAME_VERSION_FAILED = 'RENAME_VERSION_FAILED' as const;
export const RENAME_VERSION_MODAL_TRIGGERED =
  'RENAME_VERSION_MODAL_TRIGGERED' as const;
export const RESTORE_VERSION_ATTEMPTED_ASYNC =
  'RESTORE_VERSION_ATTEMPTED_ASYNC' as const;
export const RESTORE_VERSION_FAILED = 'RESTORE_VERSION_FAILED' as const;
export const RESTORE_VERSION_SUCCESS = 'RESTORE_VERSION_SUCCESS' as const;
export const RESTORE_PREVIEW_VERSION_SUCCESS =
  'RESTORE_PREVIEW_VERSION_SUCCESS' as const;
export const RESTORE_PREVIEW_VERSION_ATTEMPTED_ASYNC =
  'RESTORE_PREVIEW_VERSION_ATTEMPTED_ASYNC' as const;

export const REMOTE_CLIENT_CHANGED = 'REMOTE_CLIENT_CHANGED' as const;

export const REMOTE_CLIENT_LEFT = 'REMOTE_CLIENT_LEFT' as const;
export const REQUEST_FAILED = 'REQUEST_FAILED' as const;
export const REQUEST_FINISHED = 'REQUEST_FINISHED' as const;
export const REQUEST_STARTED = 'REQUEST_STARTED' as const;
export const REQUEST_UPLOAD_PROGRESSED = 'REQUEST_UPLOAD_PROGRESSED' as const;
export const RESET_PASSWORD_RESPONDED = 'RESET_PASSWORD_RESPONDED' as const;
export const RESET_PASSWORD_RESPONDED_BEGIN =
  'RESET_PASSWORD_RESPONDED_BEGIN' as const;
export const RESET_PASSWORD_RESPONDED_ERROR =
  'RESET_PASSWORD_RESPONDED_ERROR' as const;
export const RESPONSIVE_IFRAME_STYLE_SYNCED =
  'RESPONSIVE_IFRAME_STYLE_SYNCED' as const;
export const RESPONSIVE_IMAGE_MIGRATION_OVERLAY_VISIBILITY_CHANGED =
  'RESPONSIVE_IMAGE_MIGRATION_OVERLAY_VISIBILITY_CHANGED' as const;
export const RESPONSIVE_IMAGE_SRCSET_OPTIONS_TOGGLED =
  'RESPONSIVE_IMAGE_SRCSET_OPTIONS_TOGGLED' as const;
export const RESPONSIVE_IMAGES_MEASURED = 'RESPONSIVE_IMAGES_MEASURED' as const;
/**
 * Auto-generated actions, not user edits. The multiplayer server records no
 * site activity for them, so a release has no change row to show for them.
 */
export const SITE_CHANGE_IGNORED_ACTION_TYPES: ReadonlySet<string> = new Set([
  RESPONSIVE_IMAGES_MEASURED,
]);
export const RESPONSIVE_MIGRATION_COMPLETED =
  'RESPONSIVE_MIGRATION_COMPLETED' as const;
export const RESPONSIVE_MIGRATION_STATUS_UPDATED =
  'RESPONSIVE_MIGRATION_STATUS_UPDATED' as const;
export const RESPONSIVE_MIGRATION_TOGGLED =
  'RESPONSIVE_MIGRATION_TOGGLED' as const;
export const RESPONSIVE_MIGRATION_WAITING_FOR_DYN_ITEMS =
  'RESPONSIVE_MIGRATION_WAITING_FOR_DYN_ITEMS' as const;
export const ROOT_SYMBOL_NODE_ADDED = 'ROOT_SYMBOL_NODE_ADDED' as const;
export const CREATE_COMPONENT_ACROSS_LOCALES_STARTED =
  'CREATE_COMPONENT_ACROSS_LOCALES_STARTED' as const;
export const CREATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_STARTED =
  'CREATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_STARTED' as const;
export const CREATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_SUCCEEDED =
  'CREATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_SUCCEEDED' as const;
export const CREATE_COMPONENT_ACROSS_LOCALES_PROGRESSED =
  'CREATE_COMPONENT_ACROSS_LOCALES_PROGRESSED' as const;
export const CREATE_COMPONENT_ACROSS_LOCALES_FAILED =
  'CREATE_COMPONENT_ACROSS_LOCALES_FAILED' as const;
export const ROOT_SYMBOL_NODE_REMOVED = 'ROOT_SYMBOL_NODE_REMOVED' as const;
export const ROOT_SYMBOL_NODE_RENAMED = 'ROOT_SYMBOL_NODE_RENAMED' as const;
export const ROOT_SYMBOL_NODE_VISIBILITY_CHANGED =
  'ROOT_SYMBOL_NODE_VISIBILITY_CHANGED' as const;
export const __DEPRECATED__RULE_SELECTED =
  '__DEPRECATED__RULE_SELECTED' as const;
export const SYMBOL_ROOT_DUPLICATED = 'SYMBOL_ROOT_DUPLICATED' as const;
export const DUPLICATE_COMPONENT_ACROSS_LOCALES_STARTED =
  'DUPLICATE_COMPONENT_ACROSS_LOCALES_STARTED' as const;
export const DUPLICATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_STARTED =
  'DUPLICATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_STARTED' as const;
export const DUPLICATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_SUCCEEDED =
  'DUPLICATE_COMPONENT_ACROSS_LOCALES_ENQUEUE_SUCCEEDED' as const;
export const DUPLICATE_COMPONENT_ACROSS_LOCALES_PROGRESSED =
  'DUPLICATE_COMPONENT_ACROSS_LOCALES_PROGRESSED' as const;
export const DUPLICATE_COMPONENT_ACROSS_LOCALES_FAILED =
  'DUPLICATE_COMPONENT_ACROSS_LOCALES_FAILED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_CREATED =
  'COMPONENT_ROOT_STYLE_VARIANT_CREATED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_RENAMED =
  'COMPONENT_ROOT_STYLE_VARIANT_RENAMED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_DUPLICATED =
  'COMPONENT_ROOT_STYLE_VARIANT_DUPLICATED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_DELETED =
  'COMPONENT_ROOT_STYLE_VARIANT_DELETED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_REORDERED =
  'COMPONENT_ROOT_STYLE_VARIANT_REORDERED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_SELECTOR_COPIED =
  'COMPONENT_ROOT_STYLE_VARIANT_SELECTOR_COPIED' as const;
export const COMPONENT_ROOT_STYLE_VARIANT_SELECTOR_COPY_FAILED =
  'COMPONENT_ROOT_STYLE_VARIANT_SELECTOR_COPY_FAILED' as const;
export const COMPONENT_CANVAS_PAGE_DELETED =
  'COMPONENT_CANVAS_PAGE_DELETED' as const;
export const IMPORT_LIBRARY_ASSETS_FOR_DUPLICATION_REQUESTED =
  'IMPORT_LIBRARY_ASSETS_FOR_DUPLICATION_REQUESTED' as const;
export const IMPORT_LIBRARY_ASSETS_FOR_DUPLICATION_SUCCEEDED =
  'IMPORT_LIBRARY_ASSETS_FOR_DUPLICATION_SUCCEEDED' as const;
export const SAVE_ATTEMPTED = 'SAVE_ATTEMPTED' as const;
export const SAVE_MODAL_CLOSE_REQUESTED = 'SAVE_MODAL_CLOSE_REQUESTED' as const;
export const SAVE_MODAL_DESCRIPTION_UPDATED =
  'SAVE_MODAL_DESCRIPTION_UPDATED' as const;
export const SAVE_MODAL_TRIGGERED = 'SAVE_MODAL_TRIGGERED' as const;
export const SCHEDULED_ITEMS_UPDATED_REMOTELY =
  'SCHEDULED_ITEMS_UPDATED_REMOTELY' as const;
export const SCROLL_SAVED = 'SCROLL_SAVED' as const;
export const SEARCH_INDEX_QUEUED = 'SEARCH_INDEX_QUEUED' as const;
export const SEARCH_INDEX_STATUS_UPDATED =
  'SEARCH_INDEX_STATUS_UPDATED' as const;
export const SEARCH_INDEX_FAILED = 'SEARCH_INDEX_FAILED' as const;
export const SEARCH_PAGE_CREATION_REQUESTED =
  'SEARCH_PAGE_CREATION_REQUESTED' as const;
export const SEARCH_RESULTS_PAGE_CREATION_FAILED =
  'SEARCH_RESULTS_PAGE_CREATION_FAILED' as const;
export const SEARCH_RESULTS_PAGE_CREATION_SUCCESS =
  'SEARCH_RESULTS_PAGE_CREATION_SUCCESS' as const;
export const SECTION_CHANGED = 'SECTION_CHANGED' as const;
export const SECTION_CHANGED_ERROR = 'SECTION_CHANGED_ERROR' as const;
export const NODE_CLICKED = 'NODE_CLICKED' as const;
export const CODE_COMPONENT_INNER_NODE_CLEARED =
  'CODE_COMPONENT_INNER_NODE_CLEARED' as const;
export const CODE_COMPONENT_SELECTION_CLEARED =
  'CODE_COMPONENT_SELECTION_CLEARED' as const;
export const CODE_COMPONENT_INNER_NODE_RESOLVED =
  'CODE_COMPONENT_INNER_NODE_RESOLVED' as const;
export const NODE_IN_INSTANCE_DOUBLE_CLICKED =
  'NODE_IN_INSTANCE_DOUBLE_CLICKED' as const;
export const NODE_IN_INSTANCE_FOCUSED = 'NODE_IN_INSTANCE_FOCUSED' as const;
export const PARENT_SYMBOL_OF_NODE_IN_INSTANCE_FOCUSED =
  'PARENT_SYMBOL_OF_NODE_IN_INSTANCE_FOCUSED' as const;
export const CANVAS_BODY_RENDERED = 'CANVAS_BODY_RENDERED' as const;
export const STYLE_SHEET_RENDERED = 'STYLE_SHEET_RENDERED' as const;
export const SEND_FEEDBACK_MODAL_CLOSE = 'SEND_FEEDBACK_MODAL_CLOSE' as const;
export const SEND_FEEDBACK_MODAL_FILE_TOO_LARGE =
  'SEND_FEEDBACK_MODAL_FILE_TOO_LARGE' as const;
export const SEND_FEEDBACK_MODAL_OPEN = 'SEND_FEEDBACK_MODAL_OPEN' as const;
export const SEND_FEEDBACK_MODAL_SHOW_ERRORS =
  'SEND_FEEDBACK_MODAL_SHOW_ERRORS' as const;
export const SEND_FEEDBACK_MODAL_SUBMIT = 'SEND_FEEDBACK_MODAL_SUBMIT' as const;
export const SEND_FEEDBACK_MODAL_SUBMIT_FAILED =
  'SEND_FEEDBACK_MODAL_SUBMIT_FAILED' as const;
export const SEND_FEEDBACK_MODAL_SUBMIT_SUCCESS =
  'SEND_FEEDBACK_MODAL_SUBMIT_SUCCESS' as const;
export const SEND_FEEDBACK_MODAL_TOGGLE_INCLUDE_DATA =
  'SEND_FEEDBACK_MODAL_TOGGLE_INCLUDE_DATA' as const;
export const SEND_FEEDBACK_MODAL_INCLUDE_READONLY_LINK_TOGGLED =
  'SEND_FEEDBACK_MODAL_INCLUDE_READONLY_LINK_TOGGLED' as const;
export const SEND_FEEDBACK_MODAL_UPDATE_SUBJECT =
  'SEND_FEEDBACK_MODAL_UPDATE_SUBJECT' as const;
export const SEND_FEEDBACK_MODAL_ADD_IMAGE =
  'SEND_FEEDBACK_MODAL_ADD_IMAGE' as const;
export const SEND_FEEDBACK_MODAL_DELETE_IMAGE =
  'SEND_FEEDBACK_MODAL_DELETE_IMAGE' as const;
export const SEND_FEEDBACK_MODAL_UPDATE_DESCRIPTION =
  'SEND_FEEDBACK_MODAL_UPDATE_DESCRIPTION' as const;
export const SEND_FEEDBACK_MODAL_UPDATE_TOPIC =
  'SEND_FEEDBACK_MODAL_UPDATE_TOPIC' as const;
export const SERVER_ERROR_RECEIVED = 'SERVER_ERROR_RECEIVED' as const;
export const SERVER_REQUEST_TIMED_OUT = 'SERVER_REQUEST_TIMED_OUT' as const;
export const SERVER_SYNC_STATUS_RESET = 'SERVER_SYNC_STATUS_RESET' as const;
export const SERVER_SYNC_DOM_STATUS_CHANGED =
  'SERVER_SYNC_DOM_STATUS_CHANGED' as const;
export const SERVER_SYNC_CSS_STATUS_CHANGED =
  'SERVER_SYNC_CSS_STATUS_CHANGED' as const;
export const SERVER_SYNC_IX2_STATUS_CHANGED =
  'SERVER_SYNC_IX2_STATUS_CHANGED' as const;
export const SERVER_SYNC_IX3_STATUS_CHANGED =
  'SERVER_SYNC_IX3_STATUS_CHANGED' as const;
export const SERVER_SYNC_VALUES_STATUS_CHANGED =
  'SERVER_SYNC_VALUES_STATUS_CHANGED' as const;
export const SERVER_SYNC_UPLOAD_STATUS_CHANGED =
  'SERVER_SYNC_UPLOAD_STATUS_CHANGED' as const;

export const SESSION_REPLAY_CONSENT_GRANTED =
  'SESSION_REPLAY_CONSENT_GRANTED' as const;
export const SESSION_REPLAY_CONSENT_GRANTED_SUCCESS =
  'SESSION_REPLAY_CONSENT_GRANTED_SUCCESS' as const;
export const SESSION_REPLAY_CONSENT_GRANTED_FAILED =
  'SESSION_REPLAY_CONSENT_GRANTED_FAILED' as const;
export const SESSION_REPLAY_CONSENT_DENIED =
  'SESSION_REPLAY_CONSENT_DENIED' as const;
export const SESSION_REPLAY_CONSENT_DENIED_SUCCESS =
  'SESSION_REPLAY_CONSENT_DENIED_SUCCESS' as const;
export const SESSION_REPLAY_CONSENT_DENIED_FAILED =
  'SESSION_REPLAY_CONSENT_DENIED_FAILED' as const;

export const SESSION_STARTED = 'SESSION_STARTED' as const;
export const SESSION_NOTIFICATIONS_RETRIEVED =
  'SESSION_NOTIFICATIONS_RETRIEVED' as const;
export const SET_STYLE_ENDED = 'SET_STYLE_ENDED' as const;
export const SET_STYLE_STARTED = 'SET_STYLE_STARTED' as const;
export const SINGLE_ITEM_PUBLISHED_STARTED =
  'SINGLE_ITEM_PUBLISHED_STARTED' as const;
export const SINGLE_ITEM_PUBLISHED_FINISHED =
  'SINGLE_ITEM_PUBLISHED_FINISHED' as const;
export const BACKGROUND_VIDEO_CANCELLED = 'BACKGROUND_VIDEO_CANCELLED' as const;
export const BACKGROUND_VIDEO_CHANGED = 'BACKGROUND_VIDEO_CHANGED' as const;
export const BACKGROUND_VIDEO_TEMP_ID_CHANGED =
  'BACKGROUND_VIDEO_TEMP_ID_CHANGED' as const;
export const BACKGROUND_VIDEO_UPLOAD_FAILED =
  'BACKGROUND_VIDEO_UPLOAD_FAILED' as const;
export const SETTINGS_SECTION_TOGGLED = 'SETTINGS_SECTION_TOGGLED' as const;
export const SETTINGS_SECTIONS_BATCH_SET =
  'SETTINGS_SECTIONS_BATCH_SET' as const;
export const PROPS_PANEL_FOCUS_MODE_TOGGLED =
  'PROPS_PANEL_FOCUS_MODE_TOGGLED' as const;
export const SETTINGS_TAB_HIGHLIGHTED = 'SETTINGS_TAB_HIGHLIGHTED' as const;
export const SHOW_FLEX_OR_GRID_PARENT_OUTLINE =
  'SHOW_FLEX_OR_GRID_PARENT_OUTLINE' as const;
export const HIDE_FLEX_OR_GRID_PARENT_OUTLINE =
  'HIDE_FLEX_OR_GRID_PARENT_OUTLINE' as const;
export const SHOW_QUICK_STACK_PARENT_OUTLINE =
  'SHOW_QUICK_STACK_PARENT_OUTLINE' as const;
export const HIDE_QUICK_STACK_PARENT_OUTLINE =
  'HIDE_QUICK_STACK_PARENT_OUTLINE' as const;
export const SHOW_RELATIVE_PARENT_OUTLINE =
  'SHOW_RELATIVE_PARENT_OUTLINE' as const;
export const SIGNUP_RESPONDED = 'SIGNUP_RESPONDED' as const;
export const SIGNUP_RESPONDED_BEGIN = 'SIGNUP_RESPONDED_BEGIN' as const;
export const SIGNUP_RESPONDED_ERROR = 'SIGNUP_RESPONDED_ERROR' as const;
export const SITE_CONTEXT_SURVEY_DATA_FAILED =
  'SITE_CONTEXT_SURVEY_DATA_FAILED' as const;
export const SITE_CONTEXT_SURVEY_DATA_SUCCEEDED =
  'SITE_CONTEXT_SURVEY_DATA_SUCCEEDED' as const;
export const SITE_CONTEXT_SURVEY_DATA_REQUESTED =
  'SITE_CONTEXT_SURVEY_DATA_REQUESTED' as const;
export const SITE_MIGRATED = 'SITE_MIGRATED' as const;
export const SITE_MIGRATED_BEGIN = 'SITE_MIGRATED_BEGIN' as const;
export const SITE_PUBLISHED = 'SITE_PUBLISHED' as const;
export const SITE_PUBLISHED_BEGIN = 'SITE_PUBLISHED_BEGIN' as const;
// Records which branch the publish this tab initiated targets (`null` =
// full-site), so the branch UI can tell its own staging publish apart from a
// full-site/other-branch publish it is only blocked by (WORK-1811).
export const SITE_PUBLISH_TARGET_SET = 'SITE_PUBLISH_TARGET_SET' as const;
export const SITE_PUBLISHED_ERROR = 'SITE_PUBLISHED_ERROR' as const;
export const SITE_PUBLISHED_REMOTELY = 'SITE_PUBLISHED_REMOTELY' as const;
export const SITE_PUBLISH_STAGE_UPDATED = 'SITE_PUBLISH_STAGE_UPDATED' as const;
export const SITE_RESTORE_COMPLETED_REMOTELY =
  'SITE_RESTORE_COMPLETED_REMOTELY' as const;
export const SITE_RESTORE_IN_PROGRESS_REMOTELY =
  'SITE_RESTORE_IN_PROGRESS_REMOTELY' as const;
export const SITE_SECRET_DATA_FETCHED = 'SITE_SECRET_DATA_FETCHED' as const;
export const SITE_SETTINGS_NAV_ATTEMPTED =
  'SITE_SETTINGS_NAV_ATTEMPTED' as const;
export const SITE_SETTINGS_NAV_ATTEMPTED_BEGIN =
  'SITE_SETTINGS_NAV_ATTEMPTED_BEGIN' as const;
export const SITE_SETTINGS_NAV_ATTEMPTED_ERROR =
  'SITE_SETTINGS_NAV_ATTEMPTED_ERROR' as const;
export const SITE_SNAPSHOT_IN_PROGRESS_REMOTELY =
  'SITE_SNAPSHOT_IN_PROGRESS_REMOTELY' as const;
export const SITE_VERSIONS_FETCH_STARTED =
  'SITE_VERSIONS_FETCH_STARTED' as const;
export const SITE_VERSIONS_FETCH_FAILED = 'SITE_VERSIONS_FETCH_FAILED' as const;
export const SITE_VERSIONS_RECEIVED = 'SITE_VERSIONS_RECEIVED' as const;
export const SLIDER_ANIMATED = 'SLIDER_ANIMATED' as const;
export const SLIDER_SLIDE_SELECTED = 'SLIDER_SLIDE_SELECTED' as const;
export const STATUS_ITEM_CLICKED_EVENT = 'STATUS_ITEM_CLICKED_EVENT' as const;
export const STATUS_MENU_CLICKED_EVENT = 'STATUS_MENU_CLICKED_EVENT' as const;
export const STARTED_FILE_INPUT = 'STARTED_FILE_INPUT' as const;
export const STARTED_FILE_INPUT_BEGIN = 'STARTED_FILE_INPUT_BEGIN' as const;
export const STARTED_LOADING_NODE = 'STARTED_LOADING_NODE' as const;
export const STATIC_NODE_SAVED = 'STATIC_NODE_SAVED' as const;
export const STATIC_NODE_SAVED_BEGIN = 'STATIC_NODE_SAVED_BEGIN' as const;
export const STATIC_NODE_SAVED_ERROR = 'STATIC_NODE_SAVED_ERROR' as const;
export const STALE_CLIENT_MULTIUSER_DETECTED =
  'STALE_CLIENT_MULTIUSER_DETECTED' as const;
export const STOPPED_EDITING_NODE = 'STOPPED_EDITING_NODE' as const;
export const STOPPED_LOADING_NODE = 'STOPPED_LOADING_NODE' as const;
export const STYLE_BLOCK_ADDED = 'STYLE_BLOCK_ADDED' as const;
export const STYLE_BLOCK_DUPLICATED = 'STYLE_BLOCK_DUPLICATED' as const;
export const STYLE_BLOCK_NESTED = 'STYLE_BLOCK_NESTED' as const;
export const STYLE_BLOCK_UNNESTED = 'STYLE_BLOCK_UNNESTED' as const;
export const STYLE_BLOCK_INVALID_STYLELESS_REMOVED =
  'STYLE_BLOCK_INVALID_STYLELESS_REMOVED' as const;
export const STYLE_BLOCK_RENAMED = 'STYLE_BLOCK_RENAMED' as const;
export const STYLE_BLOCK_REMOVED = 'STYLE_BLOCK_REMOVED' as const;
export const __DEPRECATED__STYLE_BLOCK_STATE_CHANGED =
  '__DEPRECATED__STYLE_BLOCK_STATE_CHANGED' as const;
export const STYLE_BLOCKS_IMPORTED = 'STYLE_BLOCKS_IMPORTED' as const;
export const STYLE_BLOCKS_REIMPORTED = 'STYLE_BLOCKS_REIMPORTED' as const;
export const STYLE_BLOCKS_DELETED = 'STYLE_BLOCKS_DELETED' as const;
export const TAG_STYLE_RESETTED = 'TAG_STYLE_RESETTED' as const;
export const STYLE_BLOCKS_MANY_UPDATED = 'STYLE_BLOCKS_MANY_UPDATED' as const;
export const ELEMENT_STYLE_CONVERTED_TO_CLASS =
  'ELEMENT_STYLE_CONVERTED_TO_CLASS' as const;
export const STYLE_PEEK_ENDED = 'STYLE_PEEK_ENDED' as const;
export const STYLE_PEEK_STARTED = 'STYLE_PEEK_STARTED' as const;
export const STYLE_SET = 'STYLE_SET' as const;
export const __DEPRECATED__NATIVE_SRCSET_ATTRIBUTES_REMOVED =
  '__DEPRECATED__NATIVE_SRCSET_ATTRIBUTES_REMOVED' as const;
export const __DEPRECATED__RESPONSIVE_GENERATION_STARTED =
  '__DEPRECATED__RESPONSIVE_GENERATION_STARTED' as const;
export const STYLE_RULE_SELECTOR_BLURRED =
  'STYLE_RULE_SELECTOR_BLURRED' as const;
export const STYLE_RULE_SELECTOR_FOCUSED =
  'STYLE_RULE_SELECTOR_FOCUSED' as const;
export const STYLE_RULE_SELECTOR_TOKEN_BLURRED =
  'STYLE_RULE_SELECTOR_TOKEN_BLURRED' as const;
export const STYLE_RULE_SELECTOR_TOKEN_CHANGED =
  'STYLE_RULE_SELECTOR_TOKEN_CHANGED' as const;
export const STYLE_RULE_SELECTOR_TOKEN_MENU_OPEN =
  'STYLE_RULE_SELECTOR_TOKEN_MENU_OPEN' as const;
export const STYLE_RULE_SELECTOR_TOKEN_MENU_RENAME =
  'STYLE_RULE_SELECTOR_TOKEN_MENU_RENAME' as const;
export const STYLE_STATE_CHANGED_POST = 'STYLE_STATE_CHANGED_POST' as const;
export const STYLE_STATE_REPLACED = 'STYLE_STATE_REPLACED' as const;
export const STYLE_VARIANT_RESET = 'STYLE_VARIANT_RESET' as const;
export const SWATCH_ADDED = 'SWATCH_ADDED' as const;
export const SWATCH_EDITED = 'SWATCH_EDITED' as const;
export const SWATCH_UPDATE_BEGAN = 'SWATCH_UPDATE_BEGAN' as const;
export const SWATCH_EDITING_CANCELLED = 'SWATCH_EDITING_CANCELLED' as const;
export const SWATCH_REMOVED = 'SWATCH_REMOVED' as const;
export const SWATCH_UPDATED = 'SWATCH_UPDATED' as const;
export const SWATCHES_INJECTED = 'SWATCHES_INJECTED' as const;
export const SYMBOL_NODE_DYN_BINDING_REMOVED =
  'SYMBOL_NODE_DYN_BINDING_REMOVED' as const;
export const SYMBOL_NODE_FOCUSED = 'SYMBOL_NODE_FOCUSED' as const;
export const SYMBOL_NODE_FOCUSED_ATTEMPTED =
  'SYMBOL_NODE_FOCUSED_ATTEMPTED' as const;
export const SYMBOL_NODE_UNFOCUSED = 'SYMBOL_NODE_UNFOCUSED' as const;
export const COMPONENT_NODE_UNFOCUSED_ALL =
  'COMPONENT_NODE_UNFOCUSED_ALL' as const;
export const SYMBOL_NODE_UNFOCUS_ATTEMPTED =
  'SYMBOL_NODE_UNFOCUS_ATTEMPTED  ' as const;
export const COMPONENT_INSTANCE_UNLINKED =
  'COMPONENT_INSTANCE_UNLINKED' as const;
export const COMPONENT_INSTANCE_UNLINK_ATTEMPTED =
  'COMPONENT_INSTANCE_UNLINK_ATTEMPTED' as const;
export const UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_STARTED =
  'UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_STARTED' as const;
export const UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_ENQUEUE_STARTED =
  'UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_ENQUEUE_STARTED' as const;
export const UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_ENQUEUE_SUCCEEDED =
  'UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_ENQUEUE_SUCCEEDED' as const;
export const UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_PROGRESSED =
  'UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_PROGRESSED' as const;
export const UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_FAILED =
  'UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_FAILED' as const;
export const SYMBOL_INSTANCE_OVERRIDES_RESETTED =
  'SYMBOL_INSTANCE_OVERRIDES_RESETTED' as const;
export const SYMBOL_ROOT_ADDED_SILENTLY = 'SYMBOL_ROOT_ADDED_SILENTLY' as const;
export const SYMBOL_UI_CHANGE_SYMBOL_NAME =
  'SYMBOL_UI_CHANGE_SYMBOL_NAME' as const;
export const SYMBOL_UI_CHANGE_SYMBOL_DESCRIPTION =
  'SYMBOL_UI_CHANGE_SYMBOL_DESCRIPTION' as const;
export const SYMBOL_UI_CHANGE_SYMBOL_GROUP =
  'SYMBOL_UI_CHANGE_SYMBOL_GROUP' as const;
export const SYMBOL_UI_CHANGE_SYMBOL_CATEGORY =
  'SYMBOL_UI_CHANGE_SYMBOL_CATEGORY' as const;
export const SYMBOL_UI_CHANGE_SYMBOL_PREVIEW_IMAGE =
  'SYMBOL_UI_CHANGE_SYMBOL_PREVIEW_IMAGE' as const;
export const SYMBOL_UI_CREATE_SYMBOL_BEGIN =
  'SYMBOL_UI_CREATE_SYMBOL_BEGIN' as const;
export const SYMBOL_UI_CREATE_BLANK_COMPONENT_BEGIN =
  'SYMBOL_UI_CREATE_BLANK_COMPONENT_BEGIN' as const;
export const SYMBOL_UI_CREATE_SYMBOL_END =
  'SYMBOL_UI_CREATE_SYMBOL_END' as const;
export const SYMBOL_UI_EDIT_SYMBOL_BEGIN =
  'SYMBOL_UI_EDIT_SYMBOL_BEGIN' as const;
export const SYMBOL_UI_SYMBOL_DUPLICATE = 'SYMBOL_UI_SYMBOL_DUPLICATE' as const;
export const SYMBOL_UI_SYMBOL_DUPLICATE_CANCEL =
  'SYMBOL_UI_SYMBOL_DUPLICATE_CANCEL' as const;
export const SYMBOL_UI_EDIT_SYMBOL_END = 'SYMBOL_UI_EDIT_SYMBOL_END' as const;
export const COMPONENT_SHOW_INSTANCES_MENU_OPENED =
  'COMPONENT_SHOW_INSTANCES_MENU_OPENED' as const;
export const COMPONENT_SHOW_INSTANCES_MENU_CLOSED =
  'COMPONENT_SHOW_INSTANCES_MENU_CLOSED' as const;
export const SYMBOL_UI_SEARCH_INPUT_FOCUS_REQUESTED =
  'SYMBOL_UI_SEARCH_INPUT_FOCUS_REQUESTED' as const;
export const SYNC_CHANGING_CANCELED = 'SYNC_CHANGING_CANCELED' as const;
export const SYMBOLS_PANEL_SET_EXPANSION_STATE =
  'SYMBOLS_PANEL_SET_EXPANSION_STATE' as const;
export const SYSTEM_BECAME_IDLE = 'SYSTEM_BECAME_IDLE' as const;
export const TAB_HIGHLIGHT_RESET = 'TAB_HIGHLIGHT_RESET' as const;
export const TAB_SELECTED = 'TAB_SELECTED' as const;
export const TAB_SELECTED_WITH_HIGHLIGHT =
  'TAB_SELECTED_WITH_HIGHLIGHT' as const;
export const TARGET_TOGGLE_CLICKED = 'TARGET_TOGGLE_CLICKED' as const;
export const THEME_UPDATED = 'THEME_UPDATED' as const;
export const THEME_RECOMMENDATIONS_UPDATED =
  'THEME_RECOMMENDATIONS_UPDATED' as const;
export const TOGGLE_DRAG_DROP_DEBUGGER = 'TOGGLE_DRAG_DROP_DEBUGGER' as const;
export const TOGGLE_UNUSED_STYLES = 'TOGGLE_UNUSED_STYLES' as const;
export const TOGGLE_UNUSED_STYLES_SHOW_ALL =
  'TOGGLE_UNUSED_STYLES_SHOW_ALL' as const;
export const TOGGLE_ELEMENT_STYLES = 'TOGGLE_ELEMENT_STYLES' as const;
export const TOGGLE_TAG_STYLES_FULL_VIEW =
  'TOGGLE_TAG_STYLES_FULL_VIEW' as const;
export const STYLE_SECTION_TOGGLED = 'STYLE_SECTION_TOGGLED' as const;
export const STYLES_PANEL_LIBRARY_FOCUSED =
  'STYLES_PANEL_LIBRARY_FOCUSED' as const;
export const UI_VISIBILITY_CHANGED = 'UI_VISIBILITY_CHANGED' as const;
export const TOP_BAR_HOVER_TOGGLED = 'TOP_BAR_HOVER_TOGGLED' as const;
export const TRANSACTIONS_FETCHED = 'TRANSACTIONS_FETCHED' as const;
export const TRANSACTIONS_FOCUSED = 'TRANSACTIONS_FOCUSED' as const;
export const AUDIT_ASSET_REQUESTED = 'AUDIT_ASSET_REQUESTED' as const;
export const UI_STATE_TOGGLED = 'UI_STATE_TOGGLED' as const;
export const UI_STATES_TOGGLED = 'UI_STATES_TOGGLED' as const;
export const UNDO = 'UNDO' as const;
export const UNMOVABLE_NODE_DRAG_ATTEMPTED =
  'UNMOVABLE_NODE_DRAG_ATTEMPTED' as const;
export const DOMAIN_UNPUBLISHED = 'DOMAIN_UNPUBLISHED' as const;
export const DOMAIN_UNPUBLISHED_BEGIN = 'DOMAIN_UNPUBLISHED_BEGIN' as const;
export const DOMAIN_UNPUBLISHED_ERROR = 'DOMAIN_UNPUBLISHED_ERROR' as const;
export const UPDATE_CANVAS_SCROLL_LEFT = 'UPDATE_CANVAS_SCROLL_LEFT' as const;
export const UPDATE_CANVAS_SCROLL_TOP = 'UPDATE_CANVAS_SCROLL_TOP' as const;
export const UPDATE_CANVAS_SCROLL_SPEED = 'UPDATE_CANVAS_SCROLL_SPEED' as const;
export const UPDATE_DRAG_DROP_DEBUG_DATA =
  'UPDATE_DRAG_DROP_DEBUG_DATA' as const;
export const UPDATE_INITIAL_DRAG_INFO = 'UPDATE_INITIAL_DRAG_INFO' as const;
export const USERS_RECEIVED = 'USERS_RECEIVED' as const;
export const VIDEO_READ_FOR_VALIDATION_FINISHED =
  'VIDEO_READ_FOR_VALIDATION_FINISHED' as const;
export const VIDEO_READ_FOR_VALIDATION_FAILED =
  'VIDEO_READ_FOR_VALIDATION_FAILED' as const;
export const VIDEO_SELECTED_FOR_UPLOAD = 'VIDEO_SELECTED_FOR_UPLOAD' as const;
export const AUDIO_FILE_LOADING_STARTED = 'AUDIO_FILE_LOADING_STARTED' as const;
export const AUDIO_FILE_LOADING_FINISHED =
  'AUDIO_FILE_LOADING_FINISHED' as const;
export const AUDIO_FILE_LOADING_FAILED = 'AUDIO_FILE_LOADING_FAILED' as const;
export const AUDIO_FILE_READ_FOR_VALIDATION_FINISHED =
  'AUDIO_FILE_READ_FOR_VALIDATION_FINISHED' as const;
export const AUDIO_FILE_READ_FOR_VALIDATION_FAILED =
  'AUDIO_FILE_READ_FOR_VALIDATION_FAILED' as const;
export const VALIDATE_ASSET_URLS = 'VALIDATE_ASSET_URLS' as const;
export const VALIDATE_ASSET_URLS_SUCCEDED =
  'VALIDATE_ASSET_URLS_SUCCEDED' as const;
export const VALIDATE_ASSET_URLS_FAILED = 'VALIDATE_ASSET_URLS_FAILED' as const;
export const JSON_FILE_SELECTED_FOR_UPLOAD =
  'JSON_FILE_SELECTED_FOR_UPLOAD' as const;
export const JSON_FILE_READ_FOR_VALIDATION_FINISHED =
  'JSON_FILE_READ_FOR_VALIDATION_FINISHED' as const;
export const JSON_FILE_READ_FOR_VALIDATION_FAILED =
  'JSON_FILE_READ_FOR_VALIDATION_FAILED' as const;
export const RIVE_FILE_SELECTED_FOR_UPLOAD =
  'RIVE_FILE_SELECTED_FOR_UPLOAD' as const;
export const RIVE_FILE_READ_FOR_VALIDATION_FINISHED =
  'RIVE_FILE_READ_FOR_VALIDATION_FINISHED' as const;
export const RIVE_FILE_READ_FOR_VALIDATION_FAILED =
  'RIVE_FILE_READ_FOR_VALIDATION_FAILED' as const;
export const VIDEO_TUTORIAL_CHANGED = 'VIDEO_TUTORIAL_CHANGED' as const;
export const VIDEO_TUTORIAL_PLAYED = 'VIDEO_TUTORIAL_PLAYED' as const;
export const WINDOW_DIMENSIONS_UPDATED = 'WINDOW_DIMENSIONS_UPDATED' as const;
export const SITE_INTEGRATIONS_NAV_ATTEMPTED =
  'SITE_INTEGRATIONS_NAV_ATTEMPTED' as const;
export const WORKSPACE_DIMENSIONS_UPDATED =
  'WORKSPACE_DIMENSIONS_UPDATED' as const;
export const WORKSPACE_INSETS_CHANGED = 'WORKSPACE_INSETS_CHANGED' as const;
export const CSV_IMPORT_MODAL_OPENED = 'CSV_IMPORT_MODAL_OPENED' as const;
export const CSV_IMPORT_MODAL_STEP_CHANGED =
  'CSV_IMPORT_MODAL_STEP_CHANGED' as const;
export const CSV_IMPORT_SETTINGS_DISPLAYED =
  'CSV_IMPORT_SETTINGS_DISPLAYED' as const;
export const CSV_IMPORT_CANCELLED = 'CSV_IMPORT_CANCELLED' as const;
export const CSV_IMPORT_MATCHING_ITEMS_COUNT_CHANGED =
  'CSV_IMPORT_MATCHING_ITEMS_COUNT_CHANGED' as const;
export const CSV_IMPORT_LINK_CANDIDATE_ITEM_IDS_CHANGED =
  'CSV_IMPORT_LINK_CANDIDATE_ITEM_IDS_CHANGED' as const;
export const SELECTED_NODE_REMOVED = 'SELECTED_NODE_REMOVED' as const;
export const CSV_ITEMS_GENERATION_STARTED =
  'CSV_ITEMS_GENERATION_STARTED' as const;
export const CSV_ITEMS_GENERATION_CANCELLED_STARTED =
  'CSV_ITEMS_GENERATION_CANCELLED_STARTED' as const;
export const CSV_ITEMS_GENERATION_CANCELLED =
  'CSV_ITEMS_GENERATION_CANCELLED' as const;
export const CSV_DATA_RECEIVED = 'CSV_DATA_RECEIVED' as const;
export const CSV_COLUMN_HOVERED = 'CSV_COLUMN_HOVERED' as const;
export const CSV_COLUMN_UNHOVERED = 'CSV_COLUMN_UNHOVERED' as const;
export const CSV_COLUMN_FIELD_FOCUSED = 'CSV_COLUMN_FIELD_FOCUSED' as const;
export const CSV_COLUMN_FIELD_BLURRED = 'CSV_COLUMN_FIELD_BLURRED' as const;
export const CSV_ITEMS_GENERATED = 'CSV_ITEMS_GENERATED' as const;
export const CSV_CURRENT_ITEM_INDEX_CHANGED =
  'CSV_CURRENT_ITEM_INDEX_CHANGED' as const;
export const CSV_COLUMN_ACTION_CHANGED = 'CSV_COLUMN_ACTION_CHANGED' as const;
export const CSV_COLUMN_MAPPINGS_CLEARED =
  'CSV_COLUMN_MAPPINGS_CLEARED' as const;
export const CSV_COLUMN_MAPPED_TO_FIELD = 'CSV_COLUMN_MAPPED_TO_FIELD' as const;
export const CSV_COLUMN_MAPPED_TO_NEW_FIELD =
  'CSV_COLUMN_MAPPED_TO_NEW_FIELD' as const;
export const CSV_SWITCH_FIELD_TOGGLED = 'CSV_SWITCH_FIELD_TOGGLED' as const;
export const CSV_NEW_FIELD_LABEL_MODIFIED =
  'CSV_NEW_FIELD_LABEL_MODIFIED' as const;
export const CSV_NEW_FIELD_HELP_TEXT_MODIFIED =
  'CSV_NEW_FIELD_HELP_TEXT_MODIFIED' as const;
export const CSV_NEW_FIELD_COLLECTION_MODIFIED =
  'CSV_NEW_FIELD_COLLECTION_MODIFIED' as const;
export const CSV_DATA_MODIFIED = 'CSV_DATA_MODIFIED' as const;
export const CSV_HEADER_TOGGLED = 'CSV_HEADER_TOGGLED' as const;
export const CSV_HEADER_OPTION_CHANGED = 'CSV_HEADER_OPTION_CHANGED' as const;
export const CSV_IMPORT_PROGRESS_CHANGED =
  'CSV_IMPORT_PROGRESS_CHANGED' as const;
export const CSV_IMPORT_FLOW_CHANGED = 'CSV_IMPORT_FLOW_CHANGED' as const;
export const CSV_ITEM_GENERATION_FAILED = 'CSV_ITEM_GENERATION_FAILED' as const;
export const CSV_IMPORT_COMPLETED = 'CSV_IMPORT_COMPLETED' as const;
export const CSV_IMPORT_COMPLETED_REMOTELY =
  'CSV_IMPORT_COMPLETED_REMOTELY' as const;
export const CSV_CURRENT_ITEM_IGNORE_CHANGED =
  'CSV_CURRENT_ITEM_IGNORE_CHANGED' as const;
export const CSV_ITEM_FIELD_SKIPPED = 'CSV_ITEM_FIELD_SKIPPED' as const;
export const CSV_COMPONENT_ERROR_IGNORED =
  'CSV_COMPONENT_ERROR_IGNORED' as const;
export const SAVING_SNAPSHOT_BEFORE_CSV_IMPORT_FAILED =
  'SAVING_SNAPSHOT_BEFORE_CSV_IMPORT_FAILED' as const;

// Bulk item actions
export const BULK_ITEM_EDITING_DEACTIVATED =
  'BULK_ITEM_EDITING_DEACTIVATED' as const;
export const BULK_ITEM_SELECTION_CHANGED =
  'BULK_ITEM_SELECTION_CHANGED' as const;
export const BULK_ITEM_SELECT_BUTTON_TOGGLED =
  'BULK_ITEM_SELECT_BUTTON_TOGGLED' as const;
export const BULK_ALL_ITEMS_SELECTION_TOGGLED =
  'BULK_ALL_ITEMS_SELECTION_TOGGLED' as const;
export const BULK_ITEM_DELETION_STARTED = 'BULK_ITEM_DELETION_STARTED' as const;
export const BULK_ITEM_DELETION_COMPLETED =
  'BULK_ITEM_DELETION_COMPLETED' as const;
export const BULK_ITEM_UNPUBLISH_COMPLETED =
  'BULK_ITEM_UNPUBLISH_COMPLETED' as const;
export const BULK_ITEM_UNPUBLISH_STARTED =
  'BULK_ITEM_UNPUBLISH_STARTED' as const;
export const BULK_ITEM_UNPUBLISH_COMPLETED_REMOTELY =
  'BULK_ITEM_UNPUBLISH_COMPLETED_REMOTELY' as const;
export const BULK_ITEM_DELETION_COMPLETED_REMOTELY =
  'BULK_ITEM_DELETION_COMPLETED_REMOTELY' as const;
export const BULK_ITEM_DRAFTING_STARTED = 'BULK_ITEM_DRAFTING_STARTED' as const;
export const BULK_ITEM_DRAFTING_COMPLETED =
  'BULK_ITEM_DRAFTING_COMPLETED' as const;
export const BULK_ITEM_DRAFTING_COMPLETED_REMOTELY =
  'BULK_ITEM_DRAFTING_COMPLETED_REMOTELY' as const;
export const BULK_ITEM_ARCHIVING_STARTED =
  'BULK_ITEM_ARCHIVING_STARTED' as const;
export const BULK_ITEM_ARCHIVING_COMPLETED =
  'BULK_ITEM_ARCHIVING_COMPLETED' as const;
export const BULK_ITEM_PUBLISHING_STARTED =
  'BULK_ITEM_PUBLISHING_STARTED' as const;
export const BULK_ITEM_PUBLISHING_COMPLETED =
  'BULK_ITEM_PUBLISHING_COMPLETED' as const;
export const BULK_ITEM_PUBLISHING_COMPLETED_REMOTELY =
  'BULK_ITEM_PUBLISHING_COMPLETED_REMOTELY' as const;
export const BULK_ITEM_ARCHIVING_COMPLETED_REMOTELY =
  'BULK_ITEM_ARCHIVING_COMPLETED_REMOTELY' as const;
export const BULK_ITEM_EDITING_COMPLETED =
  'BULK_ITEM_EDITING_COMPLETED' as const;
export const BULK_ITEM_INDEXING_STARTED = 'BULK_ITEM_INDEXING_STARTED' as const;
export const BULK_ITEM_INDEXING_COMPLETED =
  'BULK_ITEM_INDEXING_COMPLETED' as const;
export const BULK_ITEM_INDEXING_COMPLETED_REMOTELY =
  'BULK_ITEM_INDEXING_COMPLETED_REMOTELY' as const;
export const LOCALIZATION_BULK_ITEM_DUPLICATING_COMPLETED_REMOTELY =
  'LOCALIZATION_BULK_ITEM_DUPLICATING_COMPLETED_REMOTELY' as const;
export const AUTO_TRANSLATE_ON_PUBLISH_FINISHED_REMOTELY =
  'AUTO_TRANSLATE_ON_PUBLISH_FINISHED_REMOTELY' as const;

/** Scoped to one page and locale, unlike the publish-time equivalent above. */
export const AUTO_TRANSLATE_ON_OPEN_FINISHED_REMOTELY =
  'AUTO_TRANSLATE_ON_OPEN_FINISHED_REMOTELY' as const;

/**
 * One locale's slice of a page's `autoTranslatedSettings`. Merged, not
 * replacing the page: translation runs a job per locale, so a whole-page
 * payload would let a slower locale clobber a faster one's translations.
 */
export const PAGE_AUTO_TRANSLATED_METADATA_SAVED_REMOTELY =
  'PAGE_AUTO_TRANSLATED_METADATA_SAVED_REMOTELY' as const;

export const XSCP_ASSET_IMPORT_SUCCEEDED =
  'XSCP_ASSET_IMPORT_SUCCEEDED' as const;
export const XSCP_ASSET_IMPORT_FAILED = 'XSCP_ASSET_IMPORT_FAILED' as const;
export const USER_AUTHENTICATED = 'USER_AUTHENTICATED' as const;
export const USER_NOT_AUTHENTICATED = 'USER_NOT_AUTHENTICATED' as const;
export const UNAUTHENTICATED_USER_SIGNUP_STARTED =
  'UNAUTHENTICATED_USER_SIGNUP_STARTED' as const;
export const UNAUTHENTICATED_USER_SIGNUP_SUCCEEDED =
  'UNAUTHENTICATED_USER_SIGNUP_SUCCEEDED' as const;
export const UNAUTHENTICATED_USER_SIGNUP_FAILED =
  'UNAUTHENTICATED_USER_SIGNUP_FAILED' as const;
export const PREVIEW_VISITED = 'PREVIEW_VISITED' as const;
export const ANONYMOUS_COMMENT_VISITED = 'ANONYMOUS_COMMENT_VISITED' as const;
export const COMMERCE_INVENTORY_SYNC_SELECTED =
  'COMMERCE_INVENTORY_SYNC_SELECTED' as const;
export const COMPONENT_ROOT_STARTED_EDITING_REMOTELY =
  'COMPONENT_ROOT_STARTED_EDITING_REMOTELY' as const;
export const STORE_CRASH_FORCED = 'STORE_CRASH_FORCED' as const;
export const COMMERCE_CONFIRMATION_FOR_INVENTORY_SYNC_CANCELED =
  'COMMERCE_CONFIRMATION_FOR_INVENTORY_SYNC_CANCELED' as const;
export const COMMERCE_INTEGRATIONS_FETCH_FAILED =
  'COMMERCE_INTEGRATIONS_FETCH_FAILED' as const;
export const COMMERCE_INTEGRATIONS_FETCH_SUCCEEDED =
  'COMMERCE_INTEGRATIONS_FETCH_SUCCEEDED' as const;
export const CSV_FILE_RECEIVED = 'CSV_FILE_RECEIVED' as const;

// Async CSV Export
export const CMS_ITEMS_EXPORT_REQUESTED = 'CMS_ITEMS_EXPORT_REQUESTED' as const;
export const CMS_ITEMS_EXPORT_STARTED = 'CMS_ITEMS_EXPORT_STARTED' as const;
export const COMMERCE_PRODUCT_CSV_FILE_REQUESTED =
  'COMMERCE_PRODUCT_CSV_FILE_REQUESTED' as const;
export const COMMERCE_PRODUCT_CSV_FILE_REQUEST_SUCCEEDED =
  'COMMERCE_PRODUCT_CSV_FILE_REQUEST_SUCCEEDED' as const;
export const COMMERCE_CSV_IMPORT_TEMPLATE_REQUESTED =
  'COMMERCE_CSV_IMPORT_TEMPLATE_REQUESTED' as const;
export const COMMERCE_CSV_IMPORT_TEMPLATE_SUCCEEDED =
  'COMMERCE_CSV_IMPORT_TEMPLATE_SUCCEEDED' as const;
export const COMMERCE_CSV_IMPORT_SCHEDULED =
  'COMMERCE_CSV_IMPORT_SCHEDULED' as const;
export const COMMERCE_ORDER_METRICS_FETCH =
  'COMMERCE_ORDER_METRICS_FETCH' as const;
export const COMMERCE_ORDER_METRICS_FETCH_SUCCEEDED =
  'COMMERCE_ORDER_METRICS_FETCH_SUCCEEDED' as const;
export const CSV_IMPORT_STARTED = 'CSV_IMPORT_STARTED' as const;
export const CSV_IMPORT_FAILED = 'CSV_IMPORT_FAILED' as const;
export const CSV_IMPORT_STATUS_UPDATED = 'CSV_IMPORT_STATUS_UPDATED' as const;
export const CSV_IMPORT_LOGS_DOWNLOADED = 'CSV_IMPORT_LOGS_DOWNLOADED' as const;
export const RICH_TEXT_CONTENT_MODAL_CLOSED =
  'RICH_TEXT_CONTENT_MODAL_CLOSED' as const;
export const RICH_TEXT_CONTENT_MODAL_OPENED =
  'RICH_TEXT_CONTENT_MODAL_OPENED' as const;
export const RICH_TEXT_CONTENT_MODAL_EDIT_STARTED =
  'RICH_TEXT_CONTENT_MODAL_EDIT_STARTED' as const;
export const NODE_SETTINGS_TRANSACTION_BEGAN =
  'NODE_SETTINGS_TRANSACTION_BEGAN' as const;
export const NODE_SETTINGS_TRANSACTION_COMMITTED =
  'NODE_SETTINGS_TRANSACTION_COMMITTED' as const;
export const NODE_SETTINGS_TRANSACTION_ABORTED =
  'NODE_SETTINGS_TRANSACTION_ABORTED' as const;
export const NODE_SYNC_STARTED_REMOTELY = 'NODE_SYNC_STARTED_REMOTELY' as const;
export const NODE_SYNC_CHUNK_REMOVE_NODE =
  'NODE_SYNC_CHUNK_REMOVE_NODE' as const;
export const NODE_SYNC_CHUNK_SENT_REMOTELY =
  'NODE_SYNC_CHUNK_SENT_REMOTELY' as const;
export const DOM_SYNC_FAILED = 'DOM_SYNC_FAILED' as const;
export const CSS_SYNC_FAILED = 'CSS_SYNC_FAILED' as const;
export const IX3_SYNC_FAILED = 'IX3_SYNC_FAILED' as const;
export const FONTS_LOADED = 'FONTS_LOADED' as const;
export const CUSTOM_FONTS_ADDED = 'CUSTOM_FONTS_ADDED' as const;
export const GOOGLE_FONT_ADDED = 'GOOGLE_FONT_ADDED' as const;
export const ONBOARDING_MODAL_OPENED = 'ONBOARDING_MODAL_OPENED' as const;
export const ONBOARDING_MODAL_DISMISSED = 'ONBOARDING_MODAL_DISMISSED' as const;
export const ONBOARDING_MODAL_STARTED = 'ONBOARDING_MODAL_STARTED' as const;
export const ONBOARDING_MODAL_COMPLETED = 'ONBOARDING_MODAL_COMPLETED' as const;
export const HISTORY_STATE_INITIAL_SET_UP =
  'HISTORY_STATE_INITIAL_SET_UP' as const;
export const DESIGNER_URL_UPDATED = 'DESIGNER_URL_UPDATED' as const;
export const ORION_LANDING_PAGE_WELCOME_MODAL_DISMISSED =
  'ORION_LANDING_PAGE_WELCOME_MODAL_DISMISSED' as const;
export const LANDING_PAGE_ORION_APP_ID_SET =
  'LANDING_PAGE_ORION_APP_ID_SET' as const;
export const DESIGNER_LOADED_WITH_INVALID_PAGE_ID_IN_THE_URL =
  'DESIGNER_LOADED_WITH_INVALID_PAGE_ID_IN_THE_URL' as const;
export const DESIGNER_LOADED_ON_LOCKED_BRANCH =
  'DESIGNER_LOADED_ON_LOCKED_BRANCH' as const;
export const DESIGNER_LOADED_WITH_COMPONENT_CANVAS_IN_BUILD_MODE =
  'DESIGNER_LOADED_WITH_COMPONENT_CANVAS_IN_BUILD_MODE' as const;
export const DESIGNER_LOADED_WITH_COMPONENT_CANVAS_ON_CAMPAIGN_SITE =
  'DESIGNER_LOADED_WITH_COMPONENT_CANVAS_ON_CAMPAIGN_SITE' as const;
export const DESIGNER_LOADED_WITH_INVALID_DYN_ITEM_ID_IN_THE_URL =
  'DESIGNER_LOADED_WITH_INVALID_DYN_ITEM_ID_IN_THE_URL' as const;
export const DESIGNER_LOADED_IN_EDIT_MODE =
  'DESIGNER_LOADED_IN_EDIT_MODE' as const;
export const DESIGNER_LOADED_IN_DESIGN_MODE =
  'DESIGNER_LOADED_IN_DESIGN_MODE' as const;
export const DESIGNER_LOADED_IN_VIEW_MODE =
  'DESIGNER_LOADED_IN_VIEW_MODE' as const;
export const DESIGNER_BLOCKED_INCOMPATIBLE_WORKFLOW_ON_COMPONENT_CANVAS =
  'DESIGNER_BLOCKED_INCOMPATIBLE_WORKFLOW_ON_COMPONENT_CANVAS' as const;
export const SWITCH_TO_EDIT_MODE_CLICKED =
  'SWITCH_TO_EDIT_MODE_CLICKED' as const;
export const SWITCH_TO_EDIT_MODE_DONE = 'SWITCH_TO_EDIT_MODE_DONE' as const;
export const SWITCH_TO_DESIGN_MODE_CLICKED =
  'SWITCH_TO_DESIGN_MODE_CLICKED' as const;
export const SWITCH_TO_VIEW_MODE_CLICKED =
  'SWITCH_TO_VIEW_MODE_CLICKED' as const;
export const SWITCH_TO_VIEW_MODE_DONE = 'SWITCH_TO_VIEW_MODE_DONE' as const;
export const MODAL_QUERY_PARAM_REMOVED = 'MODAL_QUERY_PARAM_REMOVED' as const;
export const ANALYTICS_TAB_URL_UPDATED = 'ANALYTICS_TAB_URL_UPDATED' as const;
export const WORKFLOW_QUERY_PARAM_REMOVED =
  'WORKFLOW_QUERY_PARAM_REMOVED' as const;
export const SIMULATE_ROLE_QUERY_PARAM_REMOVED =
  'SIMULATE_ROLE_QUERY_PARAM_REMOVED' as const;
export const OPEN_PANEL_QUERY_PARAM_REMOVED =
  'OPEN_PANEL_QUERY_PARAM_REMOVED' as const;
export const ORDER_ID_QUERY_PARAM_REMOVED =
  'ORDER_ID_QUERY_PARAM_REMOVED' as const;
export const LEFT_NAVIGATOR_PANEL_DOCKED =
  'LEFT_NAVIGATOR_PANEL_DOCKED' as const;
export const LEFT_NAVIGATOR_PANEL_UNDOCKED =
  'LEFT_NAVIGATOR_PANEL_UNDOCKED' as const;
export const LEFT_NAVIGATOR_RESIZE_STARTED =
  'LEFT_NAVIGATOR_RESIZE_STARTED' as const;
export const LEFT_NAVIGATOR_RESIZED = 'LEFT_NAVIGATOR_RESIZED' as const;
export const LEFT_NAVIGATOR_RESIZE_ENDED =
  'LEFT_NAVIGATOR_RESIZE_ENDED' as const;
export const VIEW_SETUP_GUIDE_CLICKED = 'VIEW_SETUP_GUIDE_CLICKED' as const;
export const COMMERCE_HIDE_SETUP_GUIDE_CLICKED =
  'COMMERCE_HIDE_SETUP_GUIDE_CLICKED' as const;
export const MANAGE_SEATS_CTA_CLICKED = 'MANAGE_SEATS_CTA_CLICKED' as const;
export const INVITE_MEMBER_SUCCEEDED = 'INVITE_MEMBER_SUCCEEDED' as const;
export const INVITE_MEMBER_FAILED = 'INVITE_MEMBER_FAILED' as const;
export const SEAT_PURCHASE_PAYMENT_FAILED =
  'SEAT_PURCHASE_PAYMENT_FAILED' as const;
export const SEAT_PURCHASE_INVITE_FAILED =
  'SEAT_PURCHASE_INVITE_FAILED' as const;
export const SITE_ROLE_TOGGLED = 'SITE_ROLE_TOGGLED' as const;
export const READ_ONLY_LINK_SHARING_ENABLED =
  'READ_ONLY_LINK_SHARING_ENABLED' as const;
export const READ_ONLY_LINK_SHARING_DISABLED =
  'READ_ONLY_LINK_SHARING_DISABLED' as const;
export const READ_ONLY_LINK_SHARING_ENABLED_SUCCEEDED =
  'READ_ONLY_LINK_SHARING_ENABLED_SUCCEEDED' as const;
export const READ_ONLY_LINK_SHARING_ENABLED_FAILED =
  'READ_ONLY_LINK_SHARING_ENABLED_FAILED' as const;
export const READ_ONLY_LINK_SHARING_DISABLED_SUCCEEDED =
  'READ_ONLY_LINK_SHARING_DISABLED_SUCCEEDED' as const;
export const READ_ONLY_LINK_SHARING_DISABLED_FAILED =
  'READ_ONLY_LINK_SHARING_DISABLED_FAILED' as const;
export const READ_ONLY_LINK_REGENERATED = 'READ_ONLY_LINK_REGENERATED' as const;
export const READ_ONLY_LINK_REGENERATED_SUCCEEDED =
  'READ_ONLY_LINK_REGENERATED_SUCCEEDED' as const;
export const READ_ONLY_LINK_COPIED_TO_CLIPBOARD =
  'READ_ONLY_LINK_COPIED_TO_CLIPBOARD' as const;
export const PROJECT_LINK_SHARING_ENABLED =
  'PROJECT_LINK_SHARING_ENABLED' as const;
export const PROJECT_LINK_SHARING_DISABLED =
  'PROJECT_LINK_SHARING_DISABLED' as const;
export const PROJECT_LINK_SHARING_ENABLED_SUCCEEDED =
  'PROJECT_LINK_SHARING_ENABLED_SUCCEEDED' as const;
export const PROJECT_LINK_SHARING_ENABLED_FAILED =
  'PROJECT_LINK_SHARING_ENABLED_FAILED' as const;
export const PROJECT_LINK_SHARING_DISABLED_SUCCEEDED =
  'PROJECT_LINK_SHARING_DISABLED_SUCCEEDED' as const;
export const PROJECT_LINK_SHARING_DISABLED_FAILED =
  'PROJECT_LINK_SHARING_DISABLED_FAILED' as const;
export const PROJECT_LINK_REGENERATED = 'PROJECT_LINK_REGENERATED' as const;
export const PROJECT_LINK_REGENERATED_SUCCEEDED =
  'PROJECT_LINK_REGENERATED_SUCCEEDED' as const;
export const PROJECT_LINK_COPIED_TO_CLIPBOARD =
  'PROJECT_LINK_COPIED_TO_CLIPBOARD' as const;
export const SANDBOX_LINK_COPIED_TO_CLIPBOARD =
  'SANDBOX_LINK_COPIED_TO_CLIPBOARD' as const;
export const TEXT_SUCCESSFULLY_COPIED_TO_CLIPBOARD =
  'TEXT_SUCCESSFULLY_COPIED_TO_CLIPBOARD' as const;
export const SNAPSHOT_PATH_COPIED_TO_CLIPBOARD =
  'SNAPSHOT_PATH_COPIED_TO_CLIPBOARD' as const;
export const QUICK_FIND_NOTIFICATION_SHOWED =
  'QUICK_FIND_NOTIFICATION_SHOWED' as const;
export const HANDOVER_GLOBAL_CHANGES_MADE_REMOTELY =
  'HANDOVER_GLOBAL_CHANGES_MADE_REMOTELY' as const;
export const ABLY_REALTIME_SESSION_INITIALIZED =
  'ABLY_REALTIME_SESSION_INITIALIZED' as const;
export const CMS_COLLECTION_SORT_ENDED = 'CMS_COLLECTION_SORT_ENDED' as const;
export const CMS_COLLECTION_SORT_ENDED_SUCCEEDED =
  'CMS_COLLECTION_SORT_ENDED_SUCCEEDED' as const;
export const NOTIFICATION_OPENED = 'NOTIFICATION_OPENED' as const;
export const GO_TO_ECOMMERCE_SETTINGS_CLICKED =
  'GO_TO_ECOMMERCE_SETTINGS_CLICKED' as const;
export const APPEARANCE_SETTINGS_OS_THEME_PREFERENCE_CHANGED =
  'APPEARANCE_SETTINGS_OS_THEME_PREFERENCE_CHANGED' as const;
export const APPEARANCE_SETTINGS_GLOBAL_THEME_SELECTION_CHANGED =
  'APPEARANCE_SETTINGS_GLOBAL_THEME_SELECTION_CHANGED' as const;
export const APPEARANCE_SETTINGS_GLOBAL_THEME_PREVIEWED =
  'APPEARANCE_SETTINGS_GLOBAL_THEME_PREVIEWED' as const;
export const APPEARANCE_SETTINGS_DARK_THEME_STYLE_CHANGED =
  'APPEARANCE_SETTINGS_DARK_THEME_STYLE_CHANGED' as const;
export const APPEARANCE_SETTINGS_THEME_SURFACE_OVERRIDES_CHANGED =
  'APPEARANCE_SETTINGS_THEME_SURFACE_OVERRIDES_CHANGED' as const;
export const APPEARANCE_SETTINGS_SELECTED_OPTION_CHANGED =
  'APPEARANCE_SETTINGS_SELECTED_OPTION_CHANGED' as const;
export const AUDIT_QUEUED = 'AUDIT_QUEUED' as const;
export const AUDIT_BEGAN = 'AUDIT_BEGAN' as const;
export const AUDIT_FINISHED = 'AUDIT_FINISHED' as const;
export const AUDIT_RESULT_WIZARD_OPENED = 'AUDIT_RESULT_WIZARD_OPENED' as const;
export const AUDIT_RESULT_WIZARD_CLOSED = 'AUDIT_RESULT_WIZARD_CLOSED' as const;
export const AUDIT_UI_GROUP_TOGGLED = 'AUDIT_UI_GROUP_TOGGLED' as const;
export const ALL_AUDIT_UI_GROUPS_TOGGLED =
  'ALL_AUDIT_UI_GROUPS_TOGGLED' as const;
export const PARTIAL_AUDIT_UI_GROUPS_TOGGLED =
  'PARTIAL_AUDIT_UI_GROUPS_TOGGLED' as const;
export const AUDIT_IGNORED = 'AUDIT_IGNORED';
export const AUDIT_READ = 'AUDIT_READ';
export const AUDIT_UNIGNORED = 'AUDIT_UNIGNORED';
export const AUDIT_VIEW_MODE_CHANGED = 'AUDIT_VIEW_MODE_CHANGED' as const;
export const ACTIVE_AUDIT_CHANGED = 'ACTIVE_AUDIT_CHANGED' as const;
export const ACTIVE_RECOMMENDATION_RULE_CHANGED =
  'ACTIVE_RECOMMENDATION_RULE_CHANGED' as const;
export const ALT_TEXT_GENERATION_STARTED =
  'ALT_TEXT_GENERATION_STARTED' as const;
export const ALT_TEXT_GENERATION_COMPLETED =
  'ALT_TEXT_GENERATION_COMPLETED' as const;
export const ALT_TEXT_GENERATION_CANCELLED =
  'ALT_TEXT_GENERATION_CANCELLED' as const;
export const LEGACY_GUEST_AI_BLOCKED_NOTIFICATION_REQUESTED =
  'LEGACY_GUEST_AI_BLOCKED_NOTIFICATION_REQUESTED' as const;
export const LEGACY_GUEST_CONVERSION_PICKER_VISIBILITY_CHANGED =
  'LEGACY_GUEST_CONVERSION_PICKER_VISIBILITY_CHANGED' as const;
export const DESIGNER_OPENED_IN_ANNOTATIONS_MODE =
  'DESIGNER_OPENED_IN_ANNOTATIONS_MODE' as const;
export const COPILOT_TOGGLED = 'COPILOT_TOGGLED' as const;
export const AIDA_SUCCESS_TOAST = 'AIDA_SUCCESS_TOAST' as const;
export const AIDA_ERROR_TOAST = 'AIDA_FAILURE_TOAST' as const;
export const AIDA_QUICK_ADD_SECTION = 'AIDA_QUICK_ADD_SECTION' as const;
export const AIDA_OPEN = 'AIDA_OPEN' as const;
export const AIDA_CLOSE = 'AIDA_CLOSE' as const;
export const ASSISTANT_OPENED = 'AI_ASSISTANT_OPENED' as const;
export const ASSISTANT_CLOSED = 'AI_ASSISTANT_CLOSED' as const;
export const ASSISTANT_INPUT_FOCUS_REQUESTED =
  'AI_ASSISTANT_INPUT_FOCUS_REQUESTED' as const;
export const ASSISTANT_INPUT_FOCUS_CLEARED =
  'AI_ASSISTANT_INPUT_FOCUS_CLEARED' as const;
export const ASSISTANT_TRIGGERED_POST_AISB =
  'AI_ASSISTANT_TRIGGERED_POST_AISB' as const;
export const ASSISTANT_SURFACE_MODE_CHANGED =
  'AI_ASSISTANT_SURFACE_MODE_CHANGED' as const;
export const ASSISTANT_DOCKED_SURFACE_WIDTH_CHANGED =
  'AI_ASSISTANT_DOCKED_SURFACE_WIDTH_CHANGED' as const;
export const GOOGLE_FONTS_FETCHED = 'GOOGLE_FONTS_FETCHED' as const;
export const CURRENT_COPILOT_REQUEST_ID = 'CURRENT_COPILOT_REQUEST_ID' as const;
export const LAST_COPILOT_REQUEST_ID = 'LAST_COPILOT_REQUEST_ID' as const;
export const SITE_CONTEXT_SYNCED = 'SITE_CONTEXT_SYNCED' as const;
export const SITE_CONTEXT_FETCHED = 'SITE_CONTEXT_FETCHED' as const;
export const SITE_LEVEL_INSTRUCTIONS_UPDATED =
  'SITE_LEVEL_INSTRUCTIONS_UPDATED' as const;
export const WHTML_PLAYGROUND_TOGGLED = 'WHTML_PLAYGROUND_TOGGLED' as const;
export const ANNOTATIONS_MODE_TOGGLED = 'ANNOTATIONS_MODE_TOGGLED' as const;
export const ANALYZE_MODE_TOGGLED = 'ANALYZE_MODE_TOGGLED' as const;
export const CAMPAIGN_CURRENT_ID_UPDATED =
  'CAMPAIGN_CURRENT_ID_UPDATED' as const;
export const CAMPAIGN_AD_ACTIVATION_UPDATED =
  'CAMPAIGN_AD_ACTIVATION_UPDATED' as const;
export const CAMPAIGN_BINDING_FTUX_STEP_CHANGED =
  'CAMPAIGN_BINDING_FTUX_STEP_CHANGED' as const;
export const CAMPAIGN_RECOMMENDATION_REVIEW_STARTED =
  'CAMPAIGN_RECOMMENDATION_REVIEW_STARTED' as const;
export const CAMPAIGN_RECOMMENDATION_REVIEW_CLEARED =
  'CAMPAIGN_RECOMMENDATION_REVIEW_CLEARED' as const;
export const CAMPAIGN_RECOMMENDATION_PREVIEW_STARTED =
  'CAMPAIGN_RECOMMENDATION_PREVIEW_STARTED' as const;
export const CAMPAIGN_RECOMMENDATION_PREVIEW_CANCELLED =
  'CAMPAIGN_RECOMMENDATION_PREVIEW_CANCELLED' as const;
export const CAMPAIGN_RECOMMENDATION_PREVIEW_COMMITTED =
  'CAMPAIGN_RECOMMENDATION_PREVIEW_COMMITTED' as const;
export const CAMPAIGN_REPORTING_TOGGLED = 'CAMPAIGN_REPORTING_TOGGLED' as const;
export const CAMPAIGN_REPORTING_OPENED = 'CAMPAIGN_REPORTING_OPENED' as const;
export const ANALYZE_PUBLISH_PROMO_VIEWED =
  'ANALYZE_PUBLISH_PROMO_VIEWED' as const;
export const ANALYZE_PUBLISH_PROMO_CLICKED =
  'ANALYZE_PUBLISH_PROMO_CLICKED' as const;
export const ANNOTATIONS_UPDATE_POSITION_REQUESTED =
  'ANNOTATIONS_UPDATE_POSITION_REQUESTED' as const;
export const ANNOTATIONS_UPDATE_POSITION_FAILED =
  'ANNOTATIONS_UPDATE_POSITION_FAILED' as const;
export const ANNOTATIONS_UPDATE_POSITION_SUCCEEDED =
  'ANNOTATIONS_UPDATE_POSITION_SUCCEEDED' as const;
export const ANNOTATIONS_ORPHAN_STATUS_VALIDATED =
  'ANNOTATIONS_ORPHAN_STATUS_VALIDATED' as const;
export const SYMBOL_ROOT_FIELD_DELETED = 'SYMBOL_ROOT_FIELD_DELETED' as const;
export const COMPONENT_ROOT_FIELDS_BULK_DELETED =
  'COMPONENT_ROOT_FIELDS_BULK_DELETED' as const;
export const SYMBOL_ROOT_FIELD_CHANGED = 'SYMBOL_ROOT_FIELD_CHANGED' as const;
export const SYMBOL_ROOT_FIELD_DEFAULT_CHANGED =
  'SYMBOL_ROOT_FIELD_DEFAULT_CHANGED' as const;
export const COMPONENT_PROPERTY_GROUP_DELETED =
  'COMPONENT_PROPERTY_GROUP_DELETED' as const;
export const COMPONENT_PROPERTY_GROUP_RENAMED =
  'COMPONENT_PROPERTY_GROUP_RENAMED' as const;
export const COMPONENT_PROPERTY_GROUPS_REORDERED =
  'COMPONENT_PROPERTY_GROUPS_REORDERED' as const;
export const COMPONENT_GROUP_RENAMED = 'COMPONENT_GROUP_RENAMED' as const;
export const COMPONENTS_UNGROUPED = 'COMPONENTS_UNGROUPED' as const;
export const COMPONENTS_VISIBILITY_BULK_CHANGED =
  'COMPONENTS_VISIBILITY_BULK_CHANGED' as const;
export const SYMBOL_ROOT_FIELD_ADDED_WITH_TYPE =
  'SYMBOL_ROOT_FIELD_ADDED_WITH_TYPE' as const;
export const SYMBOL_ROOT_FIELD_ADDED_WITH_TYPE_COMMITTED =
  'SYMBOL_ROOT_FIELD_ADDED_WITH_TYPE_COMMITTED' as const;
export const SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND =
  'SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND' as const;
export const SYMBOL_ROOT_FIELD_REORGANIZED =
  'SYMBOL_ROOT_FIELD_REORGANIZED' as const;
export const ANNOTATION_SAVE_REQUESTED = 'ANNOTATION_SAVE_REQUESTED' as const;
export const COMMERCE_ADVANCED_PRICE_FORMAT_SETTINGS_MODAL_OPEN_CLICKED =
  'COMMERCE_ADVANCED_PRICE_FORMAT_SETTINGS_MODAL_OPEN_CLICKED' as const;
export const COMMERCE_ADVANCED_PRICE_FORMAT_SETTINGS_MODAL_OPEN_CANCELED =
  'COMMERCE_ADVANCED_PRICE_FORMAT_SETTINGS_MODAL_OPEN_CANCELED' as const;
export const ANNOTATION_CANCELLED = 'ANNOTATION_CANCELLED' as const;
export const ANNOTATION_SAVE_SUCCEEDED = 'ANNOTATION_SAVE_SUCCEEDED' as const;
export const ANNOTATION_SAVE_FAILED = 'ANNOTATION_SAVE_FAILED' as const;
export const ANNOTATION_QUERY_PARAM_UPDATED =
  'ANNOTATION_QUERY_PARAM_UPDATED' as const;
export const SITE_USERS_LOADED = 'SITE_USERS_LOADED' as const;
export const COMMERCE_SETTINGS_ERRORS_UPDATED =
  'COMMERCE_SETTINGS_ERRORS_UPDATED' as const;
export const PUBLISH_BLOCKED = 'PUBLISH_BLOCKED' as const;
export const ANNOTATION_DELETE_FAILED = 'ANNOTATION_DELETE_FAILED' as const;
export const ANNOTATION_DELETE_SUCCEEDED =
  'ANNOTATION_DELETE_SUCCEEDED' as const;
export const ANNOTATION_DELETE_REQUESTED =
  'ANNOTATION_DELETE_REQUESTED' as const;
export const ANNOTATION_RESOLVE_UPDATE_REQUESTED =
  'ANNOTATION_RESOLVE_UPDATE_REQUESTED' as const;
export const ANNOTATION_RESOLVE_UPDATE_SUCCEEDED =
  'ANNOTATION_RESOLVE_UPDATE_SUCCEEDED' as const;
export const ANNOTATION_RESOLVE_UPDATE_FAILED =
  'ANNOTATION_RESOLVE_UPDATE_FAILED' as const;
export const ANNOTATION_EDIT_STARTED = 'ANNOTATION_EDIT_STARTED' as const;
export const ANNOTATION_EDIT_STOPPED = 'ANNOTATION_EDIT_STOPPED' as const;
export const ANNOTATION_REACTION_ADDED = 'ANNOTATION_REACTION_ADDED' as const;
export const ANNOTATION_REACTION_REMOVED =
  'ANNOTATION_REACTION_REMOVED' as const;
export const ANNOTATION_REACTIONS_LOADED =
  'ANNOTATION_REACTIONS_LOADED' as const;
export const ANNOTATION_REPLY_SAVE_DEQUEUED =
  'ANNOTATION_REPLY_SAVE_DEQUEUED' as const;
export const ANNOTATION_REPLY_SAVE_ENQUEUED =
  'ANNOTATION_REPLY_SAVE_ENQUEUED' as const;
export const ANNOTATION_REPLY_SAVE_REQUESTED =
  'ANNOTATION_REPLY_SAVE_REQUESTED' as const;
export const ANNOTATION_REPLY_SAVE_SUCCEEDED =
  'ANNOTATION_REPLY_SAVE_SUCCEEDED' as const;
export const ANNOTATION_REPLY_SAVE_FAILED =
  'ANNOTATION_REPLY_SAVE_FAILED' as const;
export const ANNOTATION_REPLY_DELETE_REQUESTED =
  'ANNOTATION_REPLY_DELETE_REQUESTED' as const;
export const ANNOTATION_REPLY_DELETE_SUCCEEDED =
  'ANNOTATION_REPLY_DELETE_SUCCEEDED' as const;
export const ANNOTATION_REPLY_DELETE_FAILED =
  'ANNOTATION_REPLY_DELETE_FAILED' as const;
export const ANNOTATION_REPLY_UPDATE_REQUESTED =
  'ANNOTATION_REPLY_UPDATE_REQUESTED' as const;
export const ANNOTATION_REPLY_UPDATE_SUCCEEDED =
  'ANNOTATION_REPLY_UPDATE_SUCCEEDED' as const;
export const ANNOTATION_REPLY_UPDATE_FAILED =
  'ANNOTATION_REPLY_UPDATE_FAILED' as const;
export const ANNOTATIONS_FILTER_UPDATED = 'ANNOTATIONS_FILTER_UPDATED' as const;
export const ANNOTATION_ITEM_HOVERED = 'ANNOTATION_ITEM_HOVERED' as const;
export const ANNOTATION_ONBOARDING_POPOVER_SHOWN =
  'ANNOTATION_ONBOARDING_POPOVER_SHOWN' as const;
export const ANNOTATION_METADATA_VIEWED = 'ANNOTATION_METADATA_VIEWED' as const;
export const ANNOTATION_PREVIEW_HOVERED = 'ANNOTATION_PREVIEW_HOVERED' as const;
export const COMMERCE_DISCOUNTS_DELETE_FAILED =
  'COMMERCE_DISCOUNTS_DELETE_FAILED' as const;
export const COMMERCE_DISCOUNTS_DELETE_STARTED =
  'COMMERCE_DISCOUNTS_DELETE_STARTED' as const;
export const COMMERCE_DISCOUNTS_DELETE_SUCCEEDED =
  'COMMERCE_DISCOUNTS_DELETE_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_BULK_DELETE_FAILED =
  'COMMERCE_DISCOUNTS_BULK_DELETE_FAILED' as const;
export const COMMERCE_DISCOUNTS_BULK_DELETE_STARTED =
  'COMMERCE_DISCOUNTS_BULK_DELETE_STARTED' as const;
export const COMMERCE_DISCOUNTS_BULK_UPDATE_STARTED =
  'COMMERCE_DISCOUNTS_BULK_UPDATE_STARTED' as const;
export const COMMERCE_DISCOUNTS_BULK_UPDATE_SUCCEEDED =
  'COMMERCE_DISCOUNTS_BULK_UPDATE_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_BULK_UPDATE_FAILED =
  'COMMERCE_DISCOUNTS_BULK_UPDATE_FAILED' as const;
export const COMMERCE_DISCOUNTS_BULK_DELETE_SUCCEEDED =
  'COMMERCE_DISCOUNTS_BULK_DELETE_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_FETCH_SUCCEEDED =
  'COMMERCE_DISCOUNTS_FETCH_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_FETCH_FAILED =
  'COMMERCE_DISCOUNTS_FETCH_FAILED' as const;
export const COMMERCE_DISCOUNTS_CREATE_STARTED =
  'COMMERCE_DISCOUNTS_CREATE_STARTED' as const;
export const COMMERCE_DISCOUNTS_CREATE_SUCCEEDED =
  'COMMERCE_DISCOUNTS_CREATE_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_CREATE_FAILED =
  'COMMERCE_DISCOUNTS_CREATE_FAILED' as const;
export const COMMERCE_DISCOUNTS_UPDATE_STARTED =
  'COMMERCE_DISCOUNTS_UPDATE_STARTED' as const;
export const COMMERCE_DISCOUNTS_UPDATE_SUCCEEDED =
  'COMMERCE_DISCOUNTS_UPDATE_SUCCEEDED' as const;
export const COMMERCE_DISCOUNTS_UPDATE_FAILED =
  'COMMERCE_DISCOUNTS_UPDATE_FAILED' as const;
export const COMMERCE_DISCOUNT_SELECTED = 'COMMERCE_DISCOUNT_SELECTED' as const;
export const COMMERCE_CURRENT_DISCOUNT_MODIFIED =
  'COMMERCE_CURRENT_DISCOUNT_MODIFIED' as const;
export const COMMERCE_CURRENT_DISCOUNT_DISABLE_BUTTON_CLICKED =
  'COMMERCE_CURRENT_DISCOUNT_DISABLE_BUTTON_CLICKED' as const;
export const COMMERCE_CURRENT_DISCOUNT_ENABLE_BUTTON_CLICKED =
  'COMMERCE_CURRENT_DISCOUNT_ENABLE_BUTTON_CLICKED' as const;
export const COMMERCE_CURRENT_DISCOUNT_ENABLE_FAILED =
  'COMMERCE_CURRENT_DISCOUNT_ENABLE_FAILED' as const;
export const COMMERCE_CURRENT_DISCOUNT_DISABLE_FAILED =
  'COMMERCE_CURRENT_DISCOUNT_DISABLE_FAILED' as const;
export const COMMERCE_CURRENT_DISCOUNT_ENABLE_SUCCEEDED =
  'COMMERCE_CURRENT_DISCOUNT_ENABLE_SUCCEEDED' as const;
export const COMMERCE_CURRENT_DISCOUNT_DISABLE_SUCCEEDED =
  'COMMERCE_CURRENT_DISCOUNT_DISABLE_SUCCEEDED' as const;
export const RESTORE_VERSION_MODAL_OPENED =
  'RESTORE_VERSION_MODAL_OPENED' as const;
export const RESTORE_VERSION_MODAL_CANCELED =
  'RESTORE_VERSION_MODAL_CANCELED' as const;
export const ANALYTICS_READY = 'ANALYTICS_READY' as const;
export const ASSET_TAB_FULL_VIEW_OPENED = 'ASSET_TAB_FULL_VIEW_OPENED' as const;
export const ASSET_TAB_SIDE_VIEW_OPENED = 'ASSET_TAB_SIDE_VIEW_OPENED' as const;
export const __ANALYTICS__ASSETS_BULK_SELECTED =
  '__ANALYTICS__ASSETS_BULK_SELECTED' as const;
export const COMMERCE_CREATE_DISCOUNTS_FORM_SUBMITTED =
  'COMMERCE_CREATE_DISCOUNTS_FORM_SUBMITTED' as const;
export const COMMERCE_UPDATE_DISCOUNTS_FORM_SUBMITTED =
  'COMMERCE_UPDATE_DISCOUNTS_FORM_SUBMITTED' as const;
export const ACTIVITY_LOG_ITEM_CLICKED = 'ACTIVITY_LOG_ITEM_CLICKED' as const;
export const ACTIVITY_LOG_DAY_EXPANDED = 'ACTIVITY_LOG_DAY_EXPANDED' as const;
export const ACTIVITY_LOG_MASTER_EXPANDED =
  'ACTIVITY_LOG_MASTER_EXPANDED' as const;
export const ACTIVITY_LOG_MERGE_DETAILS_REQUESTED =
  'ACTIVITY_LOG_MERGE_DETAILS_REQUESTED' as const;
export const ACTIVITY_LOG_MERGE_DETAILS_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_MERGE_DETAILS_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_MERGE_DETAILS_FETCH_FAILED =
  'ACTIVITY_LOG_MERGE_DETAILS_FETCH_FAILED' as const;
export const ACTIVITY_LOG_USER_LIST_REQUESTED =
  'ACTIVITY_LOG_USER_LIST_REQUESTED' as const;
export const ACTIVITY_LOG_FULL_USER_LIST_REQUESTED =
  'ACTIVITY_LOG_FULL_USER_LIST_REQUESTED' as const;
export const ACTIVITY_LOG_USER_LIST_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_USER_LIST_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_FULL_USER_LIST_FETCH_SUCCEEDED =
  'ACTIVITY_LOG_FULL_USER_LIST_FETCH_SUCCEEDED' as const;
export const ACTIVITY_LOG_USER_LIST_FETCH_FAILED =
  'ACTIVITY_LOG_USER_LIST_FETCH_FAILED' as const;
export const ACTIVITY_LOG_FILTERED_RESULTS_FETCHED =
  'ACTIVITY_LOG_FILTERED_RESULTS_FETCHED' as const;
export const ACTIVITY_LOG_FILTERS_CHECKED =
  'ACTIVITY_LOG_FILTERS_CHECKED' as const;
export const ACTIVITY_LOG_FILTERS_RESET = 'ACTIVITY_LOG_FILTERS_RESET' as const;
export const ACTIVITY_LOG_CMS_ITEM_ID_CHANGED =
  'ACTIVITY_LOG_CMS_ITEM_ID_CHANGED' as const;
export const ACTIVITY_LOG_PAGINATION_BUTTON_CLICKED =
  'ACTIVITY_LOG_PAGINATION_BUTTON_CLICKED' as const;
export const ACTIVITY_LOG_ITEM_LINK_CLICKED =
  'ACTIVITY_LOG_ITEM_LINK_CLICKED' as const;
export const ASSET_MANAGER_BULK_MOVE_ASSETS_REQUESTED =
  'ASSET_MANAGER_BULK_MOVE_ASSETS_REQUESTED' as const;
export const ASSET_MANAGER_BULK_MOVE_ASSETS_SUCCEEDED =
  'ASSET_MANAGER_BULK_MOVE_ASSETS_SUCCEEDED' as const;
export const ASSET_MANAGER_BULK_MOVE_ASSETS_FAILED =
  'ASSET_MANAGER_BULK_MOVE_ASSETS_FAILED' as const;
export const ASSET_BULK_ASSETS_UNREGISTER_REQUESTED =
  'ASSET_BULK_ASSETS_UNREGISTER_REQUESTED' as const;
export const ASSET_BULK_ASSETS_UNREGISTER_SUCCEEDED =
  'ASSET_BULK_ASSETS_UNREGISTER_SUCCEEDED' as const;
export const ASSET_BULK_ASSETS_UNREGISTER_FAILED =
  'ASSET_BULK_ASSETS_UNREGISTER_FAILED' as const;
export const ASSET_BULK_ASSETS_COMPRESSION_REQUESTED =
  'ASSET_BULK_ASSETS_COMPRESSION_REQUESTED' as const;
export const ASSET_BULK_ASSETS_COMPRESSION_FINISHED =
  'ASSET_BULK_ASSETS_COMPRESSION_FINISHED' as const;
export const ASSET_BULK_ASSETS_COMPRESSION_FAILED =
  'ASSET_BULK_ASSETS_COMPRESSION_FAILED' as const;
export const COMMERCE_PRODUCT_TYPE_CHANGED =
  'COMMERCE_PRODUCT_TYPE_CHANGED' as const;
export const PAYWALL_ENCOUNTERED = 'PAYWALL_ENCOUNTERED' as const;
export const COMMERCE_PRODUCT_SETTINGS_PRODUCT_TYPE_UPDATED =
  'COMMERCE_PRODUCT_SETTINGS_PRODUCT_TYPE_UPDATED' as const;
export const LOCATION_CLICKED = 'LOCATION_CLICKED' as const;
export const COLOR_FILTER_APPLIED = 'COLOR_FILTER_APPLIED' as const;
export const REFRESH_PROJECT_DIALOG = 'REFRESH_PROJECT_DIALOG' as const;
export const REFRESH_CMS_COLLECTIONS_AND_ITEMS =
  'REFRESH_CMS_COLLECTIONS_AND_ITEMS' as const;
export const TEXT_ZOOM_EMULATED = 'TEXT_ZOOM_EMULATED' as const;
export const DEVLINK_CONNECTION_FAILURE = 'DEVLINK_CONNECTION_FAILURE' as const;
export const DEVLINK_SLOT_MIGRATED_TO_SLOT_CONTENT =
  'DEVLINK_SLOT_MIGRATED_TO_SLOT_CONTENT' as const;
export const LIBRARIES_MARKETPLACE_CTA_CLICKED =
  'LIBRARIES_MARKETPLACE_CTA_CLICKED' as const;
export const DRAGGING_VIDEO_TUTORIAL_STARTED =
  'DRAGGING_VIDEO_TUTORIAL_STARTED' as const;
export const DRAGGING_VIDEO_TUTORIAL_ENDED =
  'DRAGGING_VIDEO_TUTORIAL_ENDED' as const;
export const RESIZING_VIDEO_TUTORIAL_STARTED =
  'RESIZING_VIDEO_TUTORIAL_STARTED' as const;
export const RESIZING_VIDEO_TUTORIAL_ENDED =
  'RESIZING_VIDEO_TUTORIAL_ENDED' as const;
export const PUBLISH_LIBRARY_REQUESTED = 'PUBLISH_LIBRARY_REQUESTED' as const;
export const PUBLISH_LIBRARY_QUEUED = 'PUBLISH_LIBRARY_QUEUED' as const;
export const PUBLISH_LIBRARY_UPDATED = 'PUBLISH_LIBRARY_UPDATED' as const;
export const PUBLISH_SITE_LIBRARY_QUEUED =
  'PUBLISH_SITE_LIBRARY_QUEUED' as const;
export const PUBLISH_SITE_LIBRARY_UPDATED =
  'PUBLISH_SITE_LIBRARY_UPDATED' as const;
export const RESET_LIBRARY_PUBLISHING_STATUS =
  'RESET_LIBRARY_PUBLISHING_STATUS' as const;
export const LIBRARY_NAME_CHANGED = 'LIBRARY_NAME_CHANGED' as const;
export const SELECTED_LAYOUT_MEASUREMENTS_UPDATED =
  'SELECTED_LAYOUT_MEASUREMENTS_UPDATED' as const;
export const MATCHMAKING_NOTE_CTA_CLICKED =
  'MATCHMAKING_NOTE_CTA_CLICKED' as const;
export const MATCHMAKING_NOTE_DISMISSED = 'MATCHMAKING_NOTE_DISMISSED' as const;
export const ON_CANVAS_CONTROLS_PANEL_OPENED =
  'ON_CANVAS_CONTROLS_PANEL_OPENED' as const;
export const ON_CANVAS_CONTROLS_PANEL_CLOSED =
  'ON_CANVAS_CONTROLS_PANEL_CLOSED' as const;
export const DYN_FIELD_SAVE_ON_CANVAS_STARTED =
  'DYN_FIELD_SAVE_ON_CANVAS_STARTED' as const;
export const PARENT_STYLE_BLOCK_STATE_CHANGED =
  'PARENT_STYLE_BLOCK_STATE_CHANGED' as const;
export const ELEMENT_LOCAL_STYLES_UPDATED =
  'ELEMENT_LOCAL_STYLES_UPDATED' as const;
export const POST_MESSAGE_RECEIVED = 'POST_MESSAGE_RECEIVED' as const;
export const POST_MESSAGES_SENT = 'POST_MESSAGES_SENT' as const;
export const RELOAD_DEV_EXTENSION = 'RELOAD_DEV_EXTENSION' as const;
export const ANNOTATION_UPDATE_READ_STATUS_REQUESTED =
  'ANNOTATION_UPDATE_READ_STATUS_REQUESTED' as const;
export const ANNOTATION_UPDATE_READ_STATUS_SUCCEEDED =
  'ANNOTATION_UPDATE_READ_STATUS_SUCCEEDED' as const;
export const ANNOTATION_UPDATE_READ_STATUS_FAILED =
  'ANNOTATION_UPDATE_READ_STATUS_FAILED' as const;
export const TOGGLE_ANNOTATION_SELECTION =
  'TOGGLE_ANNOTATION_SELECTION' as const;
export const ANNOTATIONS_LOAD_REQUESTED = 'ANNOTATIONS_LOAD_REQUESTED' as const;
export const ANNOTATIONS_LOAD_FAILED = 'ANNOTATIONS_LOAD_FAILED' as const;
export const ANNOTATIONS_LOAD_SUCCEEDED = 'ANNOTATIONS_LOAD_SUCCEEDED' as const;
export const LAYOUT_PRESET_POPOVER_DISMISSED =
  'LAYOUT_PRESET_POPOVER_DISMISSED' as const;
export const ANNOTATIONS_NOTIFICATIONS_LOAD_REQUESTED =
  'ANNOTATIONS_NOTIFICATIONS_LOAD_REQUESTED' as const;
export const ANNOTATIONS_NOTIFICATIONS_LOAD_FAILED =
  'ANNOTATIONS_NOTIFICATIONS_LOAD_FAILED' as const;
export const ANNOTATION_TOGGLE_FILTER_DROPDOWN =
  'ANNOTATION_TOGGLE_FILTER_DROPDOWN' as const;
export const ANNOTATIONS_NOTIFICATIONS_LOAD_SUCCEEDED =
  'ANNOTATIONS_NOTIFICATIONS_LOAD_SUCCEEDED' as const;
export const ANNOTATIONS_NOTIFICATIONS_UPDATE_REQUESTED =
  'ANNOTATIONS_NOTIFICATIONS_UPDATE_REQUESTED' as const;
export const ANNOTATIONS_NOTIFICATIONS_UPDATE_FAILED =
  'ANNOTATIONS_NOTIFICATIONS_UPDATE_FAILED' as const;
export const ANNOTATIONS_NOTIFICATIONS_UPDATE_SUCCEEDED =
  'ANNOTATIONS_NOTIFICATIONS_UPDATE_SUCCEEDED' as const;
export const ANNOTATION_OVERLAY_TOGGLED = 'ANNOTATION_OVERLAY_TOGGLED' as const;
export const MERGE_CELL_WARNING_MODAL_OPENED =
  'MERGE_CELL_WARNING_MODAL_OPENED' as const;
export const MERGE_CELL_WARNING_MODAL_CLOSED =
  'MERGE_CELL_WARNING_MODAL_CLOSED' as const;
export const SITE_PERMISSIONS_LOAD_SUCCESS =
  'SITE_PERMISSIONS_LOAD_SUCCESS' as const;
export const STYLE_VARIABLE_CREATED = 'STYLE_VARIABLE_CREATED' as const;
export const STYLE_VARIABLE_CREATED_FAILED =
  'STYLE_VARIABLE_CREATED_FAILED' as const;
export const STYLE_VARIABLE_CREATED_SUCCEEDED =
  'STYLE_VARIABLE_CREATED_SUCCEEDED' as const;
export const STYLE_VARIABLE_UPDATED = 'STYLE_VARIABLE_UPDATED' as const;
export const STYLE_VARIABLES_GROUP_UPDATED =
  'STYLE_VARIABLES_GROUP_UPDATED' as const;
export const STYLE_VARIABLES_EPHEMERALLY_UPDATED =
  'STYLE_VARIABLES_EPHEMERALLY_UPDATED' as const;
export const STYLE_VARIABLE_EPHEMERAL_UPDATE_CANCELLED =
  'STYLE_VARIABLE_EPHEMERAL_UPDATE_CANCELLED' as const;
export const STYLE_VARIABLE_UPDATED_FAILED =
  'STYLE_VARIABLE_UPDATED_FAILED' as const;
export const STYLE_VARIABLE_UPDATED_SUCCEEDED =
  'STYLE_VARIABLE_UPDATED_SUCCEEDED' as const;
export const STYLE_VARIABLES_BULK_UPDATED =
  'STYLE_VARIABLES_BULK_UPDATED' as const;
export const STYLE_VARIABLE_BULK_UPDATE_SUCCEEDED =
  'STYLE_VARIABLE_BULK_UPDATE_SUCCEEDED' as const;
export const STYLE_VARIABLE_BULK_UPDATE_FAILED =
  'STYLE_VARIABLE_BULK_UPDATE_FAILED' as const;
export const STYLE_VARIABLE_DELETED = 'STYLE_VARIABLE_DELETED' as const;
export const STYLE_VARIABLE_DELETED_FAILED =
  'STYLE_VARIABLE_DELETED_FAILED' as const;
export const STYLE_VARIABLE_DELETED_SUCCEEDED =
  'STYLE_VARIABLE_DELETED_SUCCEEDED' as const;
export const STYLE_VARIABLES_LOADED = 'STYLE_VARIABLES_LOADED' as const;
export const STYLE_VARIABLES_BULK_SAVE_ALL =
  'STYLE_VARIABLES_BULK_SAVE_ALL' as const;
export const STYLE_VARIABLES_BULK_SAVE_ALL_FAILED =
  'STYLE_VARIABLES_BULK_SAVE_ALL_FAILED' as const;
export const STYLE_VARIABLES_BULK_SAVE_ALL_SUCCESS =
  'STYLE_VARIABLES_BULK_SAVE_ALL_SUCCESS' as const;
export const EDIT_MULTIPLE_INSTANCES_CONFIRM_MODAL_OPENED =
  'EDIT_MULTIPLE_INSTANCES_CONFIRM_MODAL_OPENED' as const;
export const EDIT_MULTIPLE_INSTANCES_CONFIRM_MODAL_CLOSED =
  'EDIT_MULTIPLE_INSTANCES_CONFIRM_MODAL_CLOSED' as const;
export const STYLE_VARIABLE_BOUND_EVENT_TRACKED =
  'STYLE_VARIABLE_BOUND_EVENT_TRACKED' as const;
export const STYLE_VARIABLE_BINDING_MENU_TOGGLED_EVENT_TRACKED =
  'STYLE_VARIABLE_BINDING_MENU_TOGGLED_EVENT_TRACKED' as const;

export const STYLE_VARIABLE_NEW_VARIABLE_COLLECTION_CLICK_EVENT_TRACKED =
  'STYLE_VARIABLE_NEW_VARIABLE_COLLECTION_CLICK_EVENT_TRACKED' as const;
export const STYLE_VARIABLE_NEW_VARIABLE_MODE_CLICK_EVENT_TRACKED =
  'STYLE_VARIABLE_NEW_VARIABLE_MODE_CLICK_EVENT_TRACKED' as const;

export const STYLE_VARIABLES_GROUP_DUPLICATED =
  'STYLE_VARIABLES_GROUP_DUPLICATED' as const;
export const STYLE_VARIABLES_REORDERED = 'STYLE_VARIABLES_REORDERED' as const;
export const STYLE_VARIABLE_GROUP_REORDERED =
  'STYLE_VARIABLE_GROUP_REORDERED' as const;
export const STYLE_VARIABLE_RENDERED_PREVIEW_COPIED =
  'STYLE_VARIABLE_RENDERED_PREVIEW_COPIED' as const;
export const STYLE_VARIABLE_RENDERED_PREVIEW_COPY_FAILED =
  'STYLE_VARIABLE_RENDERED_PREVIEW_COPY_FAILED' as const;
export const STYLE_VARIABLE_REORDER_STARTED_EVENT_TRACKED =
  'STYLE_VARIABLE_REORDER_STARTED_EVENT_TRACKED' as const;
export const STYLE_VARIABLES_SELECTED_EVENT_TRACKED =
  'STYLE_VARIABLES_SELECTED_EVENT_TRACKED' as const;

// Variable Mode actions
export const STYLE_VARIABLE_MODE_CREATED =
  'STYLE_VARIABLE_MODE_CREATED' as const;
export const STYLE_VARIABLE_MODE_CONFIG_UPDATED =
  'STYLE_VARIABLE_MODE_CONFIG_UPDATED' as const;
export const STYLE_VARIABLE_MODE_VALUE_UPDATED =
  'STYLE_VARIABLE_MODE_VALUE_UPDATED' as const;
export const STYLE_VARIABLE_MODE_DELETED =
  'STYLE_VARIABLE_MODE_DELETED' as const;
export const STYLE_VARIABLE_MODE_RESTORED =
  'STYLE_VARIABLE_MODE_RESTORED' as const;
export const STYLE_VARIABLE_MODE_DELETED_FAILED =
  'STYLE_VARIABLE_MODE_DELETED_FAILED' as const;
export const STYLE_VARIABLE_MODE_DELETED_SUCCEEDED =
  'STYLE_VARIABLE_MODE_DELETED_SUCCEEDED' as const;
export const STYLE_VARIABLE_MODE_REORDERED =
  'STYLE_VARIABLE_MODE_REORDERED' as const;

// Variable Collection actions
export const STYLE_VARIABLE_COLLECTION_CREATED =
  'STYLE_VARIABLE_COLLECTION_CREATED' as const;
export const STYLE_VARIABLE_COLLECTION_CREATED_FAILED =
  'STYLE_VARIABLE_COLLECTION_CREATED_FAILED' as const;
export const STYLE_VARIABLE_COLLECTION_CREATED_SUCCEEDED =
  'STYLE_VARIABLE_COLLECTION_CREATED_SUCCEEDED' as const;
export const STYLE_VARIABLE_COLLECTION_UPDATED =
  'STYLE_VARIABLE_COLLECTION_UPDATED' as const;
export const STYLE_VARIABLE_COLLECTION_UPDATED_FAILED =
  'STYLE_VARIABLE_COLLECTION_UPDATED_FAILED' as const;
export const STYLE_VARIABLE_COLLECTION_UPDATED_SUCCEEDED =
  'STYLE_VARIABLE_COLLECTION_UPDATED_SUCCEEDED' as const;
export const STYLE_VARIABLE_COLLECTION_DELETED =
  'STYLE_VARIABLE_COLLECTION_DELETED' as const;
export const STYLE_VARIABLE_COLLECTION_DELETED_FAILED =
  'STYLE_VARIABLE_COLLECTION_DELETED_FAILED' as const;
export const STYLE_VARIABLE_COLLECTION_DELETED_SUCCEEDED =
  'STYLE_VARIABLE_COLLECTION_DELETED_SUCCEEDED' as const;

export const STYLE_VARIABLE_COLLECTION_REORDERED =
  'STYLE_VARIABLE_COLLECTION_REORDERED' as const;
export const STYLE_VARIABLES_CROSS_COLLECTION_MOVED =
  'STYLE_VARIABLES_CROSS_COLLECTION_MOVED' as const;

export const USER_ID_TOKEN_FETCH = 'USER_ID_TOKEN_FETCH' as const;
export const CODE_BLOCK_RESET = 'CODE_BLOCK_RESET' as const;
export const CODE_BLOCK_OPEN_EDITOR = 'CODE_BLOCK_OPEN_EDITOR' as const;
export const CODE_BLOCK_CLOSE_EDITOR = 'CODE_BLOCK_CLOSE_EDITOR' as const;
export const CODE_BLOCK_SAVE_EDITOR = 'CODE_BLOCK_SAVE_EDITOR' as const;
export const CODE_BLOCK_SAVE_AND_CLOSE_EDITOR =
  'CODE_BLOCK_SAVE_AND_CLOSE_EDITOR' as const;
export const CODE_BLOCK_TEXT_CHANGED = 'CODE_BLOCK_TEXT_CHANGED' as const;
export const CODE_BLOCK_LANGUAGE_CHANGED =
  'CODE_BLOCK_LANGUAGE_CHANGED' as const;
export const SAFARI_17_2_DETECTED = 'SAFARI_17_2_DETECTED' as const;
export const MARKETPLACE_APPS_FAILED = 'MARKETPLACE_APPS_FAILED' as const;
export const MARKETPLACE_APPS_SUCCEEDED = 'MARKETPLACE_APPS_SUCCEEDED' as const;
export const MARKETPLACE_APPS_SEARCH_REQUESTED =
  'MARKETPLACE_APPS_SEARCH_REQUESTED' as const;
export const MARKETPLACE_APPS_SEARCH_FAILED =
  'MARKETPLACE_APPS_SEARCH_FAILED' as const;
export const MARKETPLACE_APPS_SEARCH_SUCCEEDED =
  'MARKETPLACE_APPS_SEARCH_SUCCEEDED' as const;
export const MARKETPLACE_APP_DETAILS_REQUESTED =
  'MARKETPLACE_APP_DETAILS_REQUESTED' as const;
export const MARKETPLACE_APP_DETAILS_FAILED =
  'MARKETPLACE_APP_DETAILS_FAILED' as const;
export const MARKETPLACE_APP_DETAILS_SUCCEEDED =
  'MARKETPLACE_APP_DETAILS_SUCCEEDED' as const;
export const NEW_AND_NOTEWORTHY_APPS_LOAD_REQUESTED =
  'NEW_AND_NOTEWORTHY_APPS_LOAD_REQUESTED' as const;
export const NEW_AND_NOTEWORTHY_APPS_LOAD_SUCCEEDED =
  'NEW_AND_NOTEWORTHY_APPS_LOAD_SUCCEEDED' as const;
export const NEW_AND_NOTEWORTHY_APPS_LOAD_FAILED =
  'NEW_AND_NOTEWORTHY_APPS_LOAD_FAILED' as const;
export const APP_SUBSCRIPTION_FETCH = 'APP_SUBSCRIPTION_FETCH' as const;
export const NINE_BOX_FLEX_CLICKED = 'NINE_BOX_FLEX_CLICKED' as const;
export const NINE_BOX_GRID_CLICKED = 'NINE_BOX_GRID_CLICKED' as const;
export const INSTALL_MARKETPLACE_APP_REQUESTED =
  'INSTALL_MARKETPLACE_APP_REQUESTED' as const;
export const INSTALL_MARKETPLACE_APP_FAILED =
  'INSTALL_MARKETPLACE_APP_FAILED' as const;
export const INSTALL_MARKETPLACE_APP_SUCCEEDED =
  'INSTALL_MARKETPLACE_APP_SUCCEEDED' as const;
export const MARKETPLACE_APP_LIKE_REQUESTED =
  'MARKETPLACE_APP_LIKE_REQUESTED' as const;
export const MARKETPLACE_APP_LIKE_SUCCEEDED =
  'MARKETPLACE_APP_LIKE_SUCCEEDED' as const;
export const MARKETPLACE_APP_LIKE_FAILED =
  'MARKETPLACE_APP_LIKE_FAILED' as const;
export const RESET_APP_INSTALL_REDIRECT_URL =
  'RESET_APP_INSTALL_REDIRECT_URL' as const;
export const OAUTH_MARKETPLACE_APP_AUTHENTICATED =
  'OAUTH_MARKETPLACE_APP_AUTHENTICATED' as const;
export const FETCH_INSTALLED_APPS_FAILED =
  'FETCH_INSTALLED_APPS_FAILED' as const;
export const FETCH_INSTALLED_APPS_SUCCEEDED =
  'FETCH_INSTALLED_APPS_SUCCEEDED' as const;
export const CONTENT_EDITOR_VALIDATION_ERROR_SHOWN =
  'CONTENT_EDITOR_VALIDATION_ERROR_SHOWN' as const;
export const CONTENT_EDITOR_DISCARD_MODAL_OPENED =
  'CONTENT_EDITOR_DISCARD_MODAL_OPENED' as const;
export const CONTENT_EDITOR_DISCARD_MODAL_CLOSED =
  'CONTENT_EDITOR_DISCARD_MODAL_CLOSED' as const;
export const CUSTOM_CODE_SITE_APP_PUT_SUCCEEDED =
  'CUSTOM_CODE_SITE_APP_PUT_SUCCEEDED' as const;
export const CUSTOM_CODE_SITE_APP_PUT_FAILED =
  'CUSTOM_CODE_SITE_APP_PUT_FAILED' as const;
export const CUSTOM_CODE_SITE_APP_SUCCEEDED =
  'CUSTOM_CODE_SITE_APP_SUCCEEDED' as const;
export const CUSTOM_CODE_SITE_APP_FAILED =
  'CUSTOM_CODE_SITE_APP_FAILED' as const;
export const CUSTOM_CODE_PAGE_APP_SUCCEEDED =
  'CUSTOM_CODE_PAGE_APP_SUCCEEDED' as const;
export const CUSTOM_CODE_PAGE_APP_FAILED =
  'CUSTOM_CODE_PAGE_APP_FAILED' as const;
export const CUSTOM_CODE_PAGE_APP_PUT_SUCCEEDED =
  'CUSTOM_CODE_PAGE_APP_PUT_SUCCEEDED' as const;
export const CUSTOM_CODE_PAGE_APP_PUT_FAILED =
  'CUSTOM_CODE_PAGE_APP_PUT_FAILED' as const;
export const CUSTOM_CODE_PREVIEW_TOGGLED =
  'CUSTOM_CODE_PREVIEW_TOGGLED' as const;
export const CUSTOM_CODE_PREVIEWED_FOR_FIRST_TIME =
  'CUSTOM_CODE_PREVIEWED_FOR_FIRST_TIME' as const;
export const EDIT_CUSTOM_CODE_EVENT_TRACKED =
  'EDIT_CUSTOM_CODE_EVENT_TRACKED' as const;
export const SAVE_CUSTOM_CODE_EVENT_TRACKED =
  'SAVE_CUSTOM_CODE_EVENT_TRACKED' as const;
export const CUSTOM_CODE_TOGGLE_EVENT_TRACKED =
  'CUSTOM_CODE_TOGGLE_EVENT_TRACKED' as const;
export const CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP =
  'CUSTOM_CODE_ERROR_LOADING_SCRIPT_REPORTED_IN_CANVAS_APP' as const;
export const ASSET_ALT_TEXT_CHANGE_STARTED =
  'ASSET_ALT_TEXT_CHANGE_STARTED' as const;
export const ASSET_ALT_TEXT_CHANGE_SUCCEEDED =
  'ASSET_ALT_TEXT_CHANGE_SUCCEEDED' as const;
export const ASSET_ALT_TEXT_CHANGE_FAILED =
  'ASSET_ALT_TEXT_CHANGE_FAILED' as const;
export const ASSET_BULK_ALT_TEXT_UPDATED_LOCALLY =
  'ASSET_BULK_ALT_TEXT_UPDATED_LOCALLY' as const;
export const ASSET_DETAILS_UPDATE_STARTED =
  'ASSET_DETAILS_UPDATE_STARTED' as const;
export const ASSET_DETAILS_UPDATE_SUCCEEDED =
  'ASSET_DETAILS_UPDATE_SUCCEEDED' as const;
export const ASSET_DETAILS_UPDATED_REMOTELY =
  'ASSET_DETAILS_UPDATED_REMOTELY' as const;
export const ASSET_DETAILS_UPDATE_FAILED =
  'ASSET_DETAILS_UPDATE_FAILED' as const;
export const REVIEW_REQUEST_REQUESTED = 'REVIEW_REQUEST_REQUESTED' as const;
export const REVIEW_REQUEST_SUCCEEDED = 'REVIEW_REQUEST_SUCCEEDED' as const;
export const REVIEW_REQUEST_FAILED = 'REVIEW_REQUEST_FAILED' as const;
export const REVIEW_RESEND_REQUESTED = 'REVIEW_RESEND_REQUESTED' as const;
export const REVIEW_RESEND_SUCCEEDED = 'REVIEW_RESEND_SUCCEEDED' as const;
export const REVIEW_RESEND_FAILED = 'REVIEW_RESEND_FAILED' as const;
export const REVIEW_APPROVE_REQUESTED = 'REVIEW_APPROVE_REQUESTED' as const;
export const REVIEW_APPROVE_SUCCEEDED = 'REVIEW_APPROVE_SUCCEEDED' as const;
export const REVIEW_APPROVE_FAILED = 'REVIEW_APPROVE_FAILED' as const;
export const REVIEW_REQUEST_CHANGES_REQUESTED =
  'REVIEW_REQUEST_CHANGES_REQUESTED' as const;
export const REVIEW_REQUEST_CHANGES_SUCCEEDED =
  'REVIEW_REQUEST_CHANGES_SUCCEEDED' as const;
export const REVIEW_REQUEST_CHANGES_FAILED =
  'REVIEW_REQUEST_CHANGES_FAILED' as const;
export const REVIEW_CANCEL_REQUESTED = 'REVIEW_CANCEL_REQUESTED' as const;
export const REVIEW_CANCEL_SUCCEEDED = 'REVIEW_CANCEL_SUCCEEDED' as const;
export const REVIEW_CANCEL_FAILED = 'REVIEW_CANCEL_FAILED' as const;
export const REQUEST_REVIEW_MODAL_OPENED =
  'REQUEST_REVIEW_MODAL_OPENED' as const;
export const REQUEST_REVIEW_MODAL_CLOSED =
  'REQUEST_REVIEW_MODAL_CLOSED' as const;
export const REVIEW_MODAL_OPENED = 'REVIEW_MODAL_OPENED' as const;
export const REVIEW_MODAL_CLOSED = 'REVIEW_MODAL_CLOSED' as const;
export const REVIEW_CANCELED_NOTICE_DISMISSED =
  'REVIEW_CANCELED_NOTICE_DISMISSED' as const;
export const REVIEW_REQUEST_NOTICE_DISMISSED =
  'REVIEW_REQUEST_NOTICE_DISMISSED' as const;
export const DESIGNER_WORKFLOW_MODE_CHANGE_REQUESTED =
  'DESIGNER_WORKFLOW_MODE_CHANGE_REQUESTED' as const;
export const DESIGNER_WORKFLOW_MODE_CHANGE_COMPLETED =
  'DESIGNER_WORKFLOW_MODE_CHANGE_COMPLETED' as const;
export const WRITING_ASSISTANT_FEEDBACK_FREEFORM_SUBMITTED =
  'WRITING_ASSISTANT_FEEDBACK_FREEFORM_SUBMITTED' as const;
export const DESIGNER_WORKFLOW_MODE_CHANGE_REJECTED =
  'DESIGNER_WORKFLOW_MODE_CHANGE_REJECTED' as const;
export const LOCALE_PICKER_ITEM_SELECTED =
  'LOCALE_PICKER_ITEM_SELECTED' as const;
export const LOCALE_CONTEXT_OPENED = 'LOCALE_CONTEXT_OPENED' as const;
export const LOCALE_CONTEXT_CLOSED = 'LOCALE_CONTEXT_CLOSED' as const;
export const LOCALE_CONTEXT_SETTINGS_SELECTED =
  'LOCALE_CONTEXT_SETTINGS_SELECTED' as const;
export const LOCALE_PICKER_CLEAR_BUTTON_CLICKED =
  'LOCALE_PICKER_CLEAR_BUTTON_CLICKED' as const;
export const LOCALE_ADD_CLICKED = 'LOCALE_ADD_CLICKED' as const;
export const LOCALE_CONFIGURATION_OPENED =
  'LOCALE_CONFIGURATION_OPENED' as const;
export const OPTIMIZE_CONTEXT_OPENED = 'OPTIMIZE_CONTEXT_OPENED' as const;
export const OPTIMIZE_PICKER_CLEAR_BUTTON_CLICKED =
  'OPTIMIZE_PICKER_CLEAR_BUTTON_CLICKED' as const;
export const OPTIMIZE_CONTEXT_SELECTED = 'OPTIMIZE_CONTEXT_SELECTED' as const;
export const OPTIMIZE_VARIANT_SELECTED = 'OPTIMIZE_VARIANT_SELECTED' as const;
export const OPTIMIZE_CONTEXT_EXITED = 'OPTIMIZE_CONTEXT_EXITED' as const;

export const ELEMENT_STYLE_BLOCKS_ADDED = 'ELEMENT_STYLE_BLOCKS_ADDED' as const;
export const ELEMENT_STYLE_BLOCKS_REMOVED =
  'ELEMENT_STYLE_BLOCKS_REMOVED' as const;
export const ELEMENT_STYLE_BLOCKS_SET = 'ELEMENT_STYLE_BLOCKS_SET' as const;
export const EXTENSION_WINDOW_MODE_TOGGLE =
  'EXTENSION_WINDOW_MODE_TOGGLE' as const;
export const RESPONSIVE_IMAGES_MIGRATED = 'RESPONSIVE_IMAGES_MIGRATED' as const;
export const FETCH_WEBHOOKS_REQUESTED = 'FETCH_WEBHOOKS_REQUESTED' as const;
export const FETCH_WEBHOOKS_SUCCESS = 'FETCH_WEBHOOKS_SUCCESS' as const;
export const FETCH_WEBHOOKS_ERROR = 'FETCH_WEBHOOKS_ERROR' as const;
export const CREATE_WEBHOOK_REQUESTED = 'CREATE_WEBHOOK_REQUESTED' as const;
export const CREATE_WEBHOOK_SUCCESS = 'CREATE_WEBHOOK_SUCCESS' as const;
export const CREATE_WEBHOOK_ERROR = 'CREATE_WEBHOOK_ERROR' as const;
export const DELETE_WEBHOOK_REQUESTED = 'DELETE_WEBHOOK_REQUESTED' as const;
export const DELETE_WEBHOOK_SUCCESS = 'DELETE_WEBHOOK_SUCCESS' as const;
export const DELETE_WEBHOOK_ERROR = 'DELETE_WEBHOOK_ERROR' as const;
export const WEBHOOK_SECRET_MODAL_CLOSED =
  'WEBHOOK_SECRET_MODAL_CLOSED' as const;
export const FORM_SETTINGS_FETCH_REQUESTED =
  'FORM_SETTINGS_FETCH_REQUESTED' as const;
export const FORM_SETTINGS_FETCH_SUCCEEDED =
  'FORM_SETTINGS_FETCH_SUCCEEDED' as const;
export const FORM_SETTINGS_FETCH_FAILED = 'FORM_SETTINGS_FETCH_FAILED' as const;
export const FORM_SETTINGS_UPDATE_REQUESTED =
  'FORM_SETTINGS_UPDATE_REQUESTED' as const;
export const FORM_SETTINGS_UPDATE_SUCCEEDED =
  'FORM_SETTINGS_UPDATE_SUCCEEDED' as const;
export const FORM_SETTINGS_UPDATE_FAILED =
  'FORM_SETTINGS_UPDATE_FAILED' as const;
export const FORM_SETTINGS_UI_STATE_UPDATED =
  'FORM_SETTINGS_UI_STATE_UPDATED' as const;
export const FRAME_DIMENSIONS_CHANGED = 'FRAME_DIMENSIONS_CHANGED' as const;
export const FRAME_DIMENSION_OVERRIDE_RESET =
  'FRAME_DIMENSION_OVERRIDE_RESET' as const;
export const FRAME_BACKGROUND_COLOR_CHANGED =
  'FRAME_BACKGROUND_COLOR_CHANGED' as const;
export const FRAME_BACKGROUND_COLOR_OVERRIDE_RESET =
  'FRAME_BACKGROUND_COLOR_OVERRIDE_RESET' as const;
export const WORKSPACE_ACCESS_REQUEST_AUTH_REDIRECTED =
  'WORKSPACE_ACCESS_REQUEST_AUTH_REDIRECTED' as const;
export const WORKSPACE_ACCESS_REQUESTED = 'WORKSPACE_ACCESS_REQUESTED' as const;
export const WORKSPACE_ACCESS_REQUEST_SUCCEEDED =
  'WORKSPACE_ACCESS_REQUEST_SUCCEEDED' as const;
export const WORKSPACE_ACCESS_REQUEST_FAILED =
  'WORKSPACE_ACCESS_REQUEST_FAILED' as const;
export const ACCESS_REQUEST_ENQUEUED = 'ACCESS_REQUEST_ENQUEUED' as const;
export const ACCESS_REQUEST_DEQUEUED = 'ACCESS_REQUEST_DEQUEUED' as const;

export const DISMISS_AISG_DESTRUCTIVE_CHANGE_NOTIFICATION =
  'DISMISS_AISG_DESTRUCTIVE_CHANGE_NOTIFICATION' as const;

/**
 * Triggers an action when pages are loaded into flux state,
 * either during initial site load or during site data reload
 */
export const PAGES_LOADED = 'PAGES_LOADED' as const;
export const REMOVE_APP_CONNECTION = 'REMOVE_APP_CONNECTION' as const;

/**
 * Triggers an action when the current user data is loaded
 */
export const CURRENT_USER_DATA_REQUESTED =
  'CURRENT_USER_DATA_REQUESTED' as const;
export const CURRENT_USER_DATA_LOADED = 'CURRENT_USER_DATA_LOADED' as const;
export const CURRENT_USER_SITE_ONBOARDING_COMPLETION_REQUESTED =
  'CURRENT_USER_SITE_ONBOARDING_COMPLETION_REQUESTED' as const;

/**
 * Triggers an action when the cms filter is opened or closed
 */
export const CMS_FILTER_CHANGED = 'CMS_FILTER_CHANGED';
export const CMS_COLLECTION_LIST_PREVIEW_ENABLED =
  'CMS_COLLECTION_LIST_PREVIEW_ENABLED' as const;
export const CMS_COLLECTION_LIST_PREVIEW_ITEMS_COUNT_CHANGED =
  'CMS_COLLECTION_LIST_PREVIEW_ITEMS_COUNT_CHANGED' as const;
export const CMS_NESTED_COLLECTION_LIST_WRAPPER_COUNT_CHANGED =
  'CMS_NESTED_COLLECTION_LIST_WRAPPER_COUNT_CHANGED' as const;
/**
 * Force the `DesignerStore` to change without actually changing any of its
 * values.
 *
 * Useful for detecting performance regressions that are caused by poorly
 * implemented cache systems which take the full `DesignerStore` as an input.
 */
export const __HACK__FORCE_DESIGNERSTORE_INVALIDATION =
  '__HACK__FORCE_DESIGNERSTORE_INVALIDATION' as const;

/**
 * Actions related to the Checkout flow.
 */
export const DESIGNER_CHECKOUT_TRIGGERED =
  'DESIGNER_CHECKOUT_TRIGGERED' as const;
export const UPGRADE_CONFIRMATION_MODAL_OPENED =
  'UPGRADE_CONFIRMATION_MODAL_OPENED' as const;
export const UPGRADE_CONFIRMATION_MODAL_CLOSED =
  'UPGRADE_CONFIRMATION_MODAL_CLOSED' as const;
export const CHECKOUT_MODAL_OPENED = 'CHECKOUT_MODAL_OPENED' as const;
export const CHECKOUT_MODAL_CLOSED = 'CHECKOUT_MODAL_CLOSED' as const;
export const CHECKOUT_PAYMENT_METHOD_STATUS_UPDATED =
  'CHECKOUT_PAYMENT_METHOD_STATUS_UPDATED' as const;
export const CHECKOUT_COMPLETED = 'CHECKOUT_COMPLETED' as const;
export const CHECKOUT_FAILED = 'CHECKOUT_FAILED' as const;
export const CHECKOUT_ANALYTICS = 'CHECKOUT_ANALYTICS' as const;
export const CHECKOUT_RESET = 'CHECKOUT_RESET' as const;
export const SITE_DATA_REFRESH_FAILED = 'SITE_DATA_REFRESH_FAILED' as const;
export const SITE_DATA_REFRESH_REQUESTED =
  'SITE_DATA_REFRESH_REQUESTED' as const;
export const SITE_DATA_REFRESH_SUCCEEDED =
  'SITE_DATA_REFRESH_SUCCEEDED' as const;

export const BRANCH_MIGRATION_STARTED = 'BRANCH_MIGRATION_STARTED' as const;
export const BRANCH_MIGRATION_QUEUED = 'BRANCH_MIGRATION_QUEUED' as const;
export const BRANCH_MIGRATION_SUCCEEDED = 'BRANCH_MIGRATION_SUCCEEDED' as const;
export const BRANCH_MIGRATION_FAILED = 'BRANCH_MIGRATION_FAILED' as const;

/**
 * Conceptually this belongs in @packages/systems/code-libraries/designer but it's been
 * lifted here to prevent a circular dependency with @packages/systems/site-plugin-designer
 */
export const CODE_BUILD_SUCCEEDED = 'CODE_BUILD_SUCCEEDED' as const;
export const REMOTE_FILESYSTEM_FILES_COMMITTED =
  'REMOTE_FILESYSTEM_FILES_COMMITTED' as const;
